import './DataTable.scss';
import {
    CSSProperties,
    ChangeEvent,
    DragEvent,
    Fragment,
    MouseEvent,
    PointerEvent as ReactPointerEvent,
    ReactNode,
    Suspense,
    lazy,
    useCallback,
    useEffect,
    useId,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import {
    type ColumnFiltersState,
    type ColumnOrderState,
    type ColumnPinningState,
    type ColumnSizingState,
    type ColumnVisibilityState,
    type ExpandedState,
    type GroupingState,
    type PaginationState,
    type RowData,
    type RowSelectionState,
    type SortingState,
    type Updater,
    useTable,
} from '@tanstack/react-table';
import { type Atom, createAtom, useAtom, useSelector } from '@tanstack/react-store';
import { type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';
import {
    HiChevronRight,
    HiOutlineArrowDownTray,
    HiOutlineArrowPath,
    HiOutlineChevronDoubleDown,
    HiOutlineChevronDoubleUp,
    HiOutlineCheck,
    HiOutlineFunnel,
    HiOutlineInbox,
    HiOutlineMagnifyingGlass,
    HiOutlineMapPin,
    HiOutlineRectangleGroup,
    HiOutlineViewColumns,
    HiXMark,
} from 'react-icons/hi2';
import { MdDragIndicator } from 'react-icons/md';
import {
    DndContext,
    DragOverlay,
    type DragEndEvent,
    type DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import dayjs from '../lib/dayjs';
import Preloader from '../atoms/Preloader';
import Skeleton from '../atoms/Skeleton';
import DataTableSkeleton from './DataTableSkeleton';
import { errorText, errorTraceId } from '@srm/core/errors';
import { usePortalTarget, useUi } from '../provider/UiContext';
import { FieldA11yContext } from '../context/FieldA11yContext';
import { portalOverlayClassName } from '../lib/portalOverlay';
import { findScrollParent } from '../lib/findScrollParent';
import { nextPaint } from '../lib/nextPaint';
import { ValueType } from '../types/ValueType';
import {
    DataTableAlign,
    DataTableEmptyState,
    DataTableFilterKind,
    DataTablePin,
    DataTableProps,
    DataTableWrapMode,
    IDataTableColumn,
    IDataTableExportRequest,
    IDataTableColumnFilter,
    IDataTableFacetValue,
    IDataTableFilterValue,
    IDataTableMenuEntry,
    IEmptyStateAction,
} from './DataTable.types';
import {
    dxMatch,
    filterKindOf,
    createGlobalSearch,
    matchesGlobalSearch,
    isFilterValueEmpty,
    makeColumnFilterFn,
    patchColumnFilters,
    withoutHeaderFilter,
} from './dataTableFilters';
import {
    buildServerLoadOptions,
    facetPageFilter,
    type IDataTableFacetPage,
    type IServerLoadOptions,
    serverFacetSearch,
    suggestsBackgroundExport,
} from './dataTableServerSource';
import { columnExcelCell, formatCellText, isTruthyBool, type ExcelDataCell } from './dataTableFormat';
import { exportDataTableToExcel } from './dataTableExcelExport';
import {
    IDataTableSavedPinning,
    readDataTableState,
    readWrapMode,
    writeDataTableState,
    writeWrapMode,
} from './dataTableState';
import { dataTableFeatures, type DataTableColumn, type DataTableColumnDef, type DataTableRow } from './dataTableFeatures';
import { aggregateValues, DATA_TABLE_AGGREGATE_LABEL, formatAggregate } from './dataTableAggregates';
import { mergeColumnOrder, nextColumnOrder, sameFieldOrder } from './dataTableColumnOrder';
import { applyTreeSelectionChange, syncTreeSelection } from './dataTableTreeSelection';
import { VIRTUAL_THRESHOLD } from './dataTableVirtual';
import { compressMenuDividers } from './dataTableMenu';
import { useDragScroll } from '../hooks/useDragScroll';
import {
    computeRowMinWidth,
    computeStickyLayout,
    computeStickyOffsetVars,
    ILayoutTrack,
    trackWidth,
} from './dataTableLayout';
import { computeColumnGroupBands, hasColumnGroups } from './dataTableColumnGroups';
import { DTB_POPUP_Z } from './popupZIndex';
import { IServerDataState, useDataTableServerData } from './useDataTableServerData';
import { DataTableFullscreenButton, DataTableFullscreenHeader } from './parts/DataTableFullscreenControls';
import { useDataTableFullscreen } from './useDataTableFullscreen';
import { IDataTableFindMatch } from './useDataTableFind';
import DataTableFind from './parts/DataTableFind';
import DataTableGlobalSearch, { type IDataTableGlobalSearchHandle } from './parts/DataTableGlobalSearch';
import DataTableTotals, { IDataTableTotal } from './parts/DataTableTotals';
import { ServerGroupEntry, useDataTableServerGroups } from './useDataTableServerGroups';
import { useDataTableFacetLabels } from './useDataTableFacetLabels';
import DataTableChooserControl, { type IDataTableChooserHandle } from './parts/DataTableChooserControl';
import DataTableContextMenuHost, { type IDataTableContextMenuHandle } from './parts/DataTableContextMenuHost';
import DataTableExportMenu from './parts/DataTableExportMenu';
import DataTableFilterCell from './parts/DataTableFilterCell';
import DataTableRowHeightButton from './parts/DataTableRowHeightButton';
import DataTableSettingsControl from './parts/DataTableSettingsControl';
import DataTableTopGroup from './parts/DataTableTopGroup';
import DataTableFailure from './parts/DataTableFailure';
import DataTableHeaderCell, { type IDataTableHeaderCellRenderer } from './parts/DataTableHeaderCell';
import { type HeaderFilterPageLoader } from './parts/DataTableHeaderFilter';
import DataTableOverflowTip from './parts/DataTableOverflowTip';
import DataTablePager from './parts/DataTablePager';
import DataTableRows from './parts/DataTableRows';
import DataTableBodyRow, {
    type IDataTableBodyRowContentProps,
    type IDataTableBodyRowRenderer,
    type ISortableRowHandle,
} from './parts/DataTableBodyRow';
import DataTableBodyCell, { type IDataTableBodyCellRenderer } from './parts/DataTableBodyCell';
import { virtualRowStyle } from './parts/virtualRowStyle';
import { useLatest } from '../hooks/useLatest';
import { useStableHandlers } from '../hooks/useStableHandlers';
import DataTableFilterSummary from './parts/DataTableFilterSummary';
import DataTableEmptyActions from './parts/DataTableEmptyActions';
import DataTableColumnGroupsRow from './parts/DataTableColumnGroupsRow';

// Модалку лимита выгрузки включают не все потребители (ControlPanel — нет), а она тянет атомы
// Button и Modal. Ленивый чанк не грузит их стили туда, где модалка не нужна: в ControlPanel те же
// классы .rf-button/.rf-modal пока у собственных атомов.
const ExportLimitModal = lazy(() => import('./parts/ExportLimitModal'));

const DEFAULT_COL_WIDTH = 150;
const MIN_COL_WIDTH = 60;
// Служебные треки держат ровно свой элемент (чекбокс 18, ручка 22, кнопка
// разворота 24) — запас сверх него отодвигал данные от края строки, а в таблицах
// форм служебная зона идёт сплошняком: ручка + чекбокс + действия + разворот.
const CHECK_TRACK = 24;
const DRAG_TRACK = 24;
const ACTIONS_TRACK = 100;
// Зазоров между треками нет: расстояние между колонками дают паддинги ячеек
// (два соседних по PAD_CELL), отступ от края строки — паддинг крайней (PAD_EDGE).
// Те же значения объявлены в SCSS через --dtb-pad-cell / --dtb-pad-edge.
/** Служебная колонка «поколения» строки (см. appendedRowsLast). */
const ROW_GENERATION_COL = '__dtb_generation';
const PAD_CELL = 7;
const PAD_EDGE = 14;
// Ячейка действий набрана из слотов фиксированной ширины, и свой зазор у кнопок
// уже есть — паддинг ячейки ей нужен только чтобы кнопка не липла к границе
// (--dtb-pad-util в SCSS).
const PAD_UTIL = 4;
const LAYOUT_METRICS = { padCell: PAD_CELL, padEdge: PAD_EDGE };
const ACTIONS_METRICS = { padCell: PAD_UTIL, padEdge: PAD_EDGE };

/** Трек-распорка: забирает остаток ширины, когда все колонки px-фиксированы. */
const SPACER_TRACK = 'minmax(0px, 1fr)';
/** Разбор шаблона треков на токены: `minmax(...)` — один токен со скобками. */
const TRACK_TOKEN = /minmax\([^)]*\)|\S+/g;
// Шаг заметнее обычного (26 > 22): вложенность читается только позицией текста,
// без иконки у дочерних строк
const TREE_INDENT = 26;
/**
 * Минимум содержимого колонки-дерева: кнопка разворота, отступ уровня и зазор
 * съедают ~56px, поэтому у узких колонок (номер этапа маршрута) на само значение
 * места не оставалось — двузначные числа обрезались.
 */
const TREE_LABEL_MIN = 24 + TREE_INDENT + 6 + 34;
const EST_ROW = 56;
const DEFAULT_PAGE_SIZES = [5, 10, 20];
const SKELETON_ROWS = 3;
/** Сортировка, которую не отправляют на сервер (serverSorting=false): один и тот
 *  же объект, иначе загрузчик видел бы новую выборку на каждом рендере. */
const EMPTY_SORTING: SortingState = [];
/** Строки без пропа `data` (серверная таблица): один массив — новый [] на каждом рендере таблица
 *  принимала бы за новые данные. */
const NO_ROWS: never[] = [];
const TREE_SKELETON_ROWS = 2;
const DRAG_SCROLL_IGNORE_SELECTOR = '.dtb__resize, .dtb__hcell[draggable="true"], .dtb__draghandle';
/** Пауза перед onScrollPositionChange: потребитель пишет history.replaceState,
 *  а Safari/Firefox режут его чаще ~100 вызовов за 30 секунд. 300 мс дали бы
 *  ровно 100 вызовов за 30 секунд непрерывной прокрутки — без запаса. */
const SCROLL_POSITION_PUBLISH_MS = 500;
/** Прилипшую шапку двигает по горизонтали scroll-driven animation от скролла
 *  строк: на компоновщике, кадр в кадр с ними. Синхронизация scrollLeft из
 *  scroll-события отстаёт на кадр — при быстрой прокрутке шапка дрожала. */
function supportsHeadScrollTimeline(): boolean {
    return (
        typeof CSS !== 'undefined' &&
        typeof CSS.supports === 'function' &&
        CSS.supports('animation-timeline', '--dtb-x') &&
        CSS.supports('timeline-scope', '--dtb-x')
    );
}
const SERVER_START_CONFIRM_MS = 1400;
const SERVER_START_EXIT_MS = 220;

type ServerStartFeedback = 'idle' | 'confirmed' | 'exiting';

/** Показывает строку по вертикали, не сдвигая горизонтально таблицы-предки.
 *  Нативный scrollIntoView прокручивает обе оси: у строки вложенной таблицы
 *  боковые отступы detail-блока из-за этого сдвигали родительский грид. */
function scrollRowIntoView(element: HTMLElement, block: ScrollLogicalPosition = 'nearest') {
    const horizontalPositions: Array<[HTMLElement, number]> = [];
    for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
        if (ancestor.scrollLeft !== 0 || ancestor.scrollWidth > ancestor.clientWidth) {
            horizontalPositions.push([ancestor, ancestor.scrollLeft]);
        }
    }

    element.scrollIntoView?.({ block });
    horizontalPositions.forEach(([ancestor, scrollLeft]) => {
        ancestor.scrollLeft = scrollLeft;
    });
}

/** Показывает адресную ячейку внутри собственного горизонтального viewport.
 * Фокус редактора не подходит для этой задачи: readonly-ячейки получают
 * preventScroll, а разные браузерные редакторы прокручивают таблицу по-разному. */
function scrollCellIntoView(cell: HTMLElement) {
    const scroll = cell.closest<HTMLElement>('.dtb__scroll');
    if (!scroll || cell.matches('.dtb__stick-l, .dtb__stick-r')) return;

    const scrollRect = scroll.getBoundingClientRect();
    let visibleLeft = scrollRect.left;
    let visibleRight = scrollRect.right;
    const row = cell.closest<HTMLElement>('[data-row-key]');

    // Закреплённые служебные/пользовательские колонки лежат поверх viewport.
    // Доводим цель до их внутренней границы, а не просто до края скролла.
    row?.querySelectorAll<HTMLElement>('.dtb__stick-l').forEach((sticky) => {
        if (sticky !== cell) visibleLeft = Math.max(visibleLeft, sticky.getBoundingClientRect().right);
    });
    row?.querySelectorAll<HTMLElement>('.dtb__stick-r').forEach((sticky) => {
        if (sticky !== cell) visibleRight = Math.min(visibleRight, sticky.getBoundingClientRect().left);
    });
    if (visibleRight <= visibleLeft) return;

    const cellRect = cell.getBoundingClientRect();
    if (cellRect.left < visibleLeft) scroll.scrollLeft += cellRect.left - visibleLeft;
    else if (cellRect.right > visibleRight) scroll.scrollLeft += cellRect.right - visibleRight;
}

interface ISortableRowState {
    handle: ISortableRowHandle;
    dndY: number | undefined;
    dndTransition: string | undefined;
    dragging: boolean;
}

// Опции сенсоров — константы модуля: useSensor мемоизирует по ним, а от сенсоров зависят обработчики useSortable
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 4 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

/** Обёртка-хук sortable для строки: render-prop, т.к. строки рендерятся функцией. В строку уходят примитивы и
 *  объект связи, стабильный при стабильных сенсорах (setNodeRef и обработчики dnd-kit не меняются, атрибуты — вместе
 *  с признаком «несут»): memo строки срабатывает и у таблиц с переносом. */
function SortableRow({ id, children }: { id: string; children: (s: ISortableRowState) => ReactNode }) {
    const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id });
    const handle = useMemo<ISortableRowHandle>(
        () => ({ setNodeRef, attributes, listeners }),
        [setNodeRef, attributes, listeners],
    );
    // несомая строка стоит на месте: за курсором едет DragOverlay
    const dndY = transform ? (isDragging ? 0 : transform.y) : undefined;
    return <>{children({ handle, dndY, dndTransition: transition, dragging: isDragging })}</>;
}

/** Контексты dnd-kit вокруг списков строк; без dnd — прозрачный проход.
 *  За курсором едет DragOverlay-призрак, а не строка из потока: transform
 *  in-flow элемента растил scroll-область страницы (бесконечный автоскролл). */
function DndWrap({
    enabled,
    sensors,
    items,
    onDragStart,
    onDragEnd,
    onDragCancel,
    overlay,
    children,
}: {
    enabled: boolean;
    sensors: ReturnType<typeof useSensors>;
    items: string[];
    onDragStart: (e: DragStartEvent) => void;
    onDragEnd: (e: DragEndEvent) => void;
    onDragCancel: () => void;
    overlay: ReactNode;
    children: ReactNode;
}) {
    const portalTarget = usePortalTarget();
    if (!enabled) return <>{children}</>;
    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
        >
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
                {children}
            </SortableContext>
            {/* Призрак уходит в хост порталов области (иначе в body): внутри таблицы его fixed-позицию ломали
                contain: paint области прокрутки и transform модалки (призрак обрезался, место вставки считалось со
                сдвигом), а слой модалки его перекрывал. Обёртка .dtb без раскладки несёт переменные таблицы. */}
            {createPortal(
                <div className="dtb dtb__drag-portal">
                    <DragOverlay
                        zIndex={DTB_POPUP_Z}
                        dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}
                    >
                        {overlay}
                    </DragOverlay>
                </div>,
                portalTarget ?? document.body,
            )}
        </DndContext>
    );
}

const alignOf = <T,>(col: IDataTableColumn<T>): DataTableAlign => {
    if (col.alignment) return col.alignment;
    const kind = filterKindOf(col.dataType);
    if (kind === 'number' || kind === 'date') return 'right';
    if (kind === 'boolean') return 'center';
    return 'left';
};

/** Закрепление в сохранённом состоянии физическое (left/right — формат видов и URL),
 *  у таблицы — логическое (start/end). Интерфейс таблиц только LTR. */
const pinningFromSaved = (saved: IDataTableSavedPinning): ColumnPinningState => ({
    start: saved.left ?? [],
    end: saved.right ?? [],
});
const pinningToSaved = ({ start, end }: ColumnPinningState): IDataTableSavedPinning => ({ left: start, right: end });

/** Сторона закреплённой колонки для раскладки: sticky-офсеты считаются в left/right. */
const pinSideOf = (column: { getIsPinned: () => false | 'start' | 'end' }): 'left' | 'right' | undefined => {
    const pinned = column.getIsPinned();
    return pinned === 'start' ? 'left' : pinned === 'end' ? 'right' : undefined;
};

const formatCell = (value: unknown, dataType?: ValueType): ReactNode => {
    const text = formatCellText(value, dataType);
    return text === '' ? <span className="dtb__dash">—</span> : text;
};

// ----- Серверная группировка: маркеры в данных -----
// В групповом режиме data-массив таблицы перемежает строки записями-группами и
// кнопками «Показать ещё»; маркер живёт под символом и данных не задевает.
const SERVER_GROUP_ENTRY = Symbol('dtbServerGroupEntry');
function serverGroupEntryOf<T>(row: T | undefined): ServerGroupEntry<T> | undefined {
    return row ? ((row as Record<symbol, unknown>)[SERVER_GROUP_ENTRY] as ServerGroupEntry<T> | undefined) : undefined;
}

/** Служебные треки строки слева от данных: перенос, выбор, действия слева, раскрытие. */
const LEAD_SERVICE_TRACKS = new Set(['__drag', '__check', '__actionsLeft', '__expander']);

/** Группировка без колонок с `allowGrouping: false` (та же ссылка, если убирать нечего). */
function groupableOnly<T>(ids: GroupingState, columns: readonly IDataTableColumn<T>[]): GroupingState {
    const next = ids.filter((id) => columns.find((c) => c.field === id)?.allowGrouping !== false);
    return next.length === ids.length ? ids : next;
}

/** Клик по этим элементам ячейки — работа с ними, а не с деталями строки (`expandDetailOnRowClick`). */
const DETAIL_CLICK_IGNORE =
    'select, textarea, label, [role="button"], [role="combobox"], [role="listbox"], [role="option"], [role="menuitem"], [contenteditable]:not([contenteditable="false"])';

/** Клик, которым закончили выделять текст строки (скопировать значение), детали не переключает. */
const hasTextSelectionIn = (element: Element) => {
    const selection = window.getSelection();
    return !!selection && !selection.isCollapsed && !!selection.anchorNode && element.contains(selection.anchorNode);
};

/**
 * Внешний атом среза состояния таблицы (опция `atoms`): API таблицы пишут в него
 * синхронно и сразу видят новое значение. Создаётся один раз, как ленивый
 * `useState`; значение для рендера — подпиской `useAtom`/`useSelector`.
 * Не `useCreateAtom`: функцию он принимает как вычисляемый readonly-атом, а не
 * как ленивый инициализатор.
 */
function useStateAtom<S>(init: () => S): Atom<S> {
    const [atom] = useState(() => createAtom<S>(init()));
    return atom;
}

function DataTable<T extends RowData>({
    columns,
    data = NO_ROWS,
    getRowId,
    selectable = false,
    isRowSelectable,
    selectOnRowClick = false,
    singleSelection = false,
    onSelectionChange,
    showFilterRow = false,
    filterRowToggle = false,
    showHeaderFilter = false,
    showGroupPanel = false,
    showGroupsToggle = true,
    showGlobalSearch = false,
    globalSearchMatch = 'fuzzy',
    showFilterSummary = false,
    filterSummaryPosition = 'top',
    filterSummaryExtra,
    filterSummaryEnd,
    filterSummaryBelow,
    resolveFilterSummaryLabels = false,
    showColumnChooser = false,
    columnChooser: columnChooserOptions,
    defaultSort,
    defaultGrouping,
    defaultGroupExpanded,
    title,
    toolbarLead,
    toolbarStart,
    toolbarEnd,
    toolbarBelow,
    footerEnd,
    exportFileName,
    fitColumns = false,
    columnWidthMode = 'preserve',
    stateKey: stateKeyProp,
    initialState,
    onStateChange,
    onGlobalFilterChange,
    stateChangeDebounceMs = 400,
    initialScrollPosition,
    onScrollPositionChange,
    columnVisibility: controlledColumnVisibility,
    onColumnVisibilityChange,
    columnChooserLabel,
    columnChooserAlert,
    highlightColumns,
    rowStatus,
    rowClassName,
    exportTexts,
    getHeaderFilterValues,
    allowRowPinning = false,
    onExport,
    serverExport: serverExportProp,
    serverExportLimitModal: serverExportLimitModalProp,
    onRefresh,
    refreshLabel = 'Обновить',
    onCellCommit,
    editCellRequest,
    revealRowRequest,
    allowRowReordering = false,
    onRowReorder,
    rowActionsPosition = 'right',
    rowActionsAlwaysVisible = false,
    rowActionsWidth,
    rowActionsHeader,
    onRenderedRowsChange,
    stickyGroupHeader = false,
    stickyHeader = false,
    stickyLeadRef,
    maxHeight,
    fillHeight = false,
    allowFullscreen = false,
    fullscreenControl,
    fullscreenTitle,
    fullscreenContext,
    fullscreenExitLabel = 'Вернуться к документу',
    onFullscreenChange,
    initialFullscreen = false,
    cacheRows = true,
    rowVersion,
    cellVersion,
    overflowTooltip = true,
    error,
    onRetry,
    applyFiltersOnClick = false,
    controlRef,
    currentRowId,
    onRowRendered,
    onCellRendered,
    renderDetail,
    expandDetailOnRowClick = false,
    getSubRows,
    canExpandRow,
    onExpandRow,
    loadingRowIds,
    recursiveSelection = false,
    paging: pagingProp = true,
    pageSizes: pageSizesProp,
    defaultPageSize,
    totalCount: totalCountProp,
    loadingMore: loadingMoreProp = false,
    onReachEnd,
    loading = false,
    onInitialReady,
    emptyText,
    emptyPresentation,
    showEmptyState = true,
    appendedRowsLast = false,
    source,
    serverLoad: serverLoadProp,
    serverLocateRowPage,
    serverResetToken: serverResetTokenProp = 0,
    serverSearchFields: serverSearchFieldsProp,
    serverRefreshToken: serverRefreshTokenProp,
    serverScrollMode: serverScrollModeProp = 'paged',
    showServerScrollToStart = false,
    serverScrollToStartConfirmationMs = SERVER_START_CONFIRM_MS,
    serverReloadKeepsCount = false,
    serverGrouping = false,
    serverSorting = true,
    selectAllMode: selectAllModeProp,
    showSelectAll = true,
    serverSelectAllKeys,
    emptyHint,
    emptyIcon,
    filteredEmptyText,
    filteredEmptyHint,
    filteredEmptyActions,
    externalFiltersActive = false,
    onResetFilters,
    onEmptyStateChange,
    rowActions,
    rowContextMenu,
    onRowClick,
    onRowDoubleClick,
    isRowClickable,
    showTableSettings: showTableSettingsProp,
    tableSettingsExtra,
    defaultWrapMode,
    wrapMode: wrapModeProp,
    onWrapModeChange,
    wrapModeKey: wrapModeKeyProp,
    'data-testid': testId,
}: DataTableProps<T>) {
    const { notify, storageNamespace, dataTableDefaults } = useUi();
    const showTableSettings = showTableSettingsProp ?? dataTableDefaults?.showTableSettings ?? false;
    const portalTarget = usePortalTarget();
    const stateKey = stateKeyProp && storageNamespace + stateKeyProp;
    const wrapModeKey = wrapModeKeyProp && storageNamespace + wrapModeKeyProp;
    /** Ключ строки строкой. По контракту `getRowId` её и возвращает, но данные
     *  приходят с бэкенда, и числовой `key` в JSON молча ломал всё, что ищет по
     *  ключу: `rowSelection`/`expanded` держат строковые ключи объекта, а Set/Map
     *  сравнивают по типу. Нормализуем один раз на входе. */
    const rowIdOf = useCallback(
        (row: T): string => {
            const id = getRowId(row);
            return id == null ? '' : String(id);
        },
        // getRowId стабилен по контракту
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // Источник (source) сводится к прежним серверным путям; явный server*-проп сильнее поля источника.
    const sourceLoad = source?.load;
    const serverLoad = sourceLoad ?? serverLoadProp;
    const serverSearchFields = serverSearchFieldsProp ?? source?.searchFields;
    const serverRefreshToken = serverRefreshTokenProp ?? source?.refreshToken;
    const serverExportLimitModal = serverExportLimitModalProp ?? source?.exportLimitModal ?? false;
    // Новый source.load — другая выборка: дальше тем же путём, что serverResetToken (окно и прокрутка
    // с начала, ключи «выбрать все» прежней не применяются), а выделение сбрасывается ниже.
    const [sourceGeneration, setSourceGeneration] = useState({ load: sourceLoad, value: 0 });
    if (sourceGeneration.load !== sourceLoad)
        setSourceGeneration({ load: sourceLoad, value: sourceGeneration.value + 1 });
    const serverResetToken = serverResetTokenProp + sourceGeneration.value;
    const sourceConflict = !!source && !!serverLoadProp;
    useEffect(() => {
        if (sourceConflict && import.meta.env.DEV) {
            console.error('DataTable: заданы и source, и serverLoad — строки берутся из source.load.');
        }
    }, [sourceConflict]);

    // Сохранённое состояние читаем один раз на маунт (stateKey стабилен по жизни грида).
    // Приоритет: персист localStorage > initialState (сохранённый «вид»/state из URL).
    const persisted = useMemo(
        () => (stateKey ? readDataTableState(stateKey) : null) ?? initialState ?? null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [stateKey],
    );

    const [sorting, setSorting] = useState<SortingState>(
        () => persisted?.sorting ?? (defaultSort ?? []).map((s) => ({ id: s.field, desc: s.dir === 'desc' })),
    );
    // серверная схема может отдать размеры страниц пустыми (null) — берём свои
    const pageSizes = pageSizesProp?.length ? pageSizesProp : DEFAULT_PAGE_SIZES;
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => persisted?.columnFilters ?? []);
    /** Набранное в строке фильтров: с applyFiltersOnClick ждёт кнопки «Применить».
     *  Стартует тем же массивом, что и применённые: эффект применения черновика на маунте
     *  иначе подменял бы равный массив другим и перерисовывал грид целиком. */
    const [draftFilters, setDraftFilters] = useState<ColumnFiltersState>(columnFilters);
    const [globalFilter, setGlobalFilter] = useState(() => persisted?.globalFilter ?? '');
    const [globalSearch] = useState(() => createGlobalSearch<T>(globalSearchMatch));
    const rowSelectionAtom = useStateAtom<RowSelectionState>(() => ({}));
    const [rowSelection, setRowSelection] = useAtom(rowSelectionAtom);
    const [innerColumnVisibility, setInnerColumnVisibility] = useState<ColumnVisibilityState>(() => {
        if (persisted?.columnVisibility) return persisted.columnVisibility;
        const hidden: ColumnVisibilityState = {};
        for (const c of columns) if (c.visible === false) hidden[c.field] = false;
        return hidden;
    });
    // Контролируемый режим: видимостью управляет владелец (наборы колонок),
    // атом только сообщает о попытках изменения. Без пропа — свой state.
    const columnVisibility = controlledColumnVisibility ?? innerColumnVisibility;
    // Служебная колонка сортировки не должна создавать ячейки или мешать пустому состоянию.
    const tableColumnVisibility = useMemo(
        () => (appendedRowsLast ? { ...columnVisibility, [ROW_GENERATION_COL]: false } : columnVisibility),
        [appendedRowsLast, columnVisibility],
    );
    const setColumnVisibility = useCallback(
        (updater: Updater<ColumnVisibilityState>) => {
            if (controlledColumnVisibility) {
                const next = typeof updater === 'function' ? updater(controlledColumnVisibility) : updater;
                onColumnVisibilityChange?.(next);
                return;
            }
            setInnerColumnVisibility(updater);
        },
        [controlledColumnVisibility, onColumnVisibilityChange],
    );
    const columnSizingAtom = useStateAtom<ColumnSizingState>(() => persisted?.columnSizing ?? {});
    const [columnSizing, setColumnSizing] = useAtom(columnSizingAtom);
    /** Порядок колонок по схеме — он же порядок по умолчанию. */
    const schemaOrder = useMemo(() => columns.map((c) => c.field), [columns]);
    // Сохранённый порядок (localStorage/вид) сверяется со схемой сразу в начальном состоянии:
    // новые поля схемы встают на свои места, исчезнувшие уходят. Сверка эффектом после маунта
    // давала второй полный рендер каждого грида — mergeColumnOrder всегда отдаёт новый массив.
    const columnOrderAtom = useStateAtom<ColumnOrderState>(() =>
        persisted?.columnOrder ? mergeColumnOrder(persisted.columnOrder, schemaOrder) : schemaOrder,
    );
    const [columnOrder, setColumnOrder] = useAtom(columnOrderAtom);
    // allowGrouping: false действует и на группировку из начального состояния, сохранённого вида и
    // controlRef, а не только на меню и панель: сервер, не умеющий группировать по полю, отклонил бы запрос.
    const [grouping, setGrouping] = useState<GroupingState>(() =>
        groupableOnly(persisted?.grouping ?? defaultGrouping ?? [], columns),
    );
    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const forbiddenGroupingKey = columns
        .filter((c) => c.allowGrouping === false)
        .map((c) => c.field)
        .join('\u0000');
    useEffect(() => {
        setGrouping((current) => groupableOnly(current, columnsRef.current));
    }, [forbiddenGroupingKey]);
    // Набор колонок может смениться под живой таблицей (другая папка поиска, другой
    // предустановленный фильтр): порядок прошлой схемы тогда расставлял общие поля
    // по чужим местам. Пока человек колонки не переставлял — идём за схемой,
    // переставленный вручную порядок сохраняем (см. dataTableColumnOrder).
    // Схема маунта уже учтена начальным состоянием атома.
    const prevSchemaOrderRef = useRef<readonly string[]>(schemaOrder);
    useEffect(() => {
        const prevSchema = prevSchemaOrderRef.current;
        if (sameFieldOrder(prevSchema, schemaOrder)) return;
        prevSchemaOrderRef.current = schemaOrder;
        setColumnOrder((current) => nextColumnOrder(current, prevSchema, schemaOrder));
        // Смена набора колонок под живой таблицей (например, другой набор в режиме
        // редактирования карточки): состояние с id исчезнувших колонок заставляет TanStack
        // ругаться «Column with id ... does not exist» и гонять лишнее согласование моделей.
        const known = new Set(schemaOrder);
        const prune = <S extends { id: string }>(items: S[]): S[] =>
            items.some((item) => !known.has(item.id)) ? items.filter((item) => known.has(item.id)) : items;
        setSorting(prune);
        setColumnFilters(prune);
        setDraftFilters(prune);
        setGrouping((current) =>
            current.some((id) => !known.has(id)) ? current.filter((id) => known.has(id)) : current,
        );
    }, [schemaOrder, setColumnOrder, setGrouping]);
    const hasTree = !!getSubRows;
    const hasDetail = !!renderDetail;
    const hasExpansionColumn = hasTree || hasDetail;
    // Группы исторически раскрыты сразу; дерево и master-detail стартуют закрытыми. defaultGroupExpanded
    // раскрывает группы по одной (эффект ниже), кроме «всех сразу» без колонки раскрытия — это прежний флаг.
    // Раскрытие рендер читает через модели строк; перерисовку по нему даёт useTable —
    // без селектора он подписан на все срезы состояния.
    const expandedAtom = useStateAtom<ExpandedState>(() =>
        hasExpansionColumn || (defaultGroupExpanded !== undefined && defaultGroupExpanded !== true) ? {} : true,
    );
    const paginationAtom = useStateAtom<PaginationState>(() => ({
        pageIndex: persisted?.pageIndex ?? 0,
        pageSize: persisted?.pageSize ?? defaultPageSize ?? pageSizes[1] ?? pageSizes[0] ?? 10,
    }));
    const [pagination, setPagination] = useAtom(paginationAtom);
    const resetPageIndex = useCallback(
        () => setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 })),
        [setPagination],
    );
    // набираемый текст поиска — у поля туллбара (DataTableGlobalSearch), сюда — итог после паузы
    const globalSearchRef = useRef<IDataTableGlobalSearchHandle>(null);
    const applyGlobalSearch = useCallback(
        (text: string) => {
            setGlobalFilter(text);
            resetPageIndex();
        },
        [resetPageIndex],
    );
    // поиск, заданный таблицей (очистка, сброс, controlRef): заменяет и набранное в поле
    const setSearchText = (text: string) => {
        globalSearchRef.current?.set(text);
        applyGlobalSearch(text);
    };
    // Полноэкранный режим: тот же узел, position: fixed. Якорь прокрутки — строка,
    // которая была верхней видимой: после смены скролл-контейнера возвращаемся к ней.
    const rootRef = useRef<HTMLDivElement>(null);
    const fullscreenPlaceholderRef = useRef<HTMLDivElement>(null);
    const fullscreenTriggerRef = useRef<HTMLButtonElement>(null);
    const fullscreenAnchorRef = useRef<string | null>(null);
    const captureFullscreenAnchorRef = useRef<(next: boolean) => void>(() => {});
    // Таблица в потоке прокручена внутрь: туллбар и шапка (stickyHeader) прилипли к
    // верху прокрутки — возвращает это место, иначе null
    const stuckTopOf = (flowNode: HTMLElement | null): number | null => {
        if (!stickyHeader || maxHeight != null || fillHeight || !flowNode) return null;
        const parent = findScrollParent(flowNode);
        const isDoc = parent === document.scrollingElement || parent === document.documentElement;
        const top = isDoc || !parent ? 0 : parent.getBoundingClientRect().top;
        return flowNode.getBoundingClientRect().top < top ? top : null;
    };
    const fullscreenApi = useDataTableFullscreen({
        rootRef,
        placeholderRef: fullscreenPlaceholderRef,
        triggerRef: fullscreenTriggerRef,
        onBeforeChange: (next) => captureFullscreenAnchorRef.current(next),
        onChange: onFullscreenChange,
        initialActive: allowFullscreen && initialFullscreen,
        getStuckTop: stuckTopOf,
        // открытый редактор ячейки (свой редактор может не гасить Escape) — Escape ему, а не режиму
        canExitOnEscape: () => !editingCell,
    });
    const fullscreen = allowFullscreen && fullscreenApi.active;
    const fullscreenTitleId = useId();
    const titleId = useId();
    const fullscreenHeadTitle = fullscreenTitle ?? title;
    const exitFullscreen = fullscreenApi.exit;
    useEffect(() => {
        if (!allowFullscreen) exitFullscreen();
    }, [allowFullscreen, exitFullscreen]);
    // на весь экран страниц нет: постраничная серверная выборка становится лентой с догрузкой
    const serverPagedFullscreen = fullscreen && !!serverLoad && serverScrollModeProp === 'paged';
    const serverScrollMode = serverPagedFullscreen ? 'infinite' : serverScrollModeProp;
    // открытость панелей туллбара и контекстного меню — их собственное состояние
    const chooserRef = useRef<IDataTableChooserHandle>(null);
    const highlightSet = useMemo(
        () => (highlightColumns?.length ? new Set(highlightColumns) : undefined),
        [highlightColumns],
    );
    const [wrapModeState, setWrapMode] = useState<DataTableWrapMode>(
        () =>
            persisted?.wrapMode ??
            readWrapMode(wrapModeKey) ??
            defaultWrapMode ??
            dataTableDefaults?.defaultWrapMode ??
            'nowrap',
    );
    // Контролируемый режим (проп) сильнее внутреннего состояния
    const wrapMode = wrapModeProp ?? wrapModeState;
    // Настройка личная и переживает перезагрузку: пишем сразу, а не по общему снимку
    // состояния — снимком у серверных списков владеют сохранённые виды.
    const wrapModeKeyRef = useRef(wrapModeKey);
    wrapModeKeyRef.current = wrapModeKey;
    const onWrapModeChangeRef = useRef(onWrapModeChange);
    onWrapModeChangeRef.current = onWrapModeChange;
    const wrapModeControlledRef = useRef(wrapModeProp !== undefined);
    wrapModeControlledRef.current = wrapModeProp !== undefined;
    const changeWrapMode = useCallback((mode: DataTableWrapMode) => {
        onWrapModeChangeRef.current?.(mode);
        // в контролируемом режиме значением владеет потребитель — внутреннее
        // состояние и localStorage работали бы вхолостую
        if (wrapModeControlledRef.current) return;
        setWrapMode(mode);
        writeWrapMode(wrapModeKeyRef.current, mode);
    }, []);
    // Видимость строки фильтров: с filterRowToggle управляется кнопкой туллбара
    // (showFilterRow — стартовое значение), скрытие не сбрасывает фильтры
    const [filterRowOn, setFilterRowOn] = useState(showFilterRow);
    const filterRowShown = filterRowToggle ? filterRowOn : showFilterRow;

    // Управляемая видимость: изменение col.visible (формульные правила потребителя)
    // синхронизируется в state; ручные скрытия через chooser не трогаем.
    // В контролируемом режиме видимостью распоряжается владелец (наборы колонок):
    // синхронизация здесь сообщала бы ему «изменение», которого пользователь не делал,
    // и набор сразу помечался бы как изменённый.
    const prevPropVisibleRef = useRef(new Map<string, boolean | undefined>());
    useEffect(() => {
        if (controlledColumnVisibility) return;
        const patch: ColumnVisibilityState = {};
        let dirty = false;
        for (const c of columns) {
            const prev = prevPropVisibleRef.current.get(c.field);
            if (prev !== undefined && prev !== c.visible) {
                patch[c.field] = c.visible !== false;
                dirty = true;
            }
            prevPropVisibleRef.current.set(c.field, c.visible);
        }
        if (dirty) setColumnVisibility((p) => ({ ...p, ...patch }));
    }, [columns]);

    // Закрепление колонок интерактивное (контекстное меню шапки) — атом, старт из props.
    // Колонки с allowPinning: false закреплены кодом: их сторона всегда из схемы,
    // что бы ни лежало в сохранённом состоянии.
    const columnPinningAtom = useStateAtom<ColumnPinningState>(() => {
        const base = persisted?.columnPinning
            ? pinningFromSaved(persisted.columnPinning)
            : {
                  start: columns.filter((c) => c.pinned === 'left').map((c) => c.field),
                  end: columns.filter((c) => c.pinned === 'right').map((c) => c.field),
              };
        const locked = columns.filter((c) => c.allowPinning === false);
        if (!locked.length) return base;
        const lockedFields = new Set(locked.map((c) => c.field));
        return {
            start: [
                ...base.start.filter((f) => !lockedFields.has(f)),
                ...locked.filter((c) => c.pinned === 'left').map((c) => c.field),
            ],
            end: [
                ...base.end.filter((f) => !lockedFields.has(f)),
                ...locked.filter((c) => c.pinned === 'right').map((c) => c.field),
            ],
        };
    });
    const columnPinning = useSelector(columnPinningAtom);
    // Закрепление из схемы действует и на колонки, появившиеся позже (режим правки добавляет свою
    // колонку) или сменившие pinned: они закрепляются по схеме. Ручные закрепления остальных колонок
    // не трогаем — как и с видимостью выше.
    const prevPropPinnedRef = useRef<Map<string, DataTablePin | undefined> | null>(null);
    useEffect(() => {
        const prev = prevPropPinnedRef.current;
        prevPropPinnedRef.current = new Map(columns.map((c) => [c.field, c.pinned]));
        if (!prev) return;
        const changed = columns.filter((c) => (prev.has(c.field) ? prev.get(c.field) !== c.pinned : !!c.pinned));
        if (!changed.length) return;
        const fields = new Set(changed.map((c) => c.field));
        const current = columnPinningAtom.get();
        columnPinningAtom.set({
            start: [
                ...(current.start ?? []).filter((f) => !fields.has(f)),
                ...changed.filter((c) => c.pinned === 'left').map((c) => c.field),
            ],
            end: [
                ...(current.end ?? []).filter((f) => !fields.has(f)),
                ...changed.filter((c) => c.pinned === 'right').map((c) => c.field),
            ],
        });
    }, [columns, columnPinningAtom]);

    // ----- Новые строки под сортировкой -----
    // Пользовательская сортировка не должна «уводить» только что добавленную строку
    // в середину списка: она остаётся после существующих, как в старой таблице
    // (там для этого держали скрытую служебную колонку). Здесь то же самое, но
    // служебная колонка живёт внутри атома: каждая строка получает «поколение»
    // (0 — та, что была при первой загрузке; дальше по порядку появления), и
    // сортировка идёт по нему первым уровнем.
    const rowGenerationRef = useRef({ map: new Map<string, number>(), next: 0, seeded: false });
    const rowGenerationOf = useCallback(
        (row: T): number => {
            const state = rowGenerationRef.current;
            const id = rowIdOf(row);
            const known = state.map.get(id);
            if (known !== undefined) return known;
            const generation = state.seeded ? ++state.next : 0;
            state.map.set(id, generation);
            return generation;
        },
        // getRowId стабилен по контракту
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    // Полная перепрошивка ключей (операции вроде «Объединить строки» выдают всем
    // строкам новые id) — это новый базовый набор, а не «дописанные» строки: иначе
    // у каждой строки своё поколение, оно первым уровнем сортировки, и клики по
    // заголовкам перестают что-либо менять. Сброс — до построения row model
    // (useMemo, не эффект), чтобы значения поколений не успели закэшироваться.
    useMemo(() => {
        const state = rowGenerationRef.current;
        if (!appendedRowsLast || !state.seeded || !data.length) return;
        if (data.some((row) => state.map.has(rowIdOf(row)))) return;
        state.map.clear();
        state.next = 0;
        state.seeded = false;
        // getRowId стабилен по контракту, appendedRowsLast не меняется на живой таблице
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);
    useEffect(() => {
        // первый набор данных целиком считается «существующим»
        if (!rowGenerationRef.current.seeded && data.length) {
            data.forEach((row) => rowGenerationOf(row));
            rowGenerationRef.current.seeded = true;
        }
    }, [data, rowGenerationOf]);

    const columnDefs = useMemo<DataTableColumnDef<T>[]>(
        () =>
            columns.map((col) => {
                const kind = filterKindOf(col.dataType);
                return {
                    id: col.field,
                    accessorFn: col.accessor ?? ((row: T) => (row as Record<string, unknown>)[col.field]),
                    header: col.header,
                    size: col.width ?? DEFAULT_COL_WIDTH,
                    minSize: col.minWidth ?? MIN_COL_WIDTH,
                    enableSorting: col.allowSorting !== false,
                    // первый клик — по возрастанию (TanStack числовые сортирует desc-first)
                    sortDescFirst: false,
                    enableResizing: col.allowResizing !== false,
                    enableHiding: col.allowHiding !== false,
                    enableColumnFilter: col.allowFiltering !== false,
                    filterFn: makeColumnFilterFn<T>(kind, col.dataType, col.cellText),
                    aggregationFn: col.aggregate,
                    // ISO-строки дат сортируются и лексикографически, но локализованные
                    // значения (DD.MM.YYYY из бэка) — нет; сравниваем как даты всегда.
                    sortFn:
                        kind === 'date'
                            ? (a: DataTableRow<T>, b: DataTableRow<T>, id: string) =>
                                  dayjs(String(a.getValue(id) ?? '')).valueOf() -
                                  dayjs(String(b.getValue(id) ?? '')).valueOf()
                            : 'auto',
                    meta: { dataColumn: col },
                } satisfies DataTableColumnDef<T>;
            }),
        [columns],
    );

    /** Определения колонок для таблицы: к пользовательским добавляется служебная
     *  колонка «поколения» — она невидима и нужна только сортировке. */
    const tableColumnDefs = useMemo<DataTableColumnDef<T>[]>(
        () =>
            appendedRowsLast
                ? [
                      ...columnDefs,
                      {
                          id: ROW_GENERATION_COL,
                          accessorFn: (row: T) => rowGenerationOf(row),
                          enableSorting: true,
                          enableHiding: false,
                          enableColumnFilter: false,
                      } satisfies DataTableColumnDef<T>,
                  ]
                : columnDefs,
        [columnDefs, appendedRowsLast, rowGenerationOf],
    );

    // Состояние исчезнувших колонок вычищает эффект смены схемы, но первый рендер с новым
    // набором колонок идёт ещё со старым. Таблице отдаём только id её колонок: иначе
    // модели строятся по мёртвому id, а TanStack в development ругается
    // «Column with id … does not exist». Без мёртвых id массив тот же — мемо моделей целы.
    const liveColumnIds = useMemo(() => new Set(tableColumnDefs.map((c) => c.id)), [tableColumnDefs]);
    const liveGrouping = useMemo(
        () => (grouping.every((id) => liveColumnIds.has(id)) ? grouping : grouping.filter((id) => liveColumnIds.has(id))),
        [grouping, liveColumnIds],
    );
    const liveColumnFilters = useMemo(
        () =>
            columnFilters.every((f) => liveColumnIds.has(f.id))
                ? columnFilters
                : columnFilters.filter((f) => liveColumnIds.has(f.id)),
        [columnFilters, liveColumnIds],
    );

    /** Сортировка для таблицы: поколение идёт первым уровнем, поэтому только что
     *  добавленные строки остаются после существующих при любой сортировке. */
    const effectiveSorting = useMemo(() => {
        const live = sorting.every((s) => liveColumnIds.has(s.id))
            ? sorting
            : sorting.filter((s) => liveColumnIds.has(s.id));
        return appendedRowsLast && live.length > 0 ? [{ id: ROW_GENERATION_COL, desc: false }, ...live] : live;
    }, [appendedRowsLast, sorting, liveColumnIds]);

    // ----- Серверный источник -----
    // Состояние таблицы (страница, сортировка, фильтры) уходит на бэк; клиентские
    // модели пагинации/сортировки/фильтрации при этом отключаются, иначе строки
    // текущей страницы были бы отфильтрованы повторно.
    const filterSummaryColumns = useMemo(
        () => columns.map((col) => ({ id: col.field, header: col.header, kind: filterKindOf(col.dataType) })),
        [columns],
    );
    const filterKinds = useMemo(() => {
        const kinds: Record<string, DataTableFilterKind> = {};
        columns.forEach((col) => {
            kinds[col.field] = filterKindOf(col.dataType);
        });
        return kinds;
    }, [columns]);
    // Серверная группировка (DX-протокол groupPaging): активна при включённой группировке у
    // подгружаемых прокруткой и постраничных списков (страница — группы верхнего уровня),
    // целый набор ('all') группирует клиент. Плоский источник при этом молчит — данными
    // владеет групповой хук.
    const serverGroupActive = serverGrouping && !!serverLoad && serverScrollMode !== 'all' && grouping.length > 0;
    // С первой страницы начинаются другая выборка (serverResetToken, как после нового фильтра) и,
    // постранично при серверной группировке, другая группировка: номер страницы относится к тому,
    // что листается, — строкам или группам. Пока сброс страницы не применён, загрузчику уже
    // отдаётся первая — иначе успевал бы уйти (и тут же отмениться) запрос прежней страницы.
    const serverPagedGrouping = serverGrouping && !!serverLoad && serverScrollModeProp === 'paged';
    const groupingKey = JSON.stringify(grouping);
    const pageResetKey = `${serverResetToken}|${serverPagedGrouping ? groupingKey : ''}`;
    const prevPageResetKeyRef = useRef(pageResetKey);
    const pageResetPending = prevPageResetKeyRef.current !== pageResetKey;
    useEffect(() => {
        if (prevPageResetKeyRef.current === pageResetKey) return;
        prevPageResetKeyRef.current = pageResetKey;
        resetPageIndex();
    }, [pageResetKey, resetPageIndex]);
    const serverPageIndex = pageResetPending ? 0 : pagination.pageIndex;
    const flatServer = useDataTableServerData<T>({
        load: serverGroupActive ? undefined : serverLoad,
        pageIndex: serverScrollMode === 'all' ? 0 : serverPageIndex,
        pageSize: serverScrollMode === 'all' ? 0 : pagination.pageSize,
        // при клиентской сортировке её смена не должна перезапрашивать данные
        sorting: serverSorting ? sorting : EMPTY_SORTING,
        columnFilters,
        globalFilter,
        searchFields: serverSearchFields,
        filterKinds,
        refreshToken: serverRefreshToken,
        infinite: !!serverLoad && serverScrollMode === 'infinite',
        initialRowIndex: serverScrollMode === 'infinite' ? initialScrollPosition?.rowIndex : undefined,
        initialRowId: serverScrollMode === 'infinite' ? initialScrollPosition?.rowId : undefined,
        locateInitialChunk: serverLocateRowPage,
        resetToken: serverResetToken,
        keepCountOnReload: serverReloadKeepsCount,
    });
    const groupsApi = useDataTableServerGroups<T>({
        enabled: serverGroupActive,
        load: serverLoad,
        grouping,
        sorting: serverSorting ? sorting : EMPTY_SORTING,
        columnFilters,
        globalFilter,
        searchFields: serverSearchFields,
        filterKinds,
        refreshToken: serverRefreshToken,
        resetToken: serverResetToken,
        groupsPageSize: pagination.pageSize,
        pageIndex: serverScrollMode === 'paged' ? serverPageIndex : undefined,
    });
    // Данные группового режима: строки вперемешку с маркерами групп и «Показать ещё»
    const groupData = useMemo<T[]>(
        () => groupsApi.entries.map((e) => (e.kind === 'row' ? e.row : ({ [SERVER_GROUP_ENTRY]: e } as unknown as T))),
        [groupsApi.entries],
    );
    // Единый интерфейс серверного состояния: остальной код таблицы не различает,
    // кто источник — плоский хук или групповой.
    const server: IServerDataState<T> = serverGroupActive
        ? {
              rows: groupData,
              totalCount: groupsApi.totalCount,
              loading: groupsApi.loading,
              error: groupsApi.error,
              errorOnLoadMore: groupsApi.errorOnLoadMore,
              reload: groupsApi.reload,
              loadOptions: groupsApi.flatLoadOptions,
              loadMore: groupsApi.loadMore,
              hasMore: groupsApi.hasMore,
              windowStartIndex: 0,
          }
        : flatServer;
    const isServer = !!serverLoad;
    // Подгрузка прокруткой заменяет пейджер: страниц нет, строки копятся.
    const serverInfinite = isServer && serverScrollMode === 'infinite';
    const serverUnpaged = isServer && serverScrollMode !== 'paged';
    const paging = pagingProp && !serverUnpaged;
    // На весь экран клиентские строки идут одной лентой: страниц и выбора их размера нет
    // (пагинацию выключает manualPagination).
    const unpagedFullscreen = fullscreen && paging && !isServer;
    // Подгрузка прокруткой сама ведёт свои индикаторы: skeleton-строки на время
    // запроса и счётчик «Загружено X из Y» под списком.
    const loadingPrevious = serverInfinite && server.loadingDirection === 'previous' && server.rows.length > 0;
    const loadingMore =
        loadingMoreProp ||
        (serverInfinite && server.loading && server.rows.length > 0 && server.loadingDirection !== 'previous');
    const totalCount = serverUnpaged ? server.totalCount : totalCountProp;
    // Ленивое дерево на серверных строках: подгруженные дети живут у потребителя
    // и приходят новым getSubRows. Массив строк при этом не меняется, а модель
    // таблицы пересобирается только по нему — поэтому на смену загрузчика
    // отдаём новый массив, иначе раскрытая ветка остаётся пустой.
    const effectiveData = useMemo(
        () => (isServer ? [...server.rows] : data),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isServer, server.rows, data, getSubRows],
    );
    // Догрузка порции не гасит уже показанные строки общим прелоадером — для неё
    // есть skeleton-строки в конце списка.
    const effectiveLoading = isServer
        ? server.loading && !(serverScrollMode === 'infinite' && server.rows.length > 0)
        : loading;

    // Короткий повторный запрос не должен закрывать готовые строки вспышкой лоадера.
    const [loadingOverlayReady, setLoadingOverlayReady] = useState(false);
    useEffect(() => {
        if (!effectiveLoading) {
            setLoadingOverlayReady(false);
            return;
        }
        const timer = setTimeout(() => setLoadingOverlayReady(true), 300);
        return () => clearTimeout(timer);
    }, [effectiveLoading]);

    const hasGrouping = grouping.length > 0;
    const hasExpandableRows = hasGrouping || hasExpansionColumn;
    /** Запись выбора (см. writeSelection ниже): опциям таблицы она нужна раньше, чем есть сама таблица. */
    const selectionWriterRef = useRef<(updater: Updater<RowSelectionState>, fromTable: boolean) => void>(() => {});

    const table = useTable({
        features: dataTableFeatures,
        data: effectiveData,
        columns: tableColumnDefs,
        // маркеры серверной группировки несут собственный стабильный id
        getRowId: (row) => serverGroupEntryOf(row)?.id ?? rowIdOf(row),
        ...(getSubRows ? { getSubRows } : {}),
        // при renderDetail canExpandRow ограничивает раскрытие per-row
        // (например, видимость подтаблиц по формулам); группы/деревья
        // раскрываются всегда — их original = данные первой листовой строки,
        // и per-row правило к ним неприменимо
        getRowCanExpand: (row) =>
            serverGroupEntryOf(row.original)
                ? false // серверные группы раскрывает свой хук, не TanStack
                : row.getIsGrouped() || row.subRows.length > 0
                  ? true
                  : renderDetail
                    ? (canExpandRow?.(row.original) ?? true)
                    : !!canExpandRow?.(row.original),
        // Срезы, которые таблица меняет своими API без нашего перехвата, живут во
        // внешних атомах: запись синхронна, и соседний вызов API видит её сразу
        // (ресайз стартует от только что замороженной ширины, диапазон выбора —
        // от только что выбранной строки). Сортировка, фильтры, группировка и видимость
        // колонок остаются state + on*Change: их перехватываем или отсекаем на входе
        // исчезнувшие колонки (см. liveColumnIds).
        atoms: {
            rowSelection: rowSelectionAtom,
            columnSizing: columnSizingAtom,
            columnPinning: columnPinningAtom,
            columnOrder: columnOrderAtom,
            expanded: expandedAtom,
            pagination: paginationAtom,
        },
        state: {
            sorting: effectiveSorting,
            columnFilters: liveColumnFilters,
            globalFilter,
            columnVisibility: tableColumnVisibility,
            grouping: liveGrouping,
        },
        onSortingChange: setSorting,
        // смена фильтра возвращает на первую страницу (autoReset отключён —
        // он сбрасывал страницу и при ЛЮБОМ изменении data: добавил строку
        // на третьей странице — выкинуло на первую)
        onColumnFiltersChange: (updater) => {
            setColumnFilters(updater);
            resetPageIndex();
        },
        onGlobalFilterChange: setGlobalFilter,
        onColumnVisibilityChange: setColumnVisibility,
        onGroupingChange: setGrouping,
        // Row-модели зарегистрированы в dataTableFeatures все сразу, а работают
        // по режиму. Флаги явные в каждом рендере: опции сливаются с прежними,
        // и пропущенный ключ оставил бы значение прошлого режима.
        manualFiltering: isServer,
        // клиентское дерево: родитель остаётся, пока совпадает кто-то из потомков (как TreeList DX),
        // иначе вложенное совпадение пропадает вместе с неподходящим предком
        filterFromLeafRows: hasTree && !isServer,
        manualSorting: isServer && serverSorting,
        // при серверной группировке группы приходят готовыми записями от
        // группового источника — клиентская модель групп не строится
        manualGrouping: !hasGrouping || serverGroupActive,
        manualExpanding: !hasExpandableRows,
        // на весь экран клиентские строки идут одной лентой: страница не режет
        manualPagination: isServer || !paging || unpagedFullscreen,
        rowCount: isServer ? server.totalCount : undefined,
        globalFilterFn: globalSearch.filterFn,
        // сгруппированная колонка уходит из таблицы (её значение — в строке
        // группы), кроме помеченных keepWhenGrouped — для них режим выключаем
        groupedColumnMode: columns.some((c) => c.keepWhenGrouped) ? undefined : 'remove',
        enableMultiSort: true,
        isMultiSortEvent: (e) => (e as MouseEvent).shiftKey,
        // Строки групп — не записи: выбор через них каскадом брал бы и строки
        // свёрнутой группы, попавшей в Shift-диапазон.
        enableRowSelection: !selectable
            ? false
            : (row) =>
                  !row.getIsGrouped() &&
                  !serverGroupEntryOf(row.original) &&
                  (isRowSelectable ? isRowSelectable(row.original) : true),
        // одиночный выбор: выбор строки снимает остальные, Shift-диапазона нет
        enableMultiRowSelection: !singleSelection,
        enableSubRowSelection: recursiveSelection,
        onRowSelectionChange: (updater) => selectionWriterRef.current(updater, true),
        enableRowPinning: allowRowPinning,
        keepPinnedRows: true,
        // смена identity данных (StrictMode-даблфетч, refetch) не должна сворачивать
        // группы/дерево — раскрытие живёт в своём атоме
        autoResetExpanded: false,
        autoResetPageIndex: false,
        columnResizeMode: 'onChange',
    });
    /** Ключи выбранных строк — одно место вместо пересчёта в каждом потребителе. */
    const selectedKeys = table.getSelectedRowIds();
    /** Модель строк целиком: все загруженные узлы дерева, свёрнутые и отфильтрованные тоже. */
    const allRowsModel = table.getCoreRowModel();

    // Рекурсивный выбор в дереве: отметка расходится по ветке, родитель выводится из детей,
    // ленивая ветка передаёт отметку пришедшим детям (dataTableTreeSelection). `fromTable` —
    // изменение из API таблицы (клик, Shift-диапазон, «выбрать все»), иначе — ключи из кода.
    const treeSelection = recursiveSelection && hasTree && !singleSelection;
    const pendingBranchesRef = useRef(new Set<string>());
    const writeSelection = (updater: Updater<RowSelectionState>, fromTable: boolean) => {
        const prev = rowSelectionAtom.get();
        const next = typeof updater === 'function' ? updater(prev) : updater;
        setRowSelection(
            treeSelection
                ? applyTreeSelectionChange(
                      table.getCoreRowModel().rows,
                      prev,
                      next,
                      pendingBranchesRef.current,
                      fromTable,
                  )
                : next,
        );
    };
    selectionWriterRef.current = writeSelection;
    // до отрисовки: пришедшие дети отмеченной ветки не мелькают неотмеченными
    useLayoutEffect(() => {
        if (!treeSelection) {
            pendingBranchesRef.current.clear();
            return;
        }
        const prev = rowSelectionAtom.get();
        const next = syncTreeSelection(allRowsModel.rows, prev, pendingBranchesRef.current);
        if (next !== prev) setRowSelection(next);
    }, [treeSelection, allRowsModel, rowSelectionAtom, setRowSelection]);

    const onGlobalFilterChangeRef = useRef(onGlobalFilterChange);
    onGlobalFilterChangeRef.current = onGlobalFilterChange;
    const reportedGlobalFilterRef = useRef(globalFilter);
    useEffect(() => {
        if (reportedGlobalFilterRef.current === globalFilter) return;
        reportedGlobalFilterRef.current = globalFilter;
        onGlobalFilterChangeRef.current?.(globalFilter);
    }, [globalFilter]);

    // Совпадение внутри свёрнутой ветки клиентского дерева или группы не видно: пока действует фильтр или поиск,
    // ветки и группы раскрыты, а снятие фильтра возвращает раскрытие, которое было до него. Группы с деталями
    // строки не трогаем: раскрытие всего открыло бы и детали каждой строки.
    const clientFiltering = !isServer && (liveColumnFilters.length > 0 || !!globalFilter);
    const treeFilterActive =
        clientFiltering && (hasTree || (grouping.length > 0 && !hasExpansionColumn));
    const expandedBeforeFilterRef = useRef<ExpandedState | null>(null);
    useEffect(() => {
        if (treeFilterActive) {
            if (expandedBeforeFilterRef.current === null) {
                expandedBeforeFilterRef.current = expandedAtom.get();
                expandedAtom.set(true);
            }
        } else if (expandedBeforeFilterRef.current !== null) {
            expandedAtom.set(expandedBeforeFilterRef.current);
            expandedBeforeFilterRef.current = null;
        }
    }, [treeFilterActive, expandedAtom]);

    // Начальное раскрытие групп: решение — один раз на группу, которую таблица ещё не показывала, поэтому ручное
    // сворачивание переживает и перерисовку, и новые данные, и возврат прежней группировки. Пока фильтр раскрыл всё
    // на время, решение пишется в раскрытие, которое вернётся после него.
    const seenGroupIdsRef = useRef(new Set<string>());
    const defaultGroupExpandedRef = useLatest(defaultGroupExpanded);
    const groupedRowModel = table.getGroupedRowModel();
    useLayoutEffect(() => {
        const rule = defaultGroupExpandedRef.current;
        if (rule === undefined || serverGroupActive) return;
        const opened: string[] = [];
        groupedRowModel.flatRows.forEach((row) => {
            if (!row.getIsGrouped() || seenGroupIdsRef.current.has(row.id)) return;
            seenGroupIdsRef.current.add(row.id);
            const columnId = row.groupingColumnId ?? '';
            // значение — как у groupRender колонки: исходное, а не строка ключа группы
            const open =
                typeof rule === 'function' ? rule({ columnId, value: row.getValue(columnId), depth: row.depth }) : rule;
            if (open) opened.push(row.id);
        });
        if (!opened.length) return;
        const patch = (prev: ExpandedState): ExpandedState => {
            if (prev === true) return prev;
            const next = { ...prev };
            opened.forEach((id) => {
                next[id] = true;
            });
            return next;
        };
        if (expandedBeforeFilterRef.current !== null)
            expandedBeforeFilterRef.current = patch(expandedBeforeFilterRef.current);
        else expandedAtom.set(patch);
    }, [groupedRowModel, serverGroupActive, expandedAtom, defaultGroupExpandedRef]);

    // «Выбрать все» в серверном режиме `allPages` берёт весь набор выборки, а не
    // показанные строки: ключи догружаются тем же запросом, что и данные. Источник без
    // `keys` отдаёт их своим загрузчиком — всем набором.
    const sourceKeys = source?.keys;
    const selectAllKeys: ((options: IServerLoadOptions, signal: AbortSignal) => Promise<string[]>) | undefined =
        serverSelectAllKeys
            ? (options) => serverSelectAllKeys(options)
            : (sourceKeys ??
              (sourceLoad &&
                  ((options, signal) =>
                      sourceLoad({ ...options, skip: 0, take: 0 }, signal).then(({ rows }) => rows.map(rowIdOf)))));
    const selectAllMode = selectAllModeProp ?? (source && (serverSelectAllKeys || sourceKeys) ? 'allPages' : 'page');
    const selectAllPages = selectAllMode === 'allPages' && isServer && !!selectAllKeys;
    const selectAllDisabled =
        effectiveLoading || !table.getRowModel().flatRows.some((row) => row.getCanSelect());
    // Ключи всей выборки идут отдельным запросом и на больших списках он долгий:
    // пока он в пути, галочка крутится, а под таблицей висит статус с отменой.
    const [selectingAll, setSelectingAll] = useState(false);
    /** Готовится файл выгрузки (сервером или самой таблицей) — кнопка экспорта показывает ход и не даёт кликнуть
     *  второй раз. */
    const [exporting, setExporting] = useState(false);
    const [showExportLimitModal, setShowExportLimitModal] = useState(false);
    const selectAllRunRef = useRef(0);
    const selectAllAbortRef = useRef<AbortController | null>(null);
    const cancelSelectAll = useCallback(() => {
        selectAllRunRef.current += 1;
        selectAllAbortRef.current?.abort();
        selectAllAbortRef.current = null;
        setSelectingAll(false);
    }, []);
    useEffect(() => cancelSelectAll, [cancelSelectAll]);
    // Другая выборка (serverResetToken, фильтры, поиск): ключи «выбрать все» прежней, пришедшие
    // после смены, в выбор не пишутся — иначе действие над выбранными задело бы чужие строки.
    const selectAllScopeKey = `${serverResetToken}|${JSON.stringify(columnFilters)}|${globalFilter}`;
    const prevSelectAllScopeRef = useRef(selectAllScopeKey);
    useEffect(() => {
        if (prevSelectAllScopeRef.current === selectAllScopeKey) return;
        prevSelectAllScopeRef.current = selectAllScopeKey;
        cancelSelectAll();
    }, [selectAllScopeKey, cancelSelectAll]);
    // Выбор строк прежнего source.load в новой выборке не существует, а действие над выбранными задело бы
    // чужие записи (вкладки одного списка с разными наборами).
    const prevSourceGenerationRef = useRef(sourceGeneration.value);
    useEffect(() => {
        if (prevSourceGenerationRef.current === sourceGeneration.value) return;
        prevSourceGenerationRef.current = sourceGeneration.value;
        setRowSelection((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }, [sourceGeneration.value, setRowSelection]);
    const toggleAllRowsSelected = (e: ChangeEvent<HTMLInputElement>) => {
        if (selectAllDisabled) return;
        if (!selectAllPages) {
            table.getToggleAllRowsSelectedHandler()(e);
            return;
        }
        if (!e.target.checked) {
            cancelSelectAll();
            setRowSelection({});
            return;
        }
        const options = server.loadOptions;
        if (!options) return;
        const run = ++selectAllRunRef.current;
        selectAllAbortRef.current?.abort();
        const controller = new AbortController();
        selectAllAbortRef.current = controller;
        setSelectingAll(true);
        selectAllKeys?.({ ...options, skip: 0, take: server.totalCount }, controller.signal)
            .then((keys) => {
                // за время запроса выбор могли отменить или начать заново
                if (run !== selectAllRunRef.current) return;
                setSelectingAll(false);
                selectionWriterRef.current(Object.fromEntries(keys.map((key) => [key, true] as const)), false);
            })
            .catch((error: unknown) => {
                if (run !== selectAllRunRef.current) return;
                setSelectingAll(false);
                notify({
                    message: `Выбрать все строки не удалось. ${errorText(error, 'Повторите выбор.')}`,
                    traceId: errorTraceId(error),
                    variant: 'red',
                });
            });
    };

    // Потребитель узнаёт о смене выбора в том же обработчике, что её вызвал: подписка
    // на атом синхронна, и его setState попадает в один рендер с самой галочкой.
    // Эффект после рендера давал второй полный рендер таблицы на каждый клик.
    // Строки нужны групповым действиям: они работают не только по ключам.
    const notifySelectionRef = useRef<() => void>(() => {});
    notifySelectionRef.current = () =>
        onSelectionChange?.(
            table.getSelectedRowIds(),
            table.getSelectedRowModel().flatRows.map((r) => r.original),
        );
    // на маунте и со сменой колбэка потребитель, как раньше, получает текущий выбор;
    // подписка сама при подключении не срабатывает
    useEffect(() => {
        notifySelectionRef.current();
        const subscription = rowSelectionAtom.subscribe(() => notifySelectionRef.current());
        return () => subscription.unsubscribe();
    }, [rowSelectionAtom, onSelectionChange]);

    // Выключение selectable (например, переход формы edit→view без ремаунта)
    // должно снимать и подсветку is-selected — выбор больше неуправляем
    useEffect(() => {
        if (!selectable) setRowSelection((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }, [selectable, setRowSelection]);

    // Ключи удалённых строк остаются в rowSelection (TanStack их не чистит) —
    // «выбрать все» после удаления выбранных навечно застревал в indeterminate
    useEffect(() => {
        // при серверной выборке строки других страниц отсутствуют в data — их
        // ключи не «протухли», чистить выбор нельзя
        if (isServer) return;
        setRowSelection((prev) => {
            if (Object.keys(prev).length === 0) return prev;
            // в дереве — и ключи вложенных строк: их нет в data верхнего уровня
            const ids = new Set<string>();
            const collect = (rows: readonly T[]) =>
                rows.forEach((row, i) => {
                    ids.add(rowIdOf(row) || String(i));
                    const subRows = getSubRows?.(row);
                    if (subRows?.length) collect(subRows);
                });
            collect(data);
            const stale = Object.keys(prev).filter((k) => !ids.has(k));
            if (stale.length === 0) return prev;
            const next = { ...prev };
            stale.forEach((k) => delete next[k]);
            return next;
        });
        // getRowId стабилен по контракту
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Персист состояния — с дебаунсом, чтобы ресайз не спамил localStorage.
    // Тот же снапшот уходит в onStateChange — для серверных «видов» и state-в-URL.
    useEffect(() => {
        if (!stateKey && !onStateChange) return;
        const id = setTimeout(() => {
            const snapshot = {
                sorting,
                columnFilters,
                columnVisibility,
                columnSizing,
                columnOrder,
                columnPinning: pinningToSaved(columnPinning),
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
                grouping,
                wrapMode,
            };
            // Страница живёт только в state-в-URL: справочник со stateKey не должен
            // через localStorage открываться на «той» странице при каждом визите.
            if (stateKey) writeDataTableState(stateKey, { ...snapshot, pageIndex: undefined });
            onStateChange?.(snapshot);
        }, stateChangeDebounceMs);
        return () => clearTimeout(id);
    }, [
        stateKey,
        onStateChange,
        sorting,
        columnFilters,
        columnVisibility,
        columnSizing,
        columnOrder,
        columnPinning,
        pagination.pageIndex,
        pagination.pageSize,
        grouping,
        wrapMode,
        stateChangeDebounceMs,
    ]);

    const rows = allowRowPinning ? table.getCenterRows() : table.getRowModel().rows;
    const topRows = allowRowPinning ? table.getTopRows() : [];
    const filteredCount = table.getFilteredRowModel().rows.length;
    // ступень поиска известна после прогона фильтрации — строкой выше
    const globalSearchMode = isServer || globalFilter === '' ? 'phrase' : globalSearch.modeOf(globalFilter);
    // счётчик пейджера = то, что реально режет пагинация: верхнеуровневые
    // строки после группировки (для плоской таблицы совпадает с filteredCount),
    // при серверной группировке — группы верхнего уровня
    const pagedRowCount = isServer
        ? serverGroupActive
            ? groupsApi.groupCount
            : server.totalCount
        : paging
          ? table.getPrePaginatedRowModel().rows.length
          : filteredCount;
    // без autoResetPageIndex удаление строк может оставить страницу за пределом
    useEffect(() => {
        if (!paging) return;
        // На первом серверном рендере totalCount ещё равен нулю. Клэмп здесь
        // стирал страницу, восстановленную из URL, до завершения её запроса.
        if (isServer && server.loading) return;
        const pageCount = Math.max(1, Math.ceil(pagedRowCount / Math.max(1, pagination.pageSize)));
        if (pagination.pageIndex > pageCount - 1) {
            setPagination((prev) => ({ ...prev, pageIndex: pageCount - 1 }));
        }
    }, [paging, isServer, server.loading, pagedRowCount, pagination.pageIndex, pagination.pageSize, setPagination]);
    // порядок как у row.getVisibleCells(): start-pinned → center → end-pinned
    // (getVisibleLeafColumns порядок закрепления НЕ применяет — шапка разъехалась бы с ячейками)
    const visibleCols: DataTableColumn<T>[] = [
        ...table.getStartVisibleLeafColumns(),
        ...table.getCenterVisibleLeafColumns(),
        ...table.getEndVisibleLeafColumns(),
    ];
    // Пустой список колонок — это ещё не «скрыты»: так выглядит таблица, пока
    // схема не приехала, и подсказка про выбор столбцов там неуместна.
    // groupedColumnMode='remove' убирает колонки из visibleCols, но их значения
    // остаются в строках групп. Видимость проверяем по исходным колонкам схемы.
    const allColumnsHidden = columns.length > 0 && columns.every((column) => columnVisibility[column.field] === false);
    // набор колонок TanStack мемоизирует сам (схема, порядок, группировка), видимость — состояние таблицы
    const allLeafColumns = table.getAllLeafColumns();
    const chooserColumns = useMemo(
        () =>
            allLeafColumns.flatMap((c) => {
                const col = c.columnDef.meta?.dataColumn;
                return col
                    ? [
                          {
                              id: c.id,
                              header: col.header,
                              group: col.group,
                              visible: c.getIsVisible(),
                              locked: !c.getCanHide(),
                          },
                      ]
                    : [];
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allLeafColumns, tableColumnVisibility],
    );
    const applyChooserVisibility = useCallback(
        (updates: Record<string, boolean>) => setColumnVisibility((prev) => ({ ...prev, ...updates })),
        [setColumnVisibility],
    );
    const headerByCol = new Map(table.getFlatHeaders().map((h) => [h.column.id, h]));
    const hasAnyActions = !!rowActions || allowRowPinning;
    const hasActionsCol = hasAnyActions && rowActionsPosition !== 'left';
    const hasActionsLeft = hasAnyActions && rowActionsPosition === 'left';
    const actionsTrackW = rowActionsWidth ?? ACTIONS_TRACK;
    // В дереве кнопка разворота стоит в первой колонке рядом со значением,
    // поэтому отдельный трек ей не нужен — он только отодвигал бы
    // содержимое от края и съедал ширину.
    const showExpanderTrack = hasGrouping || (hasExpansionColumn && !hasTree);
    const expanderTrackWidth = 24;

    // ----- Треки CSS-grid: заданная width (или ресайз/закрепление) даёт
    // px-фиксированную колонку, колонки без width делят остаток ширины -----
    const hasFixedWidth = (c: (typeof visibleCols)[number]) =>
        columnSizing[c.id] != null || c.columnDef.meta?.dataColumn?.width != null || !!c.getIsPinned();
    // Ручная раскладка сохраняется целиком: сужение колонки не растягивает соседей.
    const fillLastColumnId =
        columnWidthMode === 'fill-last' && Object.keys(columnSizing).length === 0 && visibleCols.every(hasFixedWidth)
            ? visibleCols.findLast((c) => !c.getIsPinned())?.id
            : undefined;
    const isFixedTrack = (c: (typeof visibleCols)[number]) => hasFixedWidth(c) && c.id !== fillLastColumnId;
    const allFixed = visibleCols.every(isFixedTrack);
    const rightStickyCount = visibleCols.filter((c) => c.getIsPinned() === 'end').length + (hasActionsCol ? 1 : 0);
    // Распорка-«emptyColumn»: при сужении колонки место уходит распорке, а не последней
    // колонке. При наличии правой sticky-группы распорка стоит ПЕРЕД ней: правые sticky
    // обязаны примыкать к краю контента, иначе sticky right не держит их у края.
    const needSpacer = allFixed && visibleCols.length > 0;
    const spacerIndex = needSpacer ? visibleCols.length - (rightStickyCount - (hasActionsCol ? 1 : 0)) : -1;
    const tracks: string[] = [];
    const allowDrag = allowRowReordering && !!onRowReorder;
    // первый и последний трек несут увеличенный паддинг со стороны края строки
    const hasTrailingSpacer = needSpacer && rightStickyCount === 0;
    const px = (w: number, edge?: 'start' | 'end') => `${trackWidth(w, LAYOUT_METRICS, edge)}px`;
    const pxActions = (w: number, edge?: 'start' | 'end') => `${trackWidth(w, ACTIONS_METRICS, edge)}px`;
    const isFirstTrack = () => tracks.length === 0;
    if (allowDrag) tracks.push(px(DRAG_TRACK, 'start'));
    if (selectable) tracks.push(px(CHECK_TRACK, isFirstTrack() ? 'start' : undefined));
    if (hasActionsLeft) tracks.push(pxActions(actionsTrackW, isFirstTrack() ? 'start' : undefined));
    if (showExpanderTrack) tracks.push(px(expanderTrackWidth, isFirstTrack() ? 'start' : undefined));
    // Колонка с шириной-пожеланием (её придумал не конфиг, а список) ужимается до
    // своего минимума, когда все колонки не влезают. Заданную конфигурацией
    // ширину держим точно: её сжатие
    // схлопывало значения в таблицах форм. Закреплённые и растянутые вручную тоже
    // не сжимаем: sticky-офсеты считаются по точным ширинам.
    const hasPinnedCols = visibleCols.some((c) => !!c.getIsPinned());
    const canShrink = (c: (typeof visibleCols)[number]) =>
        // fitColumns: сжимаются и колонки с заданной шириной — в модалке ширина окна
        // важнее точного размера из шаблона (иначе список открывается со скроллом)
        (!!c.columnDef.meta?.dataColumn?.widthHint || fitColumns) &&
        !hasPinnedCols &&
        !c.getIsPinned() &&
        columnSizing[c.id] == null;
    // Колонка-дерево несёт кнопку разворота и отступ уровня — узкой ей быть нельзя,
    // пока ширину не задал сам пользователь ресайзом.
    const sizeOf = (c: (typeof visibleCols)[number], i: number) =>
        hasTree && i === 0 && columnSizing[c.id] == null ? Math.max(c.getSize(), TREE_LABEL_MIN) : c.getSize();
    visibleCols.forEach((c, i) => {
        if (i === spacerIndex && rightStickyCount > 0) tracks.push(SPACER_TRACK);
        const edge = isFirstTrack() ? ('start' as const) : undefined;
        const isLast = i === visibleCols.length - 1 && !hasActionsCol && !hasTrailingSpacer && rightStickyCount === 0;
        const side = edge ?? (isLast ? 'end' : undefined);
        const size = sizeOf(c, i);
        const minTrack = trackWidth(Math.min(c.columnDef.minSize ?? MIN_COL_WIDTH, size), LAYOUT_METRICS, side);
        tracks.push(
            isFixedTrack(c)
                ? canShrink(c)
                    ? `minmax(${minTrack}px, ${px(size, side)})`
                    : px(size, side)
                : `minmax(${trackWidth(c.id === fillLastColumnId ? size : (c.columnDef.minSize ?? MIN_COL_WIDTH), LAYOUT_METRICS, side)}px, ${c.columnDef.meta?.dataColumn?.flexWeight ?? 1}fr)`,
        );
    });
    if (needSpacer && rightStickyCount > 0 && spacerIndex === visibleCols.length) tracks.push(SPACER_TRACK);
    if (hasActionsCol) tracks.push(pxActions(actionsTrackW, 'end'));
    if (hasTrailingSpacer) tracks.push(SPACER_TRACK);
    const colsTemplate = tracks.join(' ');
    // Минимальная ширина строки = сумма минимумов треков (fr-колонки сжимаются до
    // minWidth и переносят текст) — а не max-content, который от одного длинного
    // значения растягивал грид и включал горизонтальный скролл.
    const rowMinWidth = computeRowMinWidth(
        tracks.map((t) => {
            const m = /minmax\((\d+(?:\.\d+)?)px/.exec(t) ?? /^(\d+(?:\.\d+)?)px/.exec(t);
            return m ? parseFloat(m[1]) : 0;
        }),
    );
    // распорочная ячейка вставляется в разметку строк/шапки/фильтров на той же позиции
    const spacerBeforeColId =
        needSpacer && rightStickyCount > 0 && spacerIndex < visibleCols.length ? visibleCols[spacerIndex].id : null;
    const spacerBeforeActions = needSpacer && rightStickyCount > 0 && spacerIndex === visibleCols.length;

    // ----- Раскладка закреплённых зон -----
    // Треки строки в порядке DOM: служебные, колонки данных, распорка, действия.
    // Офсеты и «вынос» фона считает dataTableLayout — там же инварианты смыкания
    // зон, здесь остаётся только описать состав строки.
    const layoutTracks = useMemo<ILayoutTrack[]>(() => {
        const list: ILayoutTrack[] = [];
        const width = (w: number) => trackWidth(w, LAYOUT_METRICS, list.length === 0 ? 'start' : undefined);
        const actionsWidth = (w: number) => trackWidth(w, ACTIONS_METRICS, list.length === 0 ? 'start' : undefined);
        if (allowDrag) list.push({ id: '__drag', width: width(DRAG_TRACK), pinned: 'left' });
        if (selectable) list.push({ id: '__check', width: width(CHECK_TRACK), pinned: 'left' });
        if (hasActionsLeft) list.push({ id: '__actionsLeft', width: actionsWidth(actionsTrackW), pinned: 'left' });
        if (showExpanderTrack) list.push({ id: '__expander', width: width(expanderTrackWidth), pinned: 'left' });
        visibleCols.forEach((c, i) => {
            if (c.id === spacerBeforeColId) list.push({ id: `__spacer-${c.id}`, width: null });
            list.push({
                id: c.id,
                width: isFixedTrack(c) ? width(sizeOf(c, i)) : null,
                pinned: pinSideOf(c),
            });
        });
        if (spacerBeforeActions) list.push({ id: '__spacerActions', width: null });
        if (hasActionsCol) {
            list.push({ id: '__actions', width: trackWidth(actionsTrackW, ACTIONS_METRICS, 'end'), pinned: 'right' });
        }
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        visibleCols,
        columnSizing,
        allowDrag,
        selectable,
        hasActionsLeft,
        hasActionsCol,
        actionsTrackW,
        showExpanderTrack,
        expanderTrackWidth,
        spacerBeforeColId,
        spacerBeforeActions,
        hasTree,
        fillLastColumnId,
    ]);

    const stickyLayout = useMemo(() => computeStickyLayout(layoutTracks), [layoutTracks]);
    const stickyVars = useMemo(() => computeStickyOffsetVars(stickyLayout), [stickyLayout]);
    // быстрый ресайз читает раскладку последнего рендера в момент первого движения
    const layoutTracksRef = useLatest(layoutTracks);
    const stickyLayoutRef = useLatest(stickyLayout);
    const stickyVarsRef = useLatest(stickyVars);

    // Стили закреплённых ячеек по id: позиция замирания (вынос фона — в CSS-переменных).
    // z 3: выше лупы фильтра (z1) и ручек ресайза (z2) скроллящихся колонок. Через переменную, чтобы строка под
    // курсором могла поднять свои закреплённые ячейки над ячейками соседних строк (иначе они закрывают её тень
    // подъёма). Офсет — переменной контейнера (stickyVars): ресайз закреплённой колонки не меняет стиль ячеек.
    const stickyStyles = useMemo(() => {
        const styles: Record<string, CSSProperties> = {};
        for (const [id, slot] of Object.entries(stickyLayout.slots)) {
            styles[id] = {
                position: 'sticky',
                [slot.side]: `var(${stickyVars.names[id]})`,
                zIndex: 'var(--dtb-pin-z, 3)',
            };
        }
        return styles;
    }, [stickyLayout, stickyVars]);
    /** Стиль закреплённой ячейки; у незакреплённой — undefined. */
    const stickyStyle = (id: string): CSSProperties | undefined => stickyStyles[id];
    /** Классы закреплённой ячейки. Зона важна: у строки фон плитки, у шапки и
     *  фильтров — поверхность таблицы, поэтому маркер `--pin` у них свой. */
    const stickyClass = (id: string, zone: 'row' | 'head' | 'filter' = 'row'): string | undefined => {
        const slot = stickyLayout.slots[id];
        if (!slot) return undefined;
        return clsx(
            zone === 'head' ? 'dtb__hcell--pin' : 'dtb__cell--pin',
            slot.side === 'left' ? 'dtb__stick-l' : 'dtb__stick-r',
            slot.edge && (slot.side === 'left' ? 'dtb__stick-first' : 'dtb__stick-last'),
            id === stickyLayout.leftEdgeId && 'dtb__pin-edge-left',
            id === stickyLayout.rightEdgeId && 'dtb__pin-edge-right',
        );
    };
    // Ключ раскладки закреплённых зон для версий вида строк и ячеек: то, что они берут из неё через
    // stickyClass/stickyStyle. Ширины колонок строки не читают (их несут --dtb-cols и офсеты --dtb-pin-* контейнера),
    // поэтому ресайз колонки, и закреплённой тоже, строки и ячейки не перерисовывает. Имя переменной офсета ключу
    // не нужно: оно выводится из стороны и порядка слотов (computeStickyOffsetVars).
    const stickyLayoutKey = [
        ...Object.entries(stickyLayout.slots).map(([id, slot]) => `${id}:${slot.side}:${slot.edge ? 1 : 0}`),
        stickyLayout.leftEdgeId,
        stickyLayout.rightEdgeId,
    ].join('\u0000');
    /** Переменные раскладки строк: шаблон треков, минимальная ширина и офсеты закреплённых зон. */
    const tableVars = useMemo(
        () =>
            ({
                '--dtb-cols': colsTemplate,
                '--dtb-minw': `${rowMinWidth}px`,
                ...stickyVars.values,
            }) as CSSProperties,
        [colsTemplate, rowMinWidth, stickyVars],
    );

    // ----- Строка групп колонок над заголовками (двухуровневая шапка) -----
    // Группы — по текущему порядку и видимости колонок; без групп строки нет вовсе.
    const groupSources = visibleCols.map((c) => ({
        id: c.id,
        group: c.columnDef.meta?.dataColumn?.group,
        pinned: pinSideOf(c),
    }));
    const showColumnGroups = hasColumnGroups(groupSources);
    const columnGroupBands = showColumnGroups ? computeColumnGroupBands(groupSources, spacerBeforeColId) : [];
    // служебные треки слева — в том порядке, в каком их уже разложил layoutTracks
    const groupLeadTracks = layoutTracks.filter((t) => LEAD_SERVICE_TRACKS.has(t.id)).map((t) => t.id);
    /** Закрепление ячейки строки групп: позиция — по её треку у края зоны, разделитель
     *  зоны — если среди треков ячейки крайний закреплённый. */
    const stickyBand = (ids: string[]) => {
        const slot = stickyLayout.slots[ids[0]];
        if (!slot) return {};
        const anchor = slot.side === 'left' ? ids[0] : ids[ids.length - 1];
        const { leftEdgeId, rightEdgeId } = stickyLayout;
        return {
            className: clsx(
                stickyClass(anchor, 'head'),
                leftEdgeId && ids.includes(leftEdgeId) && 'dtb__pin-edge-left',
                rightEdgeId && ids.includes(rightEdgeId) && 'dtb__pin-edge-right',
            ),
            style: stickyStyle(anchor),
        };
    };

    // ----- Фильтры: filter-row и header-фильтр — независимые части одной записи -----
    // Колбэки стабильные: их держат мемоизированные ячейки фильтров, которые иначе
    // перерисовывались бы на каждый кадр прокрутки.
    const patchFilter = useCallback((id: string, patch: Partial<IDataTableColumnFilter>) => {
        setDraftFilters((prev) => patchColumnFilters(prev, id, patch));
    }, []);

    // Ввод в строке фильтров всегда идёт в черновик. Обычно он тут же применяется,
    // а с applyFiltersOnClick ждёт кнопки «Применить фильтр»
    // (конфигурация applyGridFilterOnClick): выборка не дёргается на каждый символ.
    // Смена применённого фильтра возвращает на первую страницу: текущая страница
    // нового (меньшего) набора может быть пустой или вообще не существовать.
    const lastAppliedFiltersRef = useRef(columnFilters);
    const applyDraftFilters = useCallback(
        (draft: ColumnFiltersState) => {
            const changed = JSON.stringify(lastAppliedFiltersRef.current) !== JSON.stringify(draft);
            lastAppliedFiltersRef.current = draft;
            setColumnFilters(draft);
            if (changed) resetPageIndex();
        },
        [resetPageIndex],
    );
    useEffect(() => {
        if (applyFiltersOnClick) return;
        applyDraftFilters(draftFilters);
    }, [applyFiltersOnClick, draftFilters, applyDraftFilters]);

    const filtersDirty = applyFiltersOnClick && JSON.stringify(draftFilters) !== JSON.stringify(columnFilters);
    const applyFilters = useCallback(() => applyDraftFilters(draftFilters), [draftFilters, applyDraftFilters]);
    // Enter в строке фильтров — то же, что кнопка «Применить». Ячейка отдаёт набранное в том же
    // событии, поэтому черновик применяется после рендера, в котором он уже лежит в состоянии.
    const draftFiltersRef = useRef(draftFilters);
    draftFiltersRef.current = draftFilters;
    const [filterSubmitToken, setFilterSubmitToken] = useState(0);
    const submitFilters = useCallback(() => setFilterSubmitToken((token) => token + 1), []);
    useEffect(() => {
        if (filterSubmitToken) applyDraftFilters(draftFiltersRef.current);
    }, [filterSubmitToken, applyDraftFilters]);
    const setRowFilter = useCallback(
        (id: string, v: IDataTableFilterValue | null) => patchFilter(id, { row: v ?? undefined }),
        [patchFilter],
    );
    const setHeaderFilter = useCallback(
        (id: string, vals: (string | null)[] | null) => {
            const patch = { header: vals ?? undefined };
            patchFilter(id, patch);
            // Отметка в воронке — выбор, а не набор текста: с applyFiltersOnClick применяется сразу
            // поверх применённых условий, набранное в строке фильтров по-прежнему ждёт кнопки.
            if (applyFiltersOnClick) applyDraftFilters(patchColumnFilters(lastAppliedFiltersRef.current, id, patch));
        },
        [patchFilter, applyFiltersOnClick, applyDraftFilters],
    );
    // строка фильтров показывает то, что человек набрал, — то есть черновик
    const columnFilterOf = (id: string): IDataTableColumnFilter | undefined =>
        draftFilters.find((f) => f.id === id)?.value as IDataTableColumnFilter | undefined;
    const hasActiveFilters = columnFilters.length > 0 || globalFilter !== '';
    // Снятие фильтра колонки (чип, меню колонки) применяется сразу и с applyFiltersOnClick. Чистится
    // и черновик: иначе строка фильтров показывала бы снятое значение, а «Применить» вернуло бы его.
    const removeColumnFilter = (id: string) => {
        setDraftFilters((prev) => prev.filter((f) => f.id !== id));
        applyDraftFilters(lastAppliedFiltersRef.current.filter((f) => f.id !== id));
    };
    const clearSearch = () => setSearchText('');
    const clearFilters = () => {
        setDraftFilters([]);
        lastAppliedFiltersRef.current = [];
        setColumnFilters([]);
        setSearchText('');
    };
    // «Сбросить фильтры» в интерфейсе таблицы снимает и фильтры страницы; controlRef.clearFilters — только свои.
    const resetFilters = () => {
        clearFilters();
        onResetFilters?.();
    };

    // Событийные колбэки потребителя зовутся свежими, а их новая ссылка не перезапускает эффекты и ручку controlRef;
    // наличие обработчика (кликабельность строки) — по самому пропу
    const events = useStableHandlers({ onRowClick, onRowDoubleClick, onExpandRow });

    // controlRef.expandRow узла, которого ещё нет в модели или который пока не раскрывается: детей
    // у потребителя (onExpandRow) просим, когда строка появится и сможет раскрыться.
    const pendingExpandRef = useRef(new Set<string>());
    useEffect(() => {
        pendingExpandRef.current.forEach((id) => {
            const row = allRowsModel.rowsById[id];
            if (!row?.getCanExpand()) return;
            pendingExpandRef.current.delete(id);
            if (row.getIsExpanded()) void events.onExpandRow?.(row.original);
        });
    }, [allRowsModel, events]);

    // «Свернуть / развернуть все» группы. Клиентские группы без колонки раскрытия держат весь набор одним флагом
    // (`true` — раскрыто всё, в том числе группы, которые появятся); с деревом или деталями строки меняются только
    // флаги групп, чтобы не раскрыть детали каждой строки. Серверные — своим состоянием групп.
    const groupingActive = grouping.length > 0;
    const groupRowsOf = () => table.getGroupedRowModel().flatRows.filter((row) => row.getIsGrouped());
    const groupsExpanded =
        showGroupsToggle &&
        groupingActive &&
        (serverGroupActive
            ? groupsApi.anyExpanded
            : table.getGroupedRowModel().flatRows.some((row) => row.getIsGrouped() && row.getIsExpanded()));
    const canExpandAllGroups = !serverGroupActive || groupsApi.canExpandAll;
    const setAllGroupsExpanded = (open: boolean) => {
        if (!groupingActive) return;
        if (serverGroupActive) {
            groupsApi.setAllExpanded(open);
            return;
        }
        if (!hasExpansionColumn) {
            expandedAtom.set(open ? true : {});
            return;
        }
        const ids = groupRowsOf().map((row) => row.id);
        expandedAtom.set((prev) => {
            const next: Record<string, boolean> = prev === true ? {} : { ...prev };
            ids.forEach((id) => {
                if (open) next[id] = true;
                else delete next[id];
            });
            return next;
        });
    };
    const setAllGroupsExpandedRef = useRef(setAllGroupsExpanded);
    setAllGroupsExpandedRef.current = setAllGroupsExpanded;

    // Императивная ручка: то, что нельзя выразить пропами — перечитать данные,
    // снять выделение, раскрыть строку. Состав — только операции, которые
    // реально зовут потребители.
    useImperativeHandle(
        controlRef,
        () => ({
            refresh: () => server.reload?.(),
            clearSelection: () => {
                cancelSelectAll();
                setRowSelection({});
            },
            selectRows: (keys) =>
                selectionWriterRef.current(Object.fromEntries(keys.map((key) => [String(key), true] as const)), false),
            getSelected: () => ({
                keys: table.getSelectedRowIds(),
                rows: table.getSelectedRowModel().flatRows.map((row) => row.original),
            }),
            expandRow: (key, expanded = true) => {
                const id = String(key);
                const row = table.getRowModel().rowsById[id] ?? table.getCoreRowModel().rowsById[id];
                pendingExpandRef.current.delete(id);
                if (!hasTree || (row && !expanded)) {
                    if (!row) return;
                    row.toggleExpanded(expanded);
                    if (expanded) void events.onExpandRow?.(row.original);
                    return;
                }
                // Узел дерева раскрывается по ключу, даже если его ещё нет в модели или детей пока не видно:
                // флаг ставится сразу, и строка покажется раскрытой, как только у неё появятся дети.
                const patch = (prev: ExpandedState): ExpandedState => {
                    if (prev === true || !!prev[id] === expanded) return prev;
                    const next = { ...prev };
                    if (expanded) next[id] = true;
                    else delete next[id];
                    return next;
                };
                expandedAtom.set(patch);
                // фильтр дерева раскрыл всё на время — запрос переживает и его снятие
                if (expandedBeforeFilterRef.current)
                    expandedBeforeFilterRef.current = patch(expandedBeforeFilterRef.current);
                if (!expanded) return;
                if (row?.getCanExpand()) void events.onExpandRow?.(row.original);
                else pendingExpandRef.current.add(id);
            },
            setExpandedRows: (keys) => {
                const ids = new Set(keys.map(String));
                pendingExpandRef.current.forEach((id) => {
                    if (!ids.has(id)) pendingExpandRef.current.delete(id);
                });
                expandedAtom.set(Object.fromEntries([...ids].map((id) => [id, true])));
            },
            clearFilters,
            setColumnFilters: (filters) => {
                const next = filters.map((filter) => ({ id: filter.id, value: filter.value }));
                setDraftFilters(next);
                lastAppliedFiltersRef.current = next;
                setColumnFilters(next);
                resetPageIndex();
            },
            setGrouping: (columnIds) => setGrouping(groupableOnly([...columnIds], columnsRef.current)),
            setGlobalFilter: setSearchText,
            exitFullscreen: fullscreenApi.exit,
            expandAllGroups: () => setAllGroupsExpandedRef.current(true),
            collapseAllGroups: () => setAllGroupsExpandedRef.current(false),
            // срезы в атомах читаются на момент вызова: ручку зовут и сразу после
            // selectRows/смены страницы, до перерисовки
            getState: () => ({
                sorting,
                columnFilters,
                columnVisibility,
                columnSizing: columnSizingAtom.get(),
                columnOrder: columnOrderAtom.get(),
                columnPinning: pinningToSaved(columnPinningAtom.get()),
                pageIndex: paginationAtom.get().pageIndex,
                pageSize: paginationAtom.get().pageSize,
                grouping,
                wrapMode,
            }),
        }),
        // Методы table замкнуты на одно ядро таблицы: объект из прошлого рендера
        // (useTable отдаёт новый на каждый) читает актуальное состояние.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            server.reload,
            sorting,
            columnFilters,
            columnVisibility,
            grouping,
            wrapMode,
            hasTree,
            fullscreenApi.exit,
        ],
    );

    // Значения header-фильтра со счётчиками, лениво при открытии. Каскад:
    // список дофильтрован фильтрами
    // остальных колонок и глобальным поиском; у СВОЕЙ колонки исключается только
    // header-часть (filter-row своей колонки продолжает сужать список) — иначе нельзя
    // расширить уже сделанный выбор. Выбранные значения, отрезанные чужими фильтрами,
    // всё равно попадают в список (count 0) — иначе их не снять точечно.
    /** Видимый текст ячейки: у колонок с displayFormat он отличается от значения. */
    const cellTextOf = useCallback(
        (col: IDataTableColumn<T> | undefined, row: DataTableRow<T>, colId: string): string =>
            col?.cellText ? col.cellText(row.original) : formatCellText(row.getValue(colId), col?.dataType),
        [],
    );

    // buildFacet пересоздаётся каждый рендер (читает свежий черновик фильтров), а facetOf
    // обязан быть стабильным (его держит мемоизированная воронка) — читаем через ref.
    const buildFacetRef = useRef<(colId: string) => IDataTableFacetValue[]>(() => []);
    // Значения воронки у источника (source.facets): выборка колонки — те же параметры, что у загрузки, без
    // отметок в её воронке. Параметры колонки считаются один раз на состояние фильтров; их ключ (scope) при
    // открытой воронке отменяет устаревший запрос и задаёт новый. Условия колонок идут по id: отметка в
    // воронке переставляет фильтр колонки в конец, и без порядка своя же отметка перечитывала бы список.
    const sourceFacets = getHeaderFilterValues ? undefined : source?.facets;
    const sourceFacetPage = getHeaderFilterValues ? undefined : source?.facetPage;
    const facetSelectionOf = useMemo(() => {
        if (!sourceFacets && !sourceFacetPage) return undefined;
        const cache = new Map<string, { options: IServerLoadOptions; scope: string }>();
        return (colId: string) => {
            let entry = cache.get(colId);
            if (!entry) {
                const options = buildServerLoadOptions({
                    pageIndex: 0,
                    pageSize: 0,
                    sorting: serverSorting ? sorting : EMPTY_SORTING,
                    columnFilters: [...withoutHeaderFilter(columnFilters, colId)].sort((a, b) =>
                        a.id.localeCompare(b.id),
                    ),
                    globalFilter,
                    searchFields: serverSearchFields,
                    filterKinds,
                });
                entry = { options, scope: `${serverResetToken}|${JSON.stringify(options)}` };
                cache.set(colId, entry);
            }
            return entry;
        };
    }, [
        sourceFacets,
        sourceFacetPage,
        serverSorting,
        sorting,
        columnFilters,
        globalFilter,
        serverSearchFields,
        filterKinds,
        serverResetToken,
    ]);
    const sourceFacetRef = useRef<((colId: string, signal: AbortSignal) => Promise<IDataTableFacetValue[]>) | null>(
        null,
    );
    sourceFacetRef.current =
        sourceFacets && facetSelectionOf
            ? (colId, signal) =>
                  sourceFacets(
                      {
                          field: colId,
                          options: facetSelectionOf(colId).options,
                          selected: columnFilterOf(colId)?.header ?? [],
                      },
                      signal,
                  )
            : null;
    const facetOf = useCallback(
        (colId: string, signal?: AbortSignal) =>
            getHeaderFilterValues
                ? getHeaderFilterValues(colId)
                : (sourceFacetRef.current?.(colId, signal ?? new AbortController().signal) ??
                  buildFacetRef.current(colId)),
        [getHeaderFilterValues],
    );
    // Подписи отмеченных значений для чипов строки активных фильтров; у клиентского фасета подпись — сам ключ.
    const facetLabels = useDataTableFacetLabels({
        enabled: showFilterSummary && (!!getHeaderFilterValues || !!sourceFacets),
        resolveMissing: resolveFilterSummaryLabels,
        ready: !serverLoad || !server.loading,
        filters: columnFilters,
        source: facetOf,
    });

    // Порции воронки (source.facetPage): выборка колонки та же, что у facets, поверх неё — поиск по значениям и
    // отбор отмеченных. Подписи из порций копятся без перерисовки таблицы, в чипы строки активных фильтров
    // попадают только отмеченные (setHeaderFilterFromPages).
    const facetPageLabelsRef = useRef(new Map<string, Map<string | null, string>>());
    const facetPageRef = useRef<HeaderFilterPageLoader | null>(null);
    facetPageRef.current =
        sourceFacetPage && facetSelectionOf
            ? async (colId, { skip, take, search, keys }, signal): Promise<IDataTableFacetPage> => {
                  const options = facetSelectionOf(colId).options;
                  const filter = facetPageFilter(options, colId, { search, keys }, filterKinds[colId]);
                  if (filter === null) return { values: [], hasMore: false };
                  const page = await sourceFacetPage({ field: colId, options: { ...options, filter }, skip, take }, signal);
                  if (!signal.aborted) {
                      const known = facetPageLabelsRef.current.get(colId) ?? new Map<string | null, string>();
                      page.values.forEach((v) => known.set(v.key, v.label));
                      facetPageLabelsRef.current.set(colId, known);
                  }
                  return page;
              }
            : null;
    const loadFacetPage = useCallback<HeaderFilterPageLoader>(
        (colId, query, signal) =>
            facetPageRef.current?.(colId, query, signal) ?? Promise.resolve({ values: [], hasMore: false }),
        [],
    );
    const rememberFacetLabels = facetLabels.remember;
    const setHeaderFilterFromPages = useCallback(
        (id: string, vals: (string | null)[] | null) => {
            const known = facetPageLabelsRef.current.get(id);
            if (known && vals) {
                rememberFacetLabels(
                    id,
                    vals.flatMap((key) => {
                        const label = known.get(key);
                        return label === undefined ? [] : [{ key, label, count: 0 }];
                    }),
                );
            }
            setHeaderFilter(id, vals);
        },
        [rememberFacetLabels, setHeaderFilter],
    );

    buildFacetRef.current = (colId: string): IDataTableFacetValue[] => {
        const col = columns.find((c) => c.field === colId);
        const dataType = col?.dataType;
        const kind = filterKindOf(dataType);
        const own = columnFilterOf(colId);
        const otherFilters = columnFilters.filter((f) => f.id !== colId);
        const searchColumns = table.getAllLeafColumns().filter((c) => c.getCanGlobalFilter());
        const passesOthers = (r: DataTableRow<T>): boolean => {
            for (const f of otherFilters) {
                const column = table.getColumn(f.id);
                const fn = column?.getFilterFn();
                if (fn && !fn(r, f.id, f.value, () => undefined)) return false;
            }
            // row-часть собственного фильтра применяется, header-часть — нет
            if (own?.row && !isFilterValueEmpty(own.row)) {
                if (!dxMatch(kind, r.getValue(colId), own.row.op, own.row.value)) return false;
            }
            if (
                globalFilter !== '' &&
                !matchesGlobalSearch(
                    searchColumns.map((c) => r.getValue(c.id)),
                    globalFilter,
                    globalSearchMode,
                )
            ) {
                return false;
            }
            return true;
        };
        const counts = new Map<string | null, number>();
        for (const r of table.getCoreRowModel().flatRows) {
            if (!passesOthers(r)) continue;
            const text = cellTextOf(col, r, colId);
            const key = text === '' ? null : text;
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        for (const key of own?.header ?? []) {
            if (!counts.has(key)) counts.set(key, 0);
        }
        const list = [...counts.entries()].map(([key, count]) => ({
            key,
            label: key ?? '(Пусто)',
            count,
        }));
        list.sort((a, b) =>
            a.key === null ? -1 : b.key === null ? 1 : a.label.localeCompare(b.label, 'ru', { numeric: true }),
        );
        return list;
    };

    // ----- Перетаскивание заголовков: drag и drop — вся ячейка шапки. Нажатие на
    // ручке ресайза/кнопках отменяет dragstart (иначе HTML5-drag перехватывал бы
    // pointer-события ресайза); гард ставится на pointerdown — он приходит раньше -----
    const dragColRef = useRef<string | null>(null);
    const headerPressGuardRef = useRef(false);
    // Цель переноса — атрибутом на заголовке, без state: иначе каждая граница при перетаскивании давала два рендера
    // таблицы со всеми строками
    const dropCellRef = useRef<HTMLElement | null>(null);
    const markDropCell = (cell: HTMLElement | null) => {
        if (dropCellRef.current === cell) return;
        dropCellRef.current?.removeAttribute('data-dropover');
        cell?.setAttribute('data-dropover', '');
        dropCellRef.current = cell;
    };
    const canReorder = (c: (typeof visibleCols)[number]) => {
        const col = c.columnDef.meta?.dataColumn;
        return !!col && col.allowReordering !== false && !c.getIsPinned();
    };
    const headerDragProps = (c: (typeof visibleCols)[number]) =>
        canReorder(c)
            ? {
                  draggable: true,
                  onPointerDownCapture: (e: ReactPointerEvent<HTMLElement>) => {
                      headerPressGuardRef.current = !!(e.target as HTMLElement | null)?.closest(
                          '.dtb__resize, button, input',
                      );
                  },
                  onDragStart: (e: DragEvent) => {
                      if (headerPressGuardRef.current) {
                          e.preventDefault();
                          return;
                      }
                      dragColRef.current = c.id;
                      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
                  },
                  onDragEnd: () => {
                      dragColRef.current = null;
                      markDropCell(null);
                  },
              }
            : {};
    const headerDropProps = (c: (typeof visibleCols)[number]) =>
        canReorder(c)
            ? {
                  onDragOver: (e: DragEvent) => {
                      if (dragColRef.current && dragColRef.current !== c.id) {
                          e.preventDefault();
                          markDropCell(e.currentTarget as HTMLElement);
                      }
                  },
                  onDragLeave: (e: DragEvent) => {
                      if (dropCellRef.current === e.currentTarget) markDropCell(null);
                  },
                  onDrop: (e: DragEvent) => {
                      e.preventDefault();
                      const from = dragColRef.current;
                      dragColRef.current = null;
                      markDropCell(null);
                      if (!from || from === c.id) return;
                      setColumnOrder((prev) => {
                          const order = [...prev];
                          const fromIdx = order.indexOf(from);
                          const toIdx = order.indexOf(c.id);
                          if (fromIdx < 0 || toIdx < 0) return prev;
                          order.splice(fromIdx, 1);
                          order.splice(toIdx, 0, from);
                          return order;
                      });
                  },
              }
            : {};

    // ----- Панель группировки: drop заголовка = группировка -----
    const [panelOver, setPanelOver] = useState(false);
    const chipDragRef = useRef<string | null>(null);
    const removeGrouping = (id: string) => setGrouping((prev) => prev.filter((g) => g !== id));
    const canGroupBy = (id: string) => columns.find((c) => c.field === id)?.allowGrouping !== false;

    // ----- Контекстные меню (шапка/строка) в точке курсора -----
    const ctxMenuRef = useRef<IDataTableContextMenuHandle>(null);

    const buildHeaderMenu = (c: (typeof visibleCols)[number]): IDataTableMenuEntry[] => {
        const entries: IDataTableMenuEntry[] = [];
        if (c.getCanSort()) {
            entries.push(
                { key: 'sort-asc', label: 'Сортировка по возрастанию', onClick: () => c.toggleSorting(false) },
                { key: 'sort-desc', label: 'Сортировка по убыванию', onClick: () => c.toggleSorting(true) },
            );
            if (c.getIsSorted()) {
                entries.push({ key: 'sort-clear', label: 'Сбросить сортировку', onClick: () => c.clearSorting() });
            }
            entries.push({ key: 'd-sort', divider: true });
        }
        if (showGroupPanel && (grouping.includes(c.id) || canGroupBy(c.id))) {
            entries.push(
                grouping.includes(c.id)
                    ? { key: 'ungroup', label: 'Убрать из группировки', onClick: () => removeGrouping(c.id) }
                    : {
                          key: 'group',
                          label: 'Группировать по этой колонке',
                          onClick: () => setGrouping((prev) => [...prev, c.id]),
                      },
            );
            entries.push({ key: 'd-group', divider: true });
        }
        // закрепление, зафиксированное кодом (allowPinning: false), пользователь
        // не меняет — пунктов пин/анпин в меню нет вовсе
        if (c.columnDef.meta?.dataColumn?.allowPinning !== false) {
            const pinned = c.getIsPinned();
            if (pinned !== 'start')
                entries.push({ key: 'pin-left', label: 'Закрепить слева', onClick: () => c.pin('start') });
            if (pinned !== 'end') {
                entries.push({ key: 'pin-right', label: 'Закрепить справа', onClick: () => c.pin('end') });
            }
            if (pinned) entries.push({ key: 'unpin', label: 'Открепить', onClick: () => c.pin(false) });
            entries.push({ key: 'd-pin', divider: true });
        }
        // без кнопки выбора колонок скрытую колонку негде вернуть — пункт не показываем
        if (showColumnChooser && c.getCanHide()) {
            entries.push({ key: 'hide', label: 'Скрыть колонку', onClick: () => c.toggleVisibility(false) });
        }
        if (showColumnChooser) {
            entries.push({ key: 'chooser', label: 'Выбор колонок…', onClick: () => chooserRef.current?.open() });
        }
        entries.push({ key: 'd-cols', divider: true });
        if (columnFilterOf(c.id)) {
            entries.push({
                key: 'filter-clear',
                label: 'Сбросить фильтр колонки',
                onClick: () => removeColumnFilter(c.id),
            });
        }
        if (hasActiveFilters) {
            entries.push({ key: 'filters-clear', label: 'Сбросить все фильтры', onClick: resetFilters });
        }
        if (columnSizing[c.id] != null) {
            entries.push({ key: 'width-reset', label: 'Сбросить ширину', onClick: () => c.resetSize() });
        }
        if (Object.keys(columnSizing).length > 0) {
            entries.push({
                key: 'widths-reset',
                label: 'Сбросить ширины столбцов',
                onClick: () => table.resetColumnSizing(true),
            });
        }
        return compressMenuDividers(entries);
    };

    /**
     * Начало ручного ресайза фиксирует ширины «дышащих» колонок.
     *
     * Колонка с шириной-пожеланием ужимается, когда таблица не влезает
     * (см. canShrink), а fr-колонка делит остаток. Пока ширины никто не трогал,
     * это правильно, но стоит потянуть одну колонку — и соседние «дышат» следом.
     * Запоминаем их фактические ширины: дальше раскладка держит их точно, а
     * нехватка места уходит в горизонтальную прокрутку.
     *
     * Точные px-колонки (ширина из конфигурации без сжатия) не трогаем: их
     * ширина и так стабильна, а запись дублировала бы конфиг в columnSizing.
     * Мерится физическая ширина ячейки шапки = ширина трека С паддингами, а в
     * columnSizing живёт ЛОГИЧЕСКАЯ ширина (трек рендерится через trackWidth,
     * который паддинги прибавляет сам) — без вычета каждая колонка прыгала бы
     * на +14px в момент нажатия на ручку ресайза.
     */
    const freezeShrinkableWidths = (targetId?: string) => {
        const head = theadRef.current?.querySelector('.dtb__head');
        if (!head) return;
        // целевая колонка ресайза фиксируется всегда: быстрый путь читает её
        // стартовую логическую ширину из columnSizing
        const breathing = new Set(
            visibleCols.filter((c) => c.id === targetId || !isFixedTrack(c) || canShrink(c)).map((c) => c.id),
        );
        const sizing = columnSizingAtom.get();
        const measured: Record<string, number> = {};
        head.querySelectorAll<HTMLElement>('[data-head-col]').forEach((cell) => {
            const id = cell.dataset.headCol;
            if (!id || sizing[id] != null || !breathing.has(id)) return;
            const edge = cell === head.firstElementChild || cell === head.lastElementChild;
            const pads = PAD_CELL + (edge ? PAD_EDGE : PAD_CELL);
            measured[id] = Math.max(1, Math.round(cell.getBoundingClientRect().width) - pads);
        });
        if (Object.keys(measured).length > 0) setColumnSizing((prev) => ({ ...measured, ...prev }));
    };

    // Ресайз, отпущенный над заголовком, порождает click по заголовку → гасим
    // сортировку одного следующего клика (сброс после цикла события)
    const resizeClickGuardRef = useRef(false);
    /** Общее начало любого ресайза: гард клика + заморозка ширин соседей. */
    const beginResizeGuard = (targetId?: string) => {
        resizeClickGuardRef.current = true;
        freezeShrinkableWidths(targetId);
        window.addEventListener(
            'pointerup',
            () => {
                setTimeout(() => {
                    resizeClickGuardRef.current = false;
                }, 0);
            },
            { once: true },
        );
    };
    const withResizeGuard = (targetId: string, handler: (e: unknown) => void) => (e: { nativeEvent?: unknown }) => {
        beginResizeGuard(targetId);
        handler(e);
    };

    /**
     * Live-ресайз колонки мимо React: движение курсора меняет только CSS-переменные
     * шаблона треков (`--dtb-cols`) и минимальной ширины строки, а состояние
     * коммитится один раз на отпускании. Путь через `columnResizeMode='onChange'`
     * прогонял setState на каждый сдвиг мыши — перерисовывались шапка, фильтры и
     * все видимые строки. Sticky-офсеты закреплённых треков за колонкой (слева —
     * следующих, справа — предыдущих) двигаются на ту же дельту в своих переменных
     * `--dtb-pin-*`, иначе зоны разъезжались бы с шаблоном до отпускания.
     */
    const startFastResize = (c: (typeof visibleCols)[number]) => (e: ReactPointerEvent<HTMLElement>) => {
        beginResizeGuard(c.id);
        e.preventDefault();
        // захват указателя не нужен: move/up слушает window, а setPointerCapture
        // бросает NotFoundError на синтетических событиях (юнит-тесты, автотесты)
        const handle = e.currentTarget;
        handle.classList.add('is-resizing');
        const startX = e.clientX;
        const minLogical = c.columnDef.minSize ?? MIN_COL_WIDTH;
        // Снимок шаблона берётся лениво на первом движении: заморозка ширин выше пишет
        // в атом сразу, но токены шаблона обновит только её рендер — до него токены
        // авто-колонок ещё fr-«резиновые»
        let drag: {
            tokens: string[];
            index: number;
            startToken: number;
            startLogical: number;
            startMinW: number;
            pins: { name: string; start: number }[];
        } | null = null;
        const snapshot = () => {
            const wrap = tablewrapRef.current;
            const head = theadRef.current?.querySelector('.dtb__head');
            const cell = head?.querySelector<HTMLElement>(`[data-head-col="${CSS.escape(c.id)}"]`);
            if (!wrap || !head || !cell) return null;
            const tokens = getComputedStyle(wrap).getPropertyValue('--dtb-cols').trim().match(TRACK_TOKEN) ?? [];
            const index = Array.prototype.indexOf.call(head.children, cell);
            const startLogical = columnSizingAtom.get()[c.id];
            if (index < 0 || tokens.length !== head.children.length || startLogical == null) return null;
            // целевой токен обязан быть px-фиксированным — иначе рендер заморозки
            // ширин ещё не долетел, и снимок взял бы fr-«резину». Остальные треки
            // px не обязаны: колонку без схемы (нет data-head-col) заморозка не
            // трогает, её fr живёт и во время перетаскивания
            if (!/^\d/.test(tokens[index])) return null;
            const tracks = layoutTracksRef.current;
            const layout = stickyLayoutRef.current;
            const { names } = stickyVarsRef.current;
            const target = tracks.findIndex((t) => t.id === c.id);
            const pins = tracks.flatMap((t, i) => {
                const shifts = t.pinned === 'left' ? i > target : t.pinned === 'right' && i < target;
                const slot = layout.slots[t.id];
                return shifts && slot ? [{ name: names[t.id], start: slot.offset }] : [];
            });
            return {
                tokens,
                index,
                startToken: parseFloat(tokens[index]),
                startLogical,
                startMinW: parseFloat(getComputedStyle(wrap).getPropertyValue('--dtb-minw')) || 0,
                pins,
            };
        };
        let lastLogical: number | null = null;
        // trackWidth аддитивен, поэтому токен и логическая ширина двигаются
        // на одну и ту же дельту — коммит на отпускании не даст скачка
        const apply = (logical: number) => {
            const wrap = tablewrapRef.current;
            if (!drag || !wrap) return;
            const delta = logical - drag.startLogical;
            drag.tokens[drag.index] = `${drag.startToken + delta}px`;
            wrap.style.setProperty('--dtb-cols', drag.tokens.join(' '));
            wrap.style.setProperty('--dtb-minw', `${drag.startMinW + delta}px`);
            for (const pin of drag.pins) wrap.style.setProperty(pin.name, `${pin.start + delta}px`);
        };
        const onMove = (ev: PointerEvent) => {
            drag ??= snapshot();
            if (!drag) return;
            lastLogical = Math.max(minLogical, drag.startLogical + ev.clientX - startX);
            apply(lastLogical);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            handle.classList.remove('is-resizing');
            if (lastLogical != null) {
                const width = Math.round(lastLogical);
                // переменные — под округлённую ширину: совпади она с прежней, рендер style не перепишет, и в DOM
                // остались бы доли px последнего движения
                apply(width);
                setColumnSizing((prev) => ({ ...prev, [c.id]: width }));
            }
            // сброс click-гарда делает pointerup-слушатель beginResizeGuard
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp, { once: true });
    };

    // Модалку, открытую из ячейки (справочник, адресная книга), Mantine рендерит
    // порталом: в DOM она вне таблицы, но по дереву React события всплывают в
    // строку-родителя. Без проверки правый клик по строке справочника открывал
    // второе меню — от таблицы, из ячейки которой пикер и открыли.
    const isOwnEvent = (e: MouseEvent) => (e.currentTarget as Node).contains(e.target as Node);

    const onHeaderContext = (c: (typeof visibleCols)[number]) => (e: MouseEvent) => {
        if (!isOwnEvent(e)) return;
        ctxMenuRef.current?.openAt(e, buildHeaderMenu(c));
    };

    const onRowContext = (row: DataTableRow<T>, e: MouseEvent) => {
        if (!isOwnEvent(e)) return;
        // правый клик по ссылке — отдаём нативное меню браузера: там «Открыть в
        // новой вкладке/окне», «Копировать адрес» и прочее, что мы не заменим
        if ((e.target as HTMLElement).closest('a[href]')) return;
        const colId = (e.target as HTMLElement).closest('[data-col]')?.getAttribute('data-col');
        const entries: IDataTableMenuEntry[] = [];
        // клик мимо ссылки в строке-документе: дублируем навигацию нативного меню; ссылки не на саму строку
        // (`data-row-link="false"`, как «В журнал» у кода обращения) не в счёт
        const href = (e.currentTarget as HTMLElement).querySelector<HTMLAnchorElement>(
            'a[href]:not([data-row-link="false"])',
        )?.href;
        if (href) {
            entries.push(
                {
                    key: 'open-tab',
                    label: 'Открыть в новой вкладке',
                    onClick: () => {
                        window.open(href, '_blank', 'noopener');
                    },
                },
                {
                    key: 'open-window',
                    label: 'Открыть в новом окне',
                    onClick: () => {
                        window.open(href, '_blank', 'noopener,popup');
                    },
                },
                { key: 'd-open', divider: true },
            );
        }
        if (allowRowPinning) {
            const pinned = row.getIsPinned() === 'top';
            entries.push({
                key: 'row-pin',
                label: pinned ? 'Открепить строку' : 'Закрепить строку',
                onClick: () => row.pin(pinned ? false : 'top'),
            });
        }
        if (colId) {
            const dcol = columns.find((col) => col.field === colId);
            entries.push({
                key: 'copy-cell',
                label: 'Копировать значение',
                onClick: () => {
                    void navigator.clipboard?.writeText(formatCellText(row.getValue(colId), dcol?.dataType));
                },
            });
        }
        const custom = rowContextMenu?.(row.original);
        if (custom?.length) entries.push({ key: 'd-custom', divider: true }, ...custom);
        if (!entries.length) return; // пунктов нет — оставляем нативное меню браузера
        ctxMenuRef.current?.openAt(e, compressMenuDividers(entries));
    };

    // ----- Виртуализация: по внешнему скроллу страницы (как в гриде вложений)
    // или по внутреннему .dtb__scroll при maxHeight -----
    // В режиме подгрузки прокруткой виртуализация включена с первой порции:
    // переход «обычный список → виртуальный» на лету схлопывал высоту контейнера
    // и сбрасывал скролл в начало. Исключение — выборка, которая целиком меньше
    // порога: догружать нечего, значит и переключаться не придётся.
    const serverFitsWithoutVirtual =
        serverInfinite && (server.totalCount ?? Number.POSITIVE_INFINITY) <= VIRTUAL_THRESHOLD;
    const virtual = rows.length > VIRTUAL_THRESHOLD || (serverInfinite && !serverFitsWithoutVirtual);
    // Live-ресайз мимо React допустим, когда он не влияет на то, что считает React
    // (офсеты закреплённых колонок он двигает сам, в переменных --dtb-pin-*). У
    // виртуального списка с переносом текста от ширин зависят высоты строк — без
    // setState виртуализатор не узнаёт о них до отпускания, и между строками
    // появляются провалы
    const hasWrappingCells = visibleCols.some((column) =>
        (column.columnDef.meta?.dataColumn?.wrapMode ?? wrapMode) !== 'nowrap',
    );
    const canFastResize = !hasWrappingCells || !virtual;
    // Внутренний скролл: явный предел высоты либо режим «на всю высоту места»,
    // где таблица делит высоту контейнера — тело скроллится, шапка и пейджер стоят.
    const internalScroll = maxHeight != null || fillHeight || fullscreen;
    // Внешний скролл: шапка прилипает к верху прокручиваемой страницы. Внутри
    // .dtb__scroll это невозможно — горизонтальный overflow делает его
    // скролл-контейнером и по вертикали, и sticky держался бы за него. Поэтому
    // шапка живёт над ним в своём горизонтальном скролле, синхронном со строками.
    const stickyHead = stickyHeader && !internalScroll && !allColumnsHidden;
    // на маунт: тесты подменяют CSS.supports перед рендером
    const [headTimeline] = useState(supportsHeadScrollTimeline);
    const tablewrapRef = useRef<HTMLDivElement>(null);
    const rowsRef = useRef<HTMLUListElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const headStickyRef = useRef<HTMLDivElement>(null);
    const headPlateRef = useRef<HTMLDivElement>(null);
    const headScrollRef = useRef<HTMLDivElement>(null);
    const hbarRef = useRef<HTMLDivElement>(null);
    const theadRef = useRef<HTMLDivElement>(null);
    const [theadH, setTheadH] = useState(0);
    useDragScroll(scrollRef, { ignoreSelector: DRAG_SCROLL_IGNORE_SELECTOR });
    const initialScrollRef = useRef(initialScrollPosition);
    const scrollRestoredRef = useRef(false);
    const prependAdjustingRef = useRef(false);
    const serverStartAligningRef = useRef(false);
    const [scrollReady, setScrollReady] = useState(false);
    const initialReadyReported = useRef(false);
    useLayoutEffect(() => {
        if (!onInitialReady || initialReadyReported.current) return;
        // Ошибка тоже готовое состояние: её нужно открыть, даже если сохранённую
        // позицию восстановить не удалось. Пустой ответ проходит через scrollReady.
        if (error || server.error || (!effectiveLoading && !server.stale && scrollReady)) {
            initialReadyReported.current = true;
            onInitialReady();
        }
    }, [onInitialReady, effectiveLoading, server.stale, server.error, error, scrollReady]);
    const [serverTopRowIndex, setServerTopRowIndex] = useState(() => initialScrollPosition?.rowIndex ?? 0);
    const serverTopRowIndexRef = useRef(serverTopRowIndex);
    const [serverStartActionAvailable, setServerStartActionAvailable] = useState(
        () => (initialScrollPosition?.rowIndex ?? 0) > 0 || !!initialScrollPosition?.rowId,
    );
    const serverStartActionAvailableRef = useRef(serverStartActionAvailable);
    const [jumpingToServerStart, setJumpingToServerStart] = useState(false);
    const [serverStartFeedback, setServerStartFeedback] = useState<ServerStartFeedback>('idle');
    const serverStartConfirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const serverStartExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const serverStartAlignFrameRef = useRef(0);
    if (!initialScrollRef.current && initialScrollPosition) initialScrollRef.current = initialScrollPosition;
    const handledRevealRequestRef = useRef('');
    const [revealedRow, setRevealedRow] = useState<{
        rowId: string;
        requestKey: string;
        focus: boolean;
        field?: string;
    } | null>(null);
    const [filteredRevealRequest, setFilteredRevealRequest] = useState<{ rowId: string; requestKey: string } | null>(
        null,
    );
    const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
    const [scrollMargin, setScrollMargin] = useState(0);

    // Виртуализатор живёт в списке строк (DataTableRows): его состояние меняется на
    // каждый шаг прокрутки, и здесь оно перерисовывало бы всю таблицу. Ссылку
    // держим в ref — её читает sticky-заголовок текущей группы.
    const virtualizerRef = useRef<Virtualizer<HTMLElement, Element> | null>(null);
    const onVirtualizer = useCallback((instance: Virtualizer<HTMLElement, Element>) => {
        virtualizerRef.current = instance;
    }, []);

    // Прокрутка публикуется с задержкой: history.replaceState в браузерах
    // ограничен (~100 вызовов за 30 секунд в Safari/Firefox), писать адрес на
    // каждый кадр прокрутки нельзя. Публикация пересчитывает якорь в момент
    // срабатывания, поэтому отдаёт всегда последнее положение.
    const scrollPublishTimerRef = useRef<number | null>(null);
    const cancelScheduledScrollPublish = useCallback(() => {
        if (scrollPublishTimerRef.current == null) return;
        window.clearTimeout(scrollPublishTimerRef.current);
        scrollPublishTimerRef.current = null;
    }, []);
    useEffect(() => cancelScheduledScrollPublish, [cancelScheduledScrollPublish]);

    // Новая сортировка/фильтр/группировка — новая серверная выборка. Старый
    // локальный scrollTop относится к другому окну и не должен оставлять экран
    // на 15-й строке первой порции или перезаписывать сброшенный URL-якорь.
    const serverScrollSelectionKey = JSON.stringify({
        sorting,
        columnFilters,
        globalFilter,
        grouping,
        pageSize: pagination.pageSize,
        serverResetToken,
    });
    const previousServerScrollSelectionKeyRef = useRef(serverScrollSelectionKey);
    useLayoutEffect(() => {
        if (previousServerScrollSelectionKeyRef.current === serverScrollSelectionKey) return;
        previousServerScrollSelectionKeyRef.current = serverScrollSelectionKey;
        if (!serverInfinite) return;
        // Недовосстановленная позиция относится к прежней выборке — бросаем её,
        // иначе догнавший align утаскивал бы новую выборку на чужую строку.
        if (!scrollRestoredRef.current) {
            scrollRestoredRef.current = true;
            setScrollReady(true);
        }
        cancelScheduledScrollPublish();
        serverTopRowIndexRef.current = 0;
        setServerTopRowIndex(0);
        serverStartActionAvailableRef.current = false;
        setServerStartActionAvailable(false);
        const el = scrollRef.current;
        if (!el) return;
        virtualizerRef.current?.scrollToIndex(0, { align: 'start' });
        el.scrollTop = 0;
        onScrollPositionChange?.({
            top: 0,
            left: Math.max(0, Math.round(el.scrollLeft)),
            ...(serverGroupActive ? {} : { rowIndex: 0, rowOffset: 0 }),
        });
    }, [
        serverScrollSelectionKey,
        serverInfinite,
        serverGroupActive,
        onScrollPositionChange,
        cancelScheduledScrollPublish,
    ]);

    // Показ строки — отдельная операция, а не побочный эффект cell editing:
    // таблицы без addRowFocusColumn тоже обязаны открыть страницу новой строки.
    // Для старых потребителей editCellRequest остаётся fallback-запросом показа.
    const rowRevealRequest = useMemo(
        () =>
            revealRowRequest ??
            (editCellRequest ? { rowId: editCellRequest.rowId, token: editCellRequest.token, focus: false } : null),
        [revealRowRequest, editCellRequest],
    );
    // Модели строк мемоизированы библиотекой: их identity меняется ровно тогда,
    // когда меняются строки (данные, фильтры, сортировка, группировка, раскрытие).
    // Сам объект `table` от useTable в v9 новый на каждый рендер — в зависимости
    // эффектов он не годится.
    const coreRowModel = table.getCoreRowModel();
    const prePaginatedRowModel = table.getPrePaginatedRowModel();
    useEffect(() => {
        if (!rowRevealRequest) {
            setFilteredRevealRequest(null);
            return;
        }
        const requestKey = `${rowRevealRequest.rowId}:${rowRevealRequest.token}`;
        if (handledRevealRequestRef.current === requestKey) return;

        // Запрос может приехать на рендер раньше обновлённого массива RHF.
        // Пока строки нет даже в core model, ждём следующего изменения data.
        const requestedRow = coreRowModel.rowsById[rowRevealRequest.rowId];
        if (!requestedRow) return;
        if (rowRevealRequest.expand && requestedRow.getCanExpand() && !requestedRow.getIsExpanded()) {
            requestedRow.toggleExpanded(true);
            return;
        }

        if (!prePaginatedRowModel.rowsById[rowRevealRequest.rowId]) {
            if (hasActiveFilters) setFilteredRevealRequest({ rowId: rowRevealRequest.rowId, requestKey });
            return;
        }

        const containsRow = (row: DataTableRow<T>): boolean =>
            row.id === rowRevealRequest.rowId || row.subRows.some(containsRow);
        const topIndex = prePaginatedRowModel.rows.findIndex(containsRow);
        if (topIndex < 0) return;

        handledRevealRequestRef.current = requestKey;
        setFilteredRevealRequest(null);
        if (paging) {
            const page = Math.floor(topIndex / Math.max(1, pagination.pageSize));
            if (page !== pagination.pageIndex) setPagination((prev) => ({ ...prev, pageIndex: page }));
        }
        setRevealedRow({
            rowId: rowRevealRequest.rowId,
            requestKey,
            focus: rowRevealRequest.focus !== false,
            field: rowRevealRequest.field,
        });
    }, [
        rowRevealRequest,
        coreRowModel,
        prePaginatedRowModel,
        hasActiveFilters,
        paging,
        pagination.pageIndex,
        pagination.pageSize,
        setPagination,
    ]);

    useLayoutEffect(() => {
        if (!revealedRow) return;
        const rowIndex = rows.findIndex((row) => row.id === revealedRow.rowId);
        if (rowIndex < 0) return;
        if (virtual) virtualizerRef.current?.scrollToIndex(rowIndex, { align: 'center' });

        let secondFrame = 0;
        let timeout = 0;
        const firstFrame = requestAnimationFrame(() => {
            secondFrame = requestAnimationFrame(() => {
                // Срок подсветки отсчитывается от показа, а не от запроса: кадры под нагрузкой
                // и в фоновой вкладке запаздывают, и заведённый раньше них таймер снимал
                // показ вместе с ещё не выполненными прокруткой и фокусом.
                timeout = window.setTimeout(
                    () =>
                        setRevealedRow((current) => (current?.requestKey === revealedRow.requestKey ? null : current)),
                    1800,
                );
                const element = rowsRef.current?.querySelector<HTMLElement>(
                    `[data-row-key="${CSS.escape(revealedRow.rowId)}"]`,
                );
                if (!element) return;
                const scroll = element.closest<HTMLElement>('.dtb__scroll--y');
                if (scroll) {
                    const rowRect = element.getBoundingClientRect();
                    const scrollRect = scroll.getBoundingClientRect();
                    if (rowRect.top < scrollRect.top) scroll.scrollTop -= scrollRect.top - rowRect.top;
                    else if (rowRect.bottom > scrollRect.bottom) scroll.scrollTop += rowRect.bottom - scrollRect.bottom;
                } else {
                    scrollRowIntoView(element);
                }
                const cell = revealedRow.field
                    ? element.querySelector<HTMLElement>(`[data-col="${CSS.escape(revealedRow.field)}"]`)
                    : null;
                if (cell) scrollCellIntoView(cell);
                // открытый редактор важнее: фокус у него забрал бы blur, а blur — это коммит и закрытие
                if (revealedRow.focus && !rowsRef.current?.querySelector('.dtb__cell--editing')) {
                    if (cell && !cell.hasAttribute('tabindex')) cell.tabIndex = -1;
                    (cell ?? element).focus({ preventScroll: true });
                }
            });
        });
        return () => {
            cancelAnimationFrame(firstFrame);
            cancelAnimationFrame(secondFrame);
            clearTimeout(timeout);
        };
    }, [revealedRow, rows, virtual, pagination.pageIndex]);

    useLayoutEffect(() => {
        // Контейнер и смещение нужны до включения виртуализации: раскрытие
        // дерева не должно инициализировать виртуализатор с null и offset=0.
        const parent = internalScroll ? scrollRef.current : findScrollParent(tablewrapRef.current);
        setScrollParent(parent);
        const measure = () => {
            const list = rowsRef.current;
            if (!list || !parent) return;
            const top = list.getBoundingClientRect().top - parent.getBoundingClientRect().top + parent.scrollTop;
            setScrollMargin((prev) => (Math.abs(prev - top) < 1 ? prev : top));
        };
        measure();
        window.addEventListener('resize', measure);
        // Таблица на скрытой вкладке меряется с нулевыми прямоугольниками, а содержимое над ней (блоки карточки)
        // догружается позже: без перемера при показе и сдвигах виртуализатор рисовал не те строки — пустое место
        // в начале списка. При показе меняется размер самой таблицы, при сдвиге над ней — размер её предков.
        const resize = virtual ? new ResizeObserver(measure) : null;
        for (let el: HTMLElement | null = tablewrapRef.current; resize && el && el !== parent; el = el.parentElement) {
            resize.observe(el);
        }
        return () => {
            window.removeEventListener('resize', measure);
            resize?.disconnect();
        };
    }, [virtual, internalScroll, filterRowShown, visibleCols.length, wrapMode, rows.length, topRows.length]);

    // Докрутка к строке после того, как виртуализатор смонтировал её узел: два кадра, с отменой
    // прежнего запроса и при размонтировании.
    const rowScrollFramesRef = useRef<number[]>([]);
    const scrollToRowAfterLayout = useCallback((rowId: string, place: (element: HTMLElement) => void) => {
        rowScrollFramesRef.current.forEach(cancelAnimationFrame);
        rowScrollFramesRef.current = [
            requestAnimationFrame(() => {
                rowScrollFramesRef.current[1] = requestAnimationFrame(() => {
                    const element = rowsRef.current?.querySelector<HTMLElement>(
                        `[data-row-key="${CSS.escape(rowId)}"]`,
                    );
                    if (element) place(element);
                });
            }),
        ];
    }, []);
    useEffect(() => () => rowScrollFramesRef.current.forEach(cancelAnimationFrame), []);

    captureFullscreenAnchorRef.current = (next) => {
        fullscreenAnchorRef.current = null;
        const list = rowsRef.current;
        const viewport = internalScroll ? scrollRef.current : scrollParent;
        if (!next && serverPagedFullscreen) {
            // серверная лента возвращается страницами — открываем ту, где верхняя видимая строка
            const anchor = currentServerScrollAnchor();
            if (!anchor) return;
            const pageIndex = Math.floor(anchor.rowIndex / Math.max(1, pagination.pageSize));
            setPagination((prev) => (prev.pageIndex === pageIndex ? prev : { ...prev, pageIndex }));
            return;
        }
        if (!list || !viewport) return;
        const viewportTop = viewport === document.scrollingElement ? 0 : viewport.getBoundingClientRect().top;
        const cutoff = viewportTop + (internalScroll ? theadH : 0);
        // таблица не прокручена — верхней остаётся первая строка (на входе — первая строка страницы)
        const scrolled = list.getBoundingClientRect().top < cutoff;
        const rowId = scrolled
            ? [...list.querySelectorAll<HTMLElement>('[data-row-key]')].find(
                  (element) => element.getBoundingClientRect().bottom > cutoff + 1,
              )?.dataset.rowKey
            : next && paging && !isServer && pagination.pageIndex > 0
              ? rows[0]?.id
              : undefined;
        if (!rowId) {
            // ленту вернули к началу — и страницы открываются с первой
            if (!next && unpagedFullscreen) resetPageIndex();
            return;
        }
        fullscreenAnchorRef.current = rowId;
        // выход возвращает страницы — открываем ту, где строка-якорь
        if (!next && unpagedFullscreen) {
            const containsRow = (row: DataTableRow<T>): boolean => row.id === rowId || row.subRows.some(containsRow);
            const topIndex = table.getPrePaginatedRowModel().rows.findIndex(containsRow);
            if (topIndex >= 0) {
                const pageIndex = Math.floor(topIndex / Math.max(1, pagination.pageSize));
                setPagination((prev) => (prev.pageIndex === pageIndex ? prev : { ...prev, pageIndex }));
            }
        }
    };
    useLayoutEffect(() => {
        const anchor = fullscreenAnchorRef.current;
        if (!anchor) return;
        // ждём, пока виртуализатор переедет на скролл-контейнер нового режима
        if ((scrollParent === scrollRef.current) !== internalScroll) return;
        fullscreenAnchorRef.current = null;
        const index = rows.findIndex((row) => row.id === anchor);
        if (index < 0) return;
        if (virtual) virtualizerRef.current?.scrollToIndex(index, { align: 'start' });
        scrollToRowAfterLayout(anchor, (element) => {
            const scroll = internalScroll ? scrollRef.current : null;
            if (scroll) {
                const headHeight = theadRef.current?.offsetHeight ?? 0;
                scroll.scrollTop += element.getBoundingClientRect().top - scroll.getBoundingClientRect().top - headHeight;
            } else {
                // в прокрученной внутрь таблице верхняя строка слоя встаёт под прилипшую
                // шапку — туда же, куда сел морф выхода
                scrollRowIntoView(element, stuckTopOf(rootRef.current) != null ? 'start' : 'nearest');
            }
        });
        // stuckTopOf читает только пропсы режима скролла и DOM
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullscreen, scrollParent, internalScroll, rows, virtual, scrollToRowAfterLayout]);

    // ----- Полноэкранный режим: «Найти в таблице» и итоги -----
    const findInputRef = useRef<HTMLInputElement>(null);
    const dataColIdsKey = visibleCols
        .filter((column) => column.columnDef.meta?.dataColumn)
        .map((column) => column.id)
        .join('\u0000');
    const dataColIds = useMemo(() => (dataColIdsKey ? dataColIdsKey.split('\u0000') : []), [dataColIdsKey]);
    const findRows = useMemo(
        () =>
            fullscreen
                ? rows.map((row) => ({
                      id: row.id,
                      text: row.getIsGrouped()
                          ? undefined
                          : (colId: string) =>
                                cellTextOf(table.getColumn(colId)?.columnDef.meta?.dataColumn, row, colId),
                  }))
                : [],
        // table — не зависимость: методы замкнуты на одно ядро таблицы
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fullscreen, rows, cellTextOf],
    );
    const revealFindMatch = useCallback(
        (match: IDataTableFindMatch) => {
            if (virtual) virtualizerRef.current?.scrollToIndex(match.rowIndex, { align: 'center' });
            scrollToRowAfterLayout(match.rowId, (element) => {
                const scroll = scrollRef.current;
                if (scroll && !virtual) {
                    const rowRect = element.getBoundingClientRect();
                    const scrollRect = scroll.getBoundingClientRect();
                    const top = scrollRect.top + (theadRef.current?.offsetHeight ?? 0);
                    if (rowRect.top < top) scroll.scrollTop -= top - rowRect.top;
                    else if (rowRect.bottom > scrollRect.bottom) scroll.scrollTop += rowRect.bottom - scrollRect.bottom;
                }
                const cell = element.querySelector<HTMLElement>(`[data-col="${CSS.escape(match.colId)}"]`);
                if (cell) scrollCellIntoView(cell);
            });
        },
        [virtual, scrollToRowAfterLayout],
    );
    // запрос «Найти» живёт в поле (DataTableFind); здесь — чтобы вернуть его при следующем входе в режим
    const findQueryRef = useRef('');
    const fullscreenTotals = useMemo(() => {
        // у серверного источника на клиенте лежит только загруженное окно: сумма по нему выдавала бы
        // часть за целое, а счёт строк уже ведёт футер «Загружено X из Y»
        if (!fullscreen || isServer) return null;
        // у дерева в итоги идут листья: flatRows несёт и родителей, и детей
        const leaves = (model: { flatRows: DataTableRow<T>[] }) => model.flatRows.filter((row) => !row.subRows.length);
        const selected = leaves(table.getFilteredSelectedRowModel());
        const scope = selected.length ? selected : leaves(table.getFilteredRowModel());
        const totals: IDataTableTotal[] = [];
        const dataColumnOf = (colId: string) => table.getColumn(colId)?.columnDef.meta?.dataColumn;
        // конфигурация назвала итоговые колонки — верим ей; иначе складываем деньги (сюда
        // попадут и «цены», отличить их от «сумм» по типу нельзя)
        // итог названной колонки показываем, даже когда её скрыл набор колонок: число от этого не хуже
        const allColIds = table.getAllLeafColumns().map((column) => column.id);
        const configured = allColIds.some((colId) => dataColumnOf(colId)?.total);
        for (const colId of configured ? allColIds : dataColIds) {
            const col = dataColumnOf(colId);
            const aggregate = configured ? col?.total : col?.dataType === ValueType.Money ? 'sum' : undefined;
            if (!col || !aggregate || aggregate === 'none') continue;
            // итог без единого числа показываем нулём, а не прочерком
            const value = aggregateValues(aggregate, scope.map((row) => row.getValue(colId))) ?? 0;
            totals.push({
                colId,
                label: aggregate === 'sum' ? col.header : `${col.header} · ${DATA_TABLE_AGGREGATE_LABEL[aggregate]}`,
                text: formatAggregate(value, aggregate, col.dataType),
            });
            if (totals.length === 4) break;
        }
        return { rowCount: scope.length, selectedCount: selected.length, totals };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullscreen, isServer, rows, rowSelection, dataColIds]);

    // Scroll-aware тень границы закреплённых групп (как border-and-shadow у MUI):
    // атрибуты ставим прямо на DOM — без state, чтобы скролл не дёргал рендер
    // чтение scrollWidth/clientWidth — layout: на каждый тик скролла это давало
    // синхронный reflow, поэтому меряем не чаще кадра
    const scrollEdgesFrame = useRef(0);
    const updateScrollEdges = useCallback(() => {
        if (scrollEdgesFrame.current) return;
        scrollEdgesFrame.current = requestAnimationFrame(() => {
            scrollEdgesFrame.current = 0;
            const el = scrollRef.current;
            if (!el) return;
            const left = el.scrollLeft > 1;
            const right = el.scrollLeft < el.scrollWidth - el.clientWidth - 1;
            for (const target of [el, headScrollRef.current]) {
                target?.toggleAttribute('data-scrolled-left', left);
                target?.toggleAttribute('data-scrolled-right', right);
            }
        });
    }, []);

    // Прилипшая шапка и липкая полоса прокрутки листаются вместе со строками.
    // Синхронизация в любую сторону: шапку двигают колесо, автопрокрутка при
    // переносе колонки и фокус фильтра, полосу — пользователь.
    // Записанный скролл отзывается событием только в следующем кадре, когда
    // источник мог уже уехать дальше; без гашения этого эха шапка возвращала
    // строки на позицию кадр назад и горизонтальная прокрутка дёргалась.
    const scrollEchoRef = useRef(new Map<HTMLElement, number>());
    const syncScrollLeft = useCallback((from: HTMLElement | null) => {
        if (!from) return;
        const echoes = scrollEchoRef.current;
        const echo = echoes.get(from);
        echoes.delete(from);
        if (echo !== undefined && Math.abs(from.scrollLeft - echo) < 1) return;
        const head = headTimeline ? null : headScrollRef.current;
        for (const to of [scrollRef.current, head, hbarRef.current]) {
            if (!to || to === from || Math.abs(to.scrollLeft - from.scrollLeft) < 1) continue;
            to.scrollLeft = from.scrollLeft;
            echoes.set(to, to.scrollLeft);
        }
    }, [headTimeline]);
    const handleHeadScroll = useCallback(() => {
        const head = headScrollRef.current;
        const body = scrollRef.current;
        if (!headTimeline) {
            syncScrollLeft(head);
            return;
        }
        // шапка со scroll-timeline сама не листается: сдвиг (фокус фильтра в
        // невидимой колонке) переносим на строки, за ними едет и шапка
        if (!head || !body || !head.scrollLeft) return;
        body.scrollLeft += head.scrollLeft;
        head.scrollLeft = 0;
    }, [headTimeline, syncScrollLeft]);
    const handleHBarScroll = useCallback(() => syncScrollLeft(hbarRef.current), [syncScrollLeft]);
    // Полоса видна, только если строкам есть куда листаться
    const updateHBarOverflow = useCallback(() => {
        const el = scrollRef.current;
        const hbar = hbarRef.current;
        if (!el || !hbar) return;
        const overflow = el.scrollWidth > el.clientWidth + 1;
        hbar.toggleAttribute('data-overflow', overflow);
        // ход scroll-timeline шапки в пикселях: прогресс 0..1 ↔ сдвиг 0..max
        tablewrapRef.current?.style.setProperty('--dtb-x-max', `${Math.max(0, el.scrollWidth - el.clientWidth)}px`);
        // скрытая полоса теряет позицию, а заново смонтированная шапка (выход из
        // полноэкранного) её не знает — догоняют уже прокрученные строки
        if (overflow) {
            scrollEchoRef.current.delete(el);
            syncScrollLeft(el);
        }
    }, [syncScrollLeft]);

    /** Абсолютный якорь верхней видимой строки серверного окна. В отличие от
     * scrollTop он не зависит от того, сколько предыдущих страниц сейчас лежит
     * в DOM, поэтому годится и для URL, и для компенсации prepend. */
    const currentServerScrollAnchor = useCallback(() => {
        const el = scrollRef.current;
        const list = rowsRef.current;
        if (
            !el ||
            !list ||
            !serverInfinite ||
            serverGroupActive ||
            server.windowStartIndex == null ||
            rows.length === 0
        )
            return undefined;

        const cutoff = el.getBoundingClientRect().top + (internalScroll ? theadH : 0);
        let localIndex = 0;
        let rowOffset = 0;
        if (virtual) {
            const listTop = list.getBoundingClientRect().top;
            const item = virtualizerRef.current
                ?.getVirtualItems()
                .find((candidate) => listTop + candidate.start - scrollMargin + candidate.size > cutoff + 1);
            if (item) {
                localIndex = item.index;
                rowOffset = Math.max(0, Math.round(cutoff - (listTop + item.start - scrollMargin)));
            } else return undefined;
        } else {
            const items = Array.from(list.children) as HTMLElement[];
            const index = items.findIndex((item) => item.getBoundingClientRect().bottom > cutoff + 1);
            if (index >= 0) {
                localIndex = index;
                rowOffset = Math.max(0, Math.round(cutoff - items[index].getBoundingClientRect().top));
            }
        }
        return {
            rowIndex: server.windowStartIndex + localIndex,
            rowOffset,
            rowId: rows[localIndex]?.id,
        };
    }, [
        serverInfinite,
        serverGroupActive,
        server.windowStartIndex,
        rows,
        internalScroll,
        theadH,
        virtual,
        scrollMargin,
    ]);

    const publishScrollPosition = useCallback(() => {
        const el = scrollRef.current;
        if (!el || !scrollRestoredRef.current || prependAdjustingRef.current || serverStartAligningRef.current) return;
        // Пока строки принадлежат прежней выборке (ответ на новую сортировку или
        // фильтр ещё не пришёл), их якорь — мусор: он затирал бы сброшенный ноль
        // позицией из чужого порядка строк.
        if (serverInfinite && !serverGroupActive && server.stale) return;
        const anchor = currentServerScrollAnchor();
        if (serverInfinite && !serverGroupActive && !anchor) return;
        if (anchor?.rowIndex === 0 && serverStartActionAvailableRef.current) {
            serverStartActionAvailableRef.current = false;
            setServerStartActionAvailable(false);
        }
        if (anchor && serverStartActionAvailableRef.current && anchor.rowIndex !== serverTopRowIndexRef.current) {
            serverTopRowIndexRef.current = anchor.rowIndex;
            setServerTopRowIndex(anchor.rowIndex);
        }
        onScrollPositionChange?.({
            top: Math.max(0, Math.round(el.scrollTop)),
            left: Math.max(0, Math.round(el.scrollLeft)),
            ...(anchor ? { rowIndex: anchor.rowIndex, rowOffset: anchor.rowOffset } : {}),
            ...(anchor?.rowId ? { rowId: anchor.rowId } : {}),
        });
    }, [currentServerScrollAnchor, onScrollPositionChange, serverGroupActive, serverInfinite, server.stale]);
    const publishScrollPositionRef = useRef(publishScrollPosition);
    publishScrollPositionRef.current = publishScrollPosition;

    const handleScroll = useCallback(() => {
        syncScrollLeft(scrollRef.current);
        updateScrollEdges();
        if (!onScrollPositionChange || scrollPublishTimerRef.current != null) return;
        scrollPublishTimerRef.current = window.setTimeout(() => {
            scrollPublishTimerRef.current = null;
            publishScrollPositionRef.current();
        }, SCROLL_POSITION_PUBLISH_MS);
    }, [onScrollPositionChange, syncScrollLeft, updateScrollEdges]);

    const clearServerStartFeedbackTimers = useCallback(() => {
        if (serverStartConfirmTimerRef.current) clearTimeout(serverStartConfirmTimerRef.current);
        if (serverStartExitTimerRef.current) clearTimeout(serverStartExitTimerRef.current);
        serverStartConfirmTimerRef.current = null;
        serverStartExitTimerRef.current = null;
    }, []);
    const showServerStartConfirmation = useCallback(() => {
        clearServerStartFeedbackTimers();
        setServerStartFeedback('confirmed');
        serverStartConfirmTimerRef.current = setTimeout(() => {
            setServerStartFeedback('exiting');
            serverStartExitTimerRef.current = setTimeout(() => {
                serverStartActionAvailableRef.current = false;
                setServerStartActionAvailable(false);
                setServerStartFeedback('idle');
            }, SERVER_START_EXIT_MS);
        }, serverScrollToStartConfirmationMs);
    }, [clearServerStartFeedbackTimers, serverScrollToStartConfirmationMs]);
    useEffect(
        () => () => {
            clearServerStartFeedbackTimers();
            if (serverStartAlignFrameRef.current) cancelAnimationFrame(serverStartAlignFrameRef.current);
            serverStartAligningRef.current = false;
        },
        [clearServerStartFeedbackTimers],
    );

    /** Завершить переход только после того, как первая серверная порция уже
     *  заменила восстановленное окно. Так пользователь не видит локальный верх
     *  прежней середины списка во время запроса. */
    const finishServerJumpToStart = useCallback(() => {
        const el = scrollRef.current;
        if (serverStartAlignFrameRef.current) cancelAnimationFrame(serverStartAlignFrameRef.current);
        cancelScheduledScrollPublish();
        serverStartAligningRef.current = true;

        let attempts = 0;
        let stableFrames = 0;
        const complete = () => {
            serverStartAlignFrameRef.current = 0;
            serverStartAligningRef.current = false;
            serverTopRowIndexRef.current = 0;
            setServerTopRowIndex(0);
            if (el) {
                virtualizerRef.current?.scrollToIndex(0, { align: 'start' });
                el.scrollTop = 0;
                cancelScheduledScrollPublish();
                onScrollPositionChange?.({
                    top: 0,
                    left: Math.max(0, Math.round(el.scrollLeft)),
                    rowIndex: 0,
                    rowOffset: 0,
                    ...(rows[0]?.id ? { rowId: rows[0].id } : {}),
                });
            }
            setJumpingToServerStart(false);
            showServerStartConfirmation();
        };

        if (!el) {
            complete();
            return;
        }

        // После замены окна TanStack Virtual ещё несколько кадров уточняет
        // высоты строк и может вернуть прежний scrollTop. Удерживаем ноль до
        // стабилизации, иначе URL уже сообщает «начало», а экран остаётся ниже.
        const align = () => {
            attempts += 1;
            stableFrames = Math.abs(el.scrollTop) < 1 ? stableFrames + 1 : 0;
            virtualizerRef.current?.scrollToIndex(0, { align: 'start' });
            el.scrollTop = 0;
            if (attempts < 12 && stableFrames < 3) {
                serverStartAlignFrameRef.current = requestAnimationFrame(align);
                return;
            }
            complete();
        };
        align();
    }, [cancelScheduledScrollPublish, onScrollPositionChange, rows, showServerStartConfirmation]);

    const jumpServerWindowToStart = server.jumpToStart;
    const serverWindowStartIndex = server.windowStartIndex ?? 0;
    const requestServerJumpToStart = useCallback(() => {
        if (jumpingToServerStart) return;
        clearServerStartFeedbackTimers();
        setServerStartFeedback('idle');
        // Первая порция уже в накопленном окне — сервер не нужен, достаточно
        // локально поставить абсолютную строку 0 к верхней границе.
        if (serverWindowStartIndex === 0) {
            finishServerJumpToStart();
            return;
        }
        if (!jumpServerWindowToStart) return;
        setJumpingToServerStart(true);
        jumpServerWindowToStart();
    }, [
        clearServerStartFeedbackTimers,
        finishServerJumpToStart,
        jumpingToServerStart,
        jumpServerWindowToStart,
        serverWindowStartIndex,
    ]);

    useEffect(() => {
        if (!jumpingToServerStart || server.loading) return;
        if (server.error) {
            setJumpingToServerStart(false);
            return;
        }
        if ((server.windowStartIndex ?? 0) !== 0) return;
        const frame = requestAnimationFrame(finishServerJumpToStart);
        return () => cancelAnimationFrame(frame);
    }, [finishServerJumpToStart, jumpingToServerStart, server.error, server.loading, server.windowStartIndex]);

    useEffect(() => {
        if (scrollRestoredRef.current) return;
        const position = initialScrollRef.current;
        // Пустая устоявшаяся выборка (папка опустела) — восстанавливать нечего:
        // завершаем восстановление, иначе публикация позиции молчала бы вечно.
        const settledEmpty = isServer && !server.loading && !server.error && server.totalCount === 0 && !server.stale;
        if (
            effectiveLoading ||
            (position && (position.top > 0 || position.rowIndex != null) && rows.length === 0 && !settledEmpty)
        )
            return;

        let alignFrame = 0;
        // После paint: именно здесь scrollHeight уже включает пришедшие строки.
        // На layout-фазе первая пустая раскладка успевала зафиксировать ноль.
        const frame = requestAnimationFrame(() => {
            // Смена выборки во время восстановления бросает его (см. эффект сброса)
            if (scrollRestoredRef.current) return;
            const el = scrollRef.current;
            if (!el) return;
            // Без сохранённой позиции восстанавливать нечего. Не записываем нули:
            // потребитель уже мог прокрутить контейнер до выполнения этого эффекта.
            if (!position) {
                scrollRestoredRef.current = true;
                setScrollReady(true);
                return;
            }
            el.scrollLeft = Math.min(position.left, Math.max(0, el.scrollWidth - el.clientWidth));

            const absoluteRow = position.rowIndex;
            const canRestoreServerAnchor =
                absoluteRow != null && serverInfinite && !serverGroupActive && server.windowStartIndex != null;
            if (!canRestoreServerAnchor) {
                el.scrollTop = Math.min(position.top, Math.max(0, el.scrollHeight - el.clientHeight));
                scrollRestoredRef.current = true;
                setScrollReady(true);
                return;
            }

            const rowIdIndex = position.rowId ? rows.findIndex((row) => row.id === position.rowId) : -1;
            const localIndex =
                rowIdIndex >= 0
                    ? rowIdIndex
                    : Math.max(0, Math.min(rows.length - 1, absoluteRow - server.windowStartIndex!));
            const rowId = rows[localIndex]?.id;
            if (virtual) virtualizerRef.current?.scrollToIndex(localIndex, { align: 'start' });
            let attempts = 0;
            let stableFrames = 0;
            const align = () => {
                if (scrollRestoredRef.current) return;
                attempts += 1;
                const row = rowId
                    ? rowsRef.current?.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(rowId)}"]`)
                    : undefined;
                if (row) {
                    const cutoff = el.getBoundingClientRect().top + (internalScroll ? theadH : 0);
                    const delta = row.getBoundingClientRect().top - cutoff + (position.rowOffset ?? 0);
                    if (Math.abs(delta) > 0.75) {
                        el.scrollTop += delta;
                        stableFrames = 0;
                    } else stableFrames += 1;
                }
                // Виртуализатор уточняет оценки высот уже после первой раскладки.
                // Несколько кадров держим тот же якорь, иначе после измерения
                // строк над ним позиция уплывала на десятки пикселей.
                if (attempts < 12 && (!row || stableFrames < 3)) {
                    alignFrame = requestAnimationFrame(align);
                    return;
                }
                scrollRestoredRef.current = true;
                setScrollReady(true);
            };
            alignFrame = requestAnimationFrame(align);
        });
        return () => {
            cancelAnimationFrame(frame);
            cancelAnimationFrame(alignFrame);
        };
    }, [
        effectiveLoading,
        rows,
        pagination.pageIndex,
        visibleCols.length,
        initialScrollPosition,
        isServer,
        serverInfinite,
        serverGroupActive,
        server.windowStartIndex,
        server.loading,
        server.error,
        server.totalCount,
        server.stale,
        virtual,
        internalScroll,
        theadH,
    ]);
    useLayoutEffect(() => {
        updateScrollEdges();
        window.addEventListener('resize', updateScrollEdges);
        return () => window.removeEventListener('resize', updateScrollEdges);
    }, [updateScrollEdges, visibleCols.length, columnSizing, rows.length]);

    // ----- Бесконечная подгрузка: сентинелы с обеих сторон окна -----
    const sentinelRef = useRef<HTMLDivElement>(null);
    const previousSentinelRef = useRef<HTMLDivElement>(null);
    const onReachEndRef = useRef(onReachEnd);
    onReachEndRef.current = serverInfinite ? (server.loadMore ?? undefined) : onReachEnd;
    const hasReachEnd = !!onReachEnd || serverInfinite;
    useEffect(() => {
        const waitingForInitialAnchor = initialScrollRef.current?.rowIndex != null && !scrollReady;
        if (
            !hasReachEnd ||
            waitingForInitialAnchor ||
            jumpingToServerStart ||
            typeof IntersectionObserver === 'undefined'
        )
            return;
        const el = sentinelRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) onReachEndRef.current?.();
            },
            // при внутреннем скролле сентинел клиппится зоной таблицы, а не окном —
            // с root=viewport он бы никогда не «показался»
            { root: internalScroll ? scrollRef.current : null, rootMargin: '200px' },
        );
        io.observe(el);
        return () => io.disconnect();
        // Переподписка после каждой порции: наблюдатель отдаёт текущее пересечение
        // сразу, поэтому подгрузка продолжается, пока сентинел остаётся на экране.
    }, [
        hasReachEnd,
        scrollReady,
        internalScroll,
        server.rows.length,
        server.loading,
        jumpingToServerStart,
        allColumnsHidden,
    ]);

    const prependAnchorRef = useRef<ReturnType<typeof currentServerScrollAnchor>>(undefined);
    const previousWindowStartRef = useRef(server.windowStartIndex ?? 0);
    const loadPrevious = server.loadPrevious;
    const requestPrevious = useCallback(() => {
        if (!loadPrevious) return;
        prependAnchorRef.current = currentServerScrollAnchor();
        loadPrevious();
    }, [loadPrevious, currentServerScrollAnchor]);
    const requestPreviousRef = useRef(requestPrevious);
    requestPreviousRef.current = requestPrevious;
    useEffect(() => {
        if (
            !serverInfinite ||
            !scrollReady ||
            !loadPrevious ||
            jumpingToServerStart ||
            typeof IntersectionObserver === 'undefined'
        )
            return;
        const el = previousSentinelRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) requestPreviousRef.current();
            },
            { root: internalScroll ? scrollRef.current : null, rootMargin: '200px' },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [
        serverInfinite,
        scrollReady,
        loadPrevious,
        server.rows.length,
        server.loading,
        internalScroll,
        jumpingToServerStart,
        allColumnsHidden,
    ]);

    // После prepend индексы всех прежних строк сдвинулись. Возвращаем в ту же
    // строку и тот же пиксель внутри неё — динамическая высота строк не важна.
    useLayoutEffect(() => {
        const start = server.windowStartIndex ?? 0;
        const previousStart = previousWindowStartRef.current;
        previousWindowStartRef.current = start;
        const anchor = prependAnchorRef.current;
        if (!anchor || start >= previousStart || server.loading) return;
        prependAnchorRef.current = undefined;
        prependAdjustingRef.current = true;
        const localIndex = Math.max(0, anchor.rowIndex - start);
        if (virtual) virtualizerRef.current?.scrollToIndex(localIndex, { align: 'start' });
        let alignFrame = 0;
        const frame = requestAnimationFrame(() => {
            let attempts = 0;
            let stableFrames = 0;
            const align = () => {
                attempts += 1;
                const el = scrollRef.current;
                const row = anchor.rowId
                    ? rowsRef.current?.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(anchor.rowId)}"]`)
                    : undefined;
                if (!el || !row) {
                    if (attempts < 12) {
                        alignFrame = requestAnimationFrame(align);
                        return;
                    }
                    prependAdjustingRef.current = false;
                    return;
                }
                const cutoff = el.getBoundingClientRect().top + (internalScroll ? theadH : 0);
                const delta = row.getBoundingClientRect().top - cutoff + anchor.rowOffset;
                if (Math.abs(delta) > 0.75) {
                    el.scrollTop += delta;
                    stableFrames = 0;
                } else stableFrames += 1;
                if (attempts < 12 && stableFrames < 3) {
                    alignFrame = requestAnimationFrame(align);
                    return;
                }
                prependAdjustingRef.current = false;
                // Пока шла компенсация, прокрутка могла запланировать публикацию
                // по промежуточному положению — итоговое пишем сами и сразу.
                cancelScheduledScrollPublish();
                onScrollPositionChange?.({
                    top: Math.max(0, Math.round(el.scrollTop)),
                    left: Math.max(0, Math.round(el.scrollLeft)),
                    rowIndex: anchor.rowIndex,
                    rowOffset: anchor.rowOffset,
                    ...(anchor.rowId ? { rowId: anchor.rowId } : {}),
                });
            };
            alignFrame = requestAnimationFrame(align);
        });
        return () => {
            cancelAnimationFrame(frame);
            cancelAnimationFrame(alignFrame);
            prependAdjustingRef.current = false;
        };
    }, [
        server.windowStartIndex,
        server.loading,
        rows,
        virtual,
        internalScroll,
        theadH,
        onScrollPositionChange,
        cancelScheduledScrollPublish,
    ]);

    // ----- Внутренний вертикальный скролл (maxHeight): шапка и sticky-группа прилипают -----
    useLayoutEffect(() => {
        const wrap = tablewrapRef.current;
        const scroll = scrollRef.current;
        const head = theadRef.current?.querySelector<HTMLElement>('.dtb__head');
        // строка групп колонок стоит над заголовками — линия под шапкой ниже на её высоту
        const groups = theadRef.current?.querySelector<HTMLElement>('.dtb__colgroups');
        if (!wrap || !scroll || !head) return;

        const updateRulePosition = () => {
            // Высота шапки дробная (переносимые заголовки), а offset-метрики целые:
            // линия в полях расходилась с нижней границей шапки ступенькой в 1px.
            // Меряем от контейнера линии дробно; масштаб (модалки открываются через
            // scale-анимацию) снимаем отношением ширин.
            // у прилипающей шапки линия живёт в её подложке и едет вместе с ней
            const ruleBox = headPlateRef.current ?? wrap;
            const boxRect = ruleBox.getBoundingClientRect();
            const scale = ruleBox.offsetWidth > 0 && boxRect.width > 0 ? boxRect.width / ruleBox.offsetWidth : 1;
            const y = (head.getBoundingClientRect().bottom - boxRect.top) / scale;
            wrap.style.setProperty('--dtb-head-rule-y', `${y}px`);
        };
        updateRulePosition();

        const observer = new ResizeObserver(updateRulePosition);
        observer.observe(head);
        if (groups) observer.observe(groups);
        return () => {
            observer.disconnect();
            wrap.style.removeProperty('--dtb-head-rule-y');
        };
    }, [internalScroll, allColumnsHidden, showColumnGroups, stickyHead]);
    useLayoutEffect(() => {
        if (!internalScroll) return;
        setTheadH(theadRef.current?.offsetHeight ?? 0);
    }, [internalScroll, filterRowShown, visibleCols.length, wrapMode, columnSizing, showColumnGroups]);
    // overscroll-behavior: contain у области без переполнения (пустая или короткая таблица) Chrome всё
    // равно не отдаёт колесо родителю — карточка с такой таблицей переставала прокручиваться под курсором.
    // Поэтому contain включается только при переполнении; признак — атрибут, React его не перезаписывает.
    useLayoutEffect(() => {
        const scroll = scrollRef.current;
        if (!internalScroll || !scroll) return;
        const update = () => scroll.toggleAttribute('data-overflow-y', scroll.scrollHeight > scroll.clientHeight + 1);
        const resize = new ResizeObserver(update);
        const observeChildren = () => {
            resize.disconnect();
            resize.observe(scroll);
            for (const child of scroll.children) resize.observe(child);
            update();
        };
        observeChildren();
        const mutations = new MutationObserver(observeChildren);
        mutations.observe(scroll, { childList: true });
        return () => {
            resize.disconnect();
            mutations.disconnect();
            scroll.removeAttribute('data-overflow-y');
        };
    }, [internalScroll]);

    // ----- Sticky-заголовок текущей верхней группы -----
    // Строка группы уезжает за шапку (внутренний скролл) или прилипший туллбар
    // (внешний) → показываем её копию (groupRender с ctx.sticky). Отслеживаем
    // верхнюю видимую строку по скроллу.
    const barRef = useRef<HTMLDivElement>(null);
    // id группы — в атоме: смену при прокрутке рисует только копия (DataTableTopGroup), а не таблица
    const topGroupAtom = useStateAtom<string | null>(() => null);
    const trackTopGroup = stickyGroupHeader && hasGrouping;
    const stickyBar = (stickyGroupHeader || stickyHeader) && !internalScroll;
    useEffect(() => {
        if (!trackTopGroup) {
            topGroupAtom.set(null);
            return;
        }
        const parent = internalScroll ? scrollRef.current : findScrollParent(tablewrapRef.current);
        if (!parent) return;
        const isDoc = parent === document.scrollingElement || parent === document.documentElement;
        const target: EventTarget = isDoc ? window : parent;
        let raf = 0;
        const compute = () => {
            raf = 0;
            const list = rowsRef.current;
            if (!list) return;
            const stickyH = internalScroll
                ? theadH
                : (barRef.current?.offsetHeight ?? 0) + (headStickyRef.current?.offsetHeight ?? 0);
            const cutoff = (isDoc ? 0 : parent.getBoundingClientRect().top) + stickyH;
            // индекс верхней строки, чей низ ещё не уехал за туллбар
            let idx = -1;
            if (virtual) {
                const listTop = list.getBoundingClientRect().top;
                for (const vi of virtualizerRef.current?.getVirtualItems() ?? []) {
                    if (listTop + vi.start - scrollMargin + vi.size > cutoff + 1) {
                        idx = vi.index;
                        break;
                    }
                }
            } else {
                const items = list.children;
                for (let i = 0; i < items.length; i++) {
                    if (items[i].getBoundingClientRect().bottom > cutoff + 1) {
                        idx = i;
                        break;
                    }
                }
            }
            // верхняя строка — данные, а не группа → её группа уехала, показываем копию
            let next: string | null = null;
            if (idx > 0 && rows[idx] && !rows[idx].getIsGrouped()) {
                for (let i = idx - 1; i >= 0; i--) {
                    if (rows[i].getIsGrouped()) {
                        next = rows[i].id;
                        break;
                    }
                }
            }
            topGroupAtom.set(next);
        };
        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(compute);
        };
        compute();
        target.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            cancelAnimationFrame(raf);
            target.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [trackTopGroup, virtual, rows, scrollMargin, internalScroll, theadH]); // eslint-disable-line react-hooks/exhaustive-deps

    // ----- Cell editing: атом владеет режимом/фокусом/коммитом, значением — потребитель -----
    const canEdit = !!onCellCommit;
    const [editingCell, setEditingCell] = useState<{ rowId: string; colId: string } | null>(null);
    // гасит blur-коммит редактора, когда закрытие уже произошло по Enter/Esc/Tab
    const editorDoneRef = useRef(false);
    const pendingFocusRef = useRef<{ rowId: string; colId: string } | null>(null);

    const isColEditable = (col: IDataTableColumn<T> | undefined, row: DataTableRow<T>): boolean => {
        if (!canEdit || !col?.editable) return false;
        return typeof col.editable === 'function' ? col.editable(row.original) : true;
    };

    // после закрытия редактора фокус возвращается на ячейку — Tab-навигация не теряется.
    // preventScroll обязателен: ячейка могла уехать далеко за экран (её таблица —
    // одна из нескольких на карточке), и браузер подтянул бы к ней всю страницу.
    // Докручивается только собственный скролл таблицы.
    useLayoutEffect(() => {
        if (editingCell || !pendingFocusRef.current) return;
        const { rowId, colId } = pendingFocusRef.current;
        pendingFocusRef.current = null;
        // Фокус уже ушёл дальше сам: Tab из кастомного редактора, коммитящего по blur.
        // Возврат на ячейку отменил бы переход — Tab «застревал» на правленой ячейке.
        const active = document.activeElement;
        if (active && active !== document.body && active !== document.documentElement) return;
        const cell = rowsRef.current?.querySelector<HTMLElement>(
            `[data-row="${CSS.escape(rowId)}"] [data-col="${CSS.escape(colId)}"]`,
        );
        if (!cell) return;
        // правка могла сделать ячейку нередактируемой — фокус всё равно остаётся на ней
        if (!cell.hasAttribute('tabindex')) cell.tabIndex = -1;
        cell.focus({ preventScroll: true });
        const scroll = internalScroll ? scrollRef.current : null;
        if (scroll) {
            const cellRect = cell.getBoundingClientRect();
            const scrollRect = scroll.getBoundingClientRect();
            const top = scrollRect.top + theadH;
            if (cellRect.top < top) scroll.scrollTop -= top - cellRect.top;
            else if (cellRect.bottom > scrollRect.bottom) scroll.scrollTop += cellRect.bottom - scrollRect.bottom;
        }
        scrollCellIntoView(cell);
    }, [editingCell, internalScroll, theadH]);

    const startEdit = (rowId: string, colId: string) => {
        editorDoneRef.current = false;
        setEditingCell({ rowId, colId });
    };
    // программное открытие редактора (автофокус после добавления строки)
    useEffect(() => {
        if (!editCellRequest) return;
        startEdit(editCellRequest.rowId, editCellRequest.field);
        // token в запросе повторяет автофокус для одинаковых rowId/field
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editCellRequest]);
    const closeEditor = (returnFocus: boolean) => {
        editorDoneRef.current = true;
        if (returnFocus && editingCell) pendingFocusRef.current = editingCell;
        setEditingCell(null);
    };
    const commitCell = (row: DataTableRow<T>, colId: string, value: unknown, returnFocus: boolean) => {
        onCellCommit?.(row.original, colId, value);
        closeEditor(returnFocus);
    };

    // Клик мимо закрывает редактор. Без этого редактор с собственным плавающим
    // списком (справочник в режиме списка, Select) оставался в ячейке после
    // того, как список закрылся сам: ячейка так и стояла «раскрытой».
    // Порядок важен: сначала снимаем фокус — редакторы, коммитящие по blur
    // (текст, число, дата), успевают записать значение, — и только потом
    // закрываем то, что осталось открытым. Клик в плавающий слой редактора
    // (портальный дропдаун, календарь, модалка справочника) — не «мимо». Слой, в
    // котором лежит сама таблица (модалка с таблицей), редактору не принадлежит.
    useEffect(() => {
        if (!editingCell) return undefined;
        const onPointerDownOutside = (e: PointerEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target?.isConnected) return;
            const cell = rowsRef.current?.querySelector<HTMLElement>('.dtb__cell--editing');
            if (cell?.contains(target)) return;
            // Слой — ближайший `.rf-portal-overlay` или корень портала: прямой потомок
            // узла-портала, а не сам узел. Mantine 9 кладёт все порталы в один общий
            // [data-portal] (reuseTargetNode), и в модалке он содержит и таблицу, и
            // список редактора — клик по варианту закрывал ячейку до выбора. Хост
            // порталов области (portalTarget) — такой же узел, только без атрибута.
            let layer: HTMLElement | null = null;
            for (let node: HTMLElement | null = target; node && !layer; node = node.parentElement) {
                const host = node.parentElement;
                if (
                    node.classList.contains(portalOverlayClassName) ||
                    (host && (host.hasAttribute('data-portal') || host === portalTarget))
                )
                    layer = node;
            }
            if (layer && !layer.contains(rootRef.current)) return;
            const active = document.activeElement as HTMLElement | null;
            if (active && cell?.contains(active)) active.blur();
            if (!editorDoneRef.current) closeEditor(false);
            // Фокус пользователь уже отдал сам — тому, по чему кликнул. Возврат на
            // ячейку увёл бы его обратно: редактор таблицы формы коммитит по blur,
            // а коммит просит вернуть фокус (нужный после Enter, но не после клика).
            pendingFocusRef.current = null;
        };
        document.addEventListener('pointerdown', onPointerDownOutside, true);
        return () => document.removeEventListener('pointerdown', onPointerDownOutside, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingCell, portalTarget]);

    /** Следующая редактируемая ячейка (Tab): по колонкам строки, затем по строкам. */
    const nextEditableCell = (rowId: string, colId: string, dir: 1 | -1) => {
        const editableCols = (r: DataTableRow<T>) =>
            r.getIsGrouped() ? [] : visibleCols.filter((c) => isColEditable(c.columnDef.meta?.dataColumn, r));
        let r = rows.findIndex((x) => x.id === rowId);
        if (r < 0) return null;
        let cols = editableCols(rows[r]);
        let i = cols.findIndex((c) => c.id === colId) + dir;
        for (;;) {
            if (i >= 0 && i < cols.length) return { rowId: rows[r].id, colId: cols[i].id };
            do {
                r += dir;
            } while (r >= 0 && r < rows.length && editableCols(rows[r]).length === 0);
            if (r < 0 || r >= rows.length) return null;
            cols = editableCols(rows[r]);
            i = dir === 1 ? 0 : cols.length - 1;
        }
    };

    /** Дефолтный uncontrolled-редактор по dataType (text/number/date/boolean). */
    const renderDefaultEditor = (row: DataTableRow<T>, col: IDataTableColumn<T>, value: unknown) => {
        const colId = col.field;
        const kind = filterKindOf(col.dataType);
        if (kind === 'boolean') {
            return (
                <input
                    type="checkbox"
                    className="dtb-check"
                    // тот же предикат, что у отображения «Да»/«Нет»: ячейка с числом 1
                    // не должна открывать снятый чекбокс под текстом «Да»
                    defaultChecked={isTruthyBool(value)}
                    autoFocus
                    aria-label={col.header}
                    onChange={(e) => commitCell(row, colId, e.target.checked, true)}
                    onBlur={() => {
                        if (!editorDoneRef.current) closeEditor(false);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.stopPropagation();
                            closeEditor(true);
                        }
                    }}
                />
            );
        }
        const parse = (raw: string): unknown => (kind === 'number' ? (raw === '' ? null : Number(raw)) : raw);
        const defaultValue = kind === 'date' ? dayjs(String(value ?? '')).format('YYYY-MM-DD') : String(value ?? '');
        return (
            <input
                type={kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text'}
                className="dtb__cell-editor"
                defaultValue={defaultValue === 'Invalid Date' ? '' : defaultValue}
                autoFocus
                aria-label={col.header}
                onFocus={(e) => kind !== 'date' && e.target.select()}
                onBlur={(e) => {
                    if (!editorDoneRef.current) commitCell(row, colId, parse(e.target.value), false);
                }}
                onKeyDown={(e) => {
                    const input = e.currentTarget;
                    if (e.key === 'Escape') {
                        e.stopPropagation();
                        closeEditor(true);
                    } else if (e.key === 'Enter') {
                        e.preventDefault();
                        commitCell(row, colId, parse(input.value), true);
                    } else if (e.key === 'Tab') {
                        e.preventDefault();
                        onCellCommit?.(row.original, colId, parse(input.value));
                        editorDoneRef.current = true;
                        const next = nextEditableCell(row.id, colId, e.shiftKey ? -1 : 1);
                        if (next) startEdit(next.rowId, next.colId);
                        else closeEditor(true);
                    }
                }}
            />
        );
    };

    const renderCellEditor = (row: DataTableRow<T>, col: IDataTableColumn<T>, value: unknown) =>
        col.renderEditor
            ? col.renderEditor({
                  row: row.original,
                  value,
                  commit: (v) => commitCell(row, col.field, v, true),
                  cancel: () => closeEditor(true),
              })
            : renderDefaultEditor(row, col, value);

    const canToggleDetailOnClick = (row: DataTableRow<T>) => expandDetailOnRowClick && hasDetail && row.getCanExpand();
    const onRowLineClick = (row: DataTableRow<T>, e: MouseEvent) => {
        if (!isOwnEvent(e)) return;
        const canRunRowClick = !!onRowClick && (!isRowClickable || isRowClickable(row.original));
        const toggleDetail = canToggleDetailOnClick(row);
        if (!canRunRowClick && !selectOnRowClick && !toggleDetail) return;
        const t = e.target as HTMLElement;
        if (t.closest('button, input, a, .dtb__cell--check, .dtb__row-actions, .dtb__cell--editable')) return;
        // в пикерах строка и есть выбор: клик по ней ставит и снимает галочку
        if (selectOnRowClick && row.getCanSelect()) toggleRowSelected(row, { target: { checked: !row.getIsSelected() }, shiftKey: e.shiftKey });
        if (canRunRowClick) events.onRowClick?.(row.original);
        if (toggleDetail && !t.closest(DETAIL_CLICK_IGNORE) && !hasTextSelectionIn(e.currentTarget)) {
            const next = !row.getIsExpanded();
            row.toggleExpanded(next);
            if (next) void events.onExpandRow?.(row.original);
        }
    };

    // Shift-клик выбирает диапазон — текст между кликами выделяться не должен
    const onRowLineMouseDown = (e: MouseEvent) => {
        if (!selectable || singleSelection || !e.shiftKey) return;
        const t = e.target as HTMLElement;
        const onRowBody = selectOnRowClick && !t.closest('button, input, textarea, a, .dtb__cell--editable');
        if (onRowBody || t.closest('.dtb__cell--check')) e.preventDefault();
    };

    const onRowLineDoubleClick = (row: DataTableRow<T>, e: MouseEvent) => {
        if (!onRowDoubleClick || !isOwnEvent(e)) return;
        if (selectable && !row.getCanSelect()) return;
        const target = e.target as HTMLElement;
        if (target.closest('button, input, a, .dtb__cell--check, .dtb__row-actions, .dtb__cell--editable')) return;
        // Два обычных клика уже переключили галочку: подтверждаем именно эту строку.
        if (selectOnRowClick && row.getCanSelect()) row.toggleSelected(true);
        events.onRowDoubleClick?.(row.original);
    };

    /** Выбор строки кликом по чекбоксу или по самой строке. Обработчик TanStack
     *  ведёт якорь Shift-диапазона: последняя строка, выбранная через него, по id;
     *  диапазон — в порядке строк до пагинации, то есть и через страницы.
     *  Закреплённые строки показаны вне этого порядка: им только переключение. */
    const toggleRowSelected = (row: DataTableRow<T>, e: { target: { checked: boolean }; shiftKey?: boolean }) => {
        // Пропущенная строка держит чекбокс с Row своего рендера, а после подгрузки модель строк новая.
        // TanStack строит Shift-диапазон по идентичности Row в текущей модели.
        const currentRow = table.getRow(row.id, true);
        if (currentRow.getIsPinned()) currentRow.toggleSelected(e.target.checked);
        else currentRow.getToggleSelectedHandler()(e);
    };

    // ----- Drag&drop строк (dnd-kit): строки расступаются при перетаскивании; -----
    // порядок применяет потребитель (onRowReorder); перетаскивание строки из
    // выделения тащит всё выделение
    const dndSensors = useSensors(
        useSensor(PointerSensor, POINTER_SENSOR_OPTIONS),
        useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
    );
    const sortableIds = useMemo(
        () => (allowDrag ? rows.filter((r) => !r.getIsGrouped()).map((r) => r.id) : []),
        [allowDrag, rows],
    );
    // группа перетаскивания фиксируется на старте: выделение, если тащат его элемент
    const [dragGroup, setDragGroup] = useState<{ activeId: string; ids: Set<string> } | null>(null);

    const onDndStart = ({ active }: DragStartEvent) => {
        const id = String(active.id);
        const dragged = table.getRowModel().rowsById[id];
        const ids =
            dragged?.getIsSelected() && selectedKeys.some((k) => k !== id) ? new Set(selectedKeys) : new Set([id]);
        setDragGroup({ activeId: id, ids });
    };
    const onDndEnd = ({ active, over }: DragEndEvent) => {
        const group = dragGroup;
        setDragGroup(null);
        if (!over || over.id === active.id || !group) return;
        const overId = String(over.id);
        if (group.ids.has(overId)) return; // дроп внутрь собственного выделения — no-op
        const model = table.getRowModel().rowsById;
        const target = model[overId];
        if (!target) return;
        const activeIdx = sortableIds.indexOf(String(active.id));
        const overIdx = sortableIds.indexOf(overId);
        // тянули вниз — встаём после цели, вверх — перед (семантика arrayMove)
        const position: 'before' | 'after' = activeIdx < overIdx ? 'after' : 'before';
        const groupRows = rows.filter((r) => group.ids.has(r.id)).map((r) => r.original);
        onRowReorder?.({ rows: groupRows, targetRow: target.original, position });
    };

    // Атрибуты меняются вместе с признаком «несут» — новая связь перерисовывает содержимое строки.
    const renderDragHandle = (sortable: ISortableRowHandle) => (
        <div className={clsx('dtb__cell dtb__cell--draggrip', stickyClass('__drag'))} style={stickyStyle('__drag')}>
            <button
                type="button"
                className="dtb__draghandle"
                title="Перетащить строку"
                {...sortable.attributes}
                {...sortable.listeners}
                aria-label="Перетащить строку"
            >
                <MdDragIndicator aria-hidden />
            </button>
        </div>
    );

    // Серверная выгрузка: файл строит бэкенд по текущей выборке, и на больших
    // списках это минуты — кнопка на это время показывает ход. Выгрузку источника
    // размонтирование таблицы отменяет; явный serverExport сигнала не получает.
    // Фоновую (source.backgroundExport) — нет: её файл готовит задача на сервере.
    const sourceExport = source?.export;
    const serverExport: ((request: IDataTableExportRequest, signal: AbortSignal) => Promise<void>) | undefined =
        serverExportProp ? (request) => serverExportProp(request) : sourceExport;
    const backgroundExport = serverExportProp ? undefined : source?.backgroundExport;
    const exportAbortRef = useRef<AbortController | null>(null);
    const serverExportButtonRef = useRef<HTMLButtonElement>(null);
    const unmountedRef = useRef(false);
    useEffect(() => {
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            exportAbortRef.current?.abort();
        };
    }, []);
    const notifyExportFailure = (e: unknown) =>
        notify({
            message: `Файл не выгрузился. ${errorText(e, 'Повторите выгрузку.')}`,
            traceId: errorTraceId(e),
            variant: 'red',
        });
    const exportRequest = (maxItemsCount?: number): IDataTableExportRequest => ({
        loadOptions: server.loadOptions,
        maxItemsCount,
        columns: visibleCols.flatMap((c, index) => {
            const col = c.columnDef.meta?.dataColumn;
            return col
                ? [
                      {
                          field: col.field,
                          header: col.header,
                          dataType: col.dataType,
                          width: c.getSize(),
                          visibleIndex: index,
                          ...(col.group ? { group: col.group } : {}),
                      },
                  ]
                : [];
        }),
    });
    const runForegroundExport = (request: IDataTableExportRequest) => {
        if (!serverExport) return;
        const controller = new AbortController();
        exportAbortRef.current = controller;
        setExporting(true);
        serverExport(request, controller.signal)
            .catch((e: unknown) => {
                // отменённая размонтированием выгрузка источника — не отказ
                if (!serverExportProp && controller.signal.aborted) return;
                notifyExportFailure(e);
            })
            .finally(() => setExporting(false));
    };
    const runServerExport = (maxItemsCount?: number, background?: { suggested: boolean }) => {
        if (!serverExport || exporting) return;
        const request = exportRequest(maxItemsCount);
        onExport?.();
        if (!background || !backgroundExport) {
            runForegroundExport(request);
            return;
        }
        setExporting(true);
        backgroundExport
            .start({
                ...request,
                suggested: background.suggested,
                origin: serverExportButtonRef.current ?? undefined,
            })
            .then((outcome) => {
                // фона нет — файл обычной выгрузкой, но только пока таблица на экране: её сигнал её и отменит
                if (outcome === 'unavailable' && !unmountedRef.current) runForegroundExport(request);
                else setExporting(false);
            })
            .catch((e: unknown) => {
                notifyExportFailure(e);
                setExporting(false);
            });
    };

    const handleServerExportClick = () => {
        if (serverExportLimitModal) {
            setShowExportLimitModal(true);
            return;
        }
        runServerExport(
            undefined,
            suggestsBackgroundExport(backgroundExport, server.totalCount) ? { suggested: true } : undefined,
        );
    };

    /** Шапка и строки файла клиентской выгрузки — как на экране: видимые колонки, фильтр, сортировка. */
    const collectExportRows = (onlySelected: boolean) => {
        const exportCols = visibleCols.filter((c) => c.columnDef.meta?.dataColumn);
        const headers = exportCols.map((c) => c.columnDef.meta?.dataColumn?.header ?? c.id);
        const headerGroups = showColumnGroups ? exportCols.map((c) => c.columnDef.meta?.dataColumn?.group) : undefined;
        const model = paging ? table.getPrePaginatedRowModel() : table.getRowModel();
        const selectedIds = onlySelected ? new Set(selectedKeys) : null;
        // группы уходят строками-заголовками (как у DX), свёрнутость не влияет
        const rowsOut: (ExcelDataCell[] | { group: string })[] = [];
        const walk = (rs: DataTableRow<T>[]) => {
            for (const r of rs) {
                if (r.getIsGrouped()) {
                    const colId = r.groupingColumnId ?? '';
                    const dcol = columns.find((c) => c.field === colId);
                    const leafRows = r.getLeafRows().filter((x) => !x.getIsGrouped());
                    const value = r.getValue(colId);
                    const text =
                        dcol?.groupExportText?.({ value, rows: leafRows.map((x) => x.original) }) ??
                        `${dcol?.header ?? colId}: ${formatCellText(value, dcol?.dataType) || '(Пусто)'} (${leafRows.length})`;
                    rowsOut.push({ group: text });
                    walk(r.subRows);
                } else {
                    if (!selectedIds || selectedIds.has(r.id)) {
                        rowsOut.push(
                            exportCols.map((c) =>
                                columnExcelCell(
                                    c.columnDef.meta?.dataColumn as IDataTableColumn<T> | undefined,
                                    r.original,
                                    r.getValue(c.id),
                                ),
                            ),
                        );
                    }
                    if (r.subRows.length) walk(r.subRows);
                }
            }
        };
        walk(model.rows);
        return { headers, headerGroups, rows: rowsOut };
    };

    const handleExport = (onlySelected = false) => {
        if (serverExport) {
            handleServerExportClick();
            return;
        }
        if (!exportFileName || exporting) return;
        setExporting(true);
        // строки и книга собираются в главном потоке: сначала кадр с ходом на кнопке, потом работа
        nextPaint()
            .then(() => exportDataTableToExcel({ fileName: exportFileName, ...collectExportRows(onlySelected) }))
            // экспорт идёт мимо списка: список цел, потерян только файл — уведомление
            .catch((e: unknown) => {
                notify({
                    message: `Файл не выгрузился. ${errorText(e, 'Повторите выгрузку.')}`,
                    variant: 'red',
                    traceId: errorTraceId(e),
                });
            })
            .finally(() => setExporting(false));
        onExport?.();
    };
    const selectedCount = selectedKeys.length;
    const exportButtonProps = {
        type: 'button',
        className: 'dtb__icon-btn',
        title: exporting ? 'Готовим файл…' : (exportTexts?.exportTo ?? 'Экспорт в Excel'),
        'aria-label': exporting ? 'Готовим файл' : 'Экспорт в Excel',
        'aria-busy': exporting,
        disabled: exporting,
    } as const;
    const exportButtonIcon = exporting ? (
        <span className="dtb__btn-spin" aria-hidden />
    ) : (
        <HiOutlineArrowDownTray aria-hidden />
    );

    // aria-colindex ячеек и шапки: номер колонки среди видимых данных (1-based),
    // служебные треки (чекбокс, действия, экспандер) не нумеруются
    const colIndexById = new Map(visibleCols.map((c, i) => [c.id, i + 1] as const));

    // ----- Пропуск неизменившихся строк -----
    // Прокрутка вызывает рендер списка целиком, а сами строки при этом не меняются:
    // memo-строка с теми же пропсами пропускает всё своё поддерево (ячейки, ссылки,
    // hover-карточки, меню). Версия собирает всё, что меняет вид сразу всех строк;
    // признаки самой строки — её собственные пропсы. Без cacheRows версия новая на
    // каждый рендер: строки перерисовываются вместе с таблицей.
    const dragging = !!dragGroup;
    const visibleColsKey = visibleCols.map((c) => c.id).join('\u0000');
    // Render-пропсы потребителя (колонки, действия, подробности) входят по ссылке: их замыкания рисуют
    // содержимое строк. Скрипты строк и ячеек — тоже по ссылке: загрузившийся позже скрипт применяется к
    // уже показанным строкам. Событийные колбэки (onRowClick, onRowDoubleClick, onExpandRow, меню строки)
    // строка читает в момент события, и их новая ссылка строки не перерисовывает.
    const cachedRowsView = useMemo(
        () => ({}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            columns,
            visibleColsKey,
            stickyLayoutKey,
            spacerBeforeColId,
            spacerBeforeActions,
            hasTrailingSpacer,
            wrapMode,
            dragging,
            selectable,
            hasActionsCol,
            hasActionsLeft,
            showExpanderTrack,
            allowDrag,
            allowRowPinning,
            hasTree,
            rowActions,
            rowActionsAlwaysVisible,
            renderDetail,
            onRowRendered,
            onCellRendered,
        ],
    );
    const rowsView = cacheRows ? cachedRowsView : {};
    // Версия вида ячеек: только то, что читает их рендер (значение, закрепление, кнопка разворота, скрипт ячейки).
    // Действия, подробности, выбор и перенос строк ячейки не перерисовывают.
    const cachedCellsView = useMemo(
        () => ({}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [columns, stickyLayoutKey, hasTree, onCellRendered],
    );
    const cellsView = cacheRows ? cachedCellsView : {};

    const editingColOf = (row: DataTableRow<T>) => (editingCell?.rowId === row.id ? editingCell.colId : undefined);

    const renderCells = (
        row: DataTableRow<T>,
        editingColId: string | undefined,
        rowVersionValue: string | number | undefined,
    ) =>
        row.getVisibleCells().flatMap((cell) => {
            const col = cell.column.columnDef.meta?.dataColumn;
            if (!col) return [<div key={cell.id} />];
            const colId = cell.column.id;
            const editable = isColEditable(col, row);
            const editing = editable && editingColId === colId;
            const treeLabel = !editing && hasTree && colId === visibleCols[0]?.id;
            // распорка перед правой sticky-группой — див держит соответствие ячеек трекам
            const spacer = colId === spacerBeforeColId ? [<div key="__spacer" className="dtb__elastic-spacer" />] : [];
            return [
                ...spacer,
                <DataTableBodyCell<T>
                    key={cell.id}
                    row={row}
                    colId={colId}
                    col={col}
                    value={cell.getValue()}
                    colIndex={colIndexById.get(colId)}
                    align={alignOf(col)}
                    editable={editable}
                    editing={editing}
                    treeLabel={treeLabel}
                    indent={treeLabel ? row.depth * TREE_INDENT : 0}
                    canExpand={treeLabel && row.getCanExpand()}
                    expanded={treeLabel && row.getIsExpanded()}
                    loading={treeLabel && loadingRowIdSet.has(row.id)}
                    // редактор держит замыкания коммита: ячейку в правке рисуем на каждый рендер
                    view={editing ? {} : cellsView}
                    version={cellVersion ? cellVersion(row.original, colId) : rowVersionValue}
                    renderer={bodyCellRenderer}
                />,
            ];
        });

    const virtualStyle = (vi?: VirtualItem): CSSProperties | undefined =>
        vi ? virtualRowStyle(vi.start - scrollMargin) : undefined;

    // ключи строк нормализованы (см. rowIdOf) — приводим и то, что пришло снаружи
    const loadingRowIdSet = useMemo(() => new Set(Array.from(loadingRowIds ?? [], String)), [loadingRowIds]);

    /** Сама кнопка: в дереве она живёт в первой колонке, у master-detail — своим треком. */
    const renderExpandButton = (row: DataTableRow<T>, open: boolean, rowLoading: boolean) => {
        const subject = hasTree ? 'строку' : 'подробности';
        return (
            <button
                type="button"
                className={clsx('dtb__expand', open && 'is-open', rowLoading && 'is-loading')}
                onClick={(e) => {
                    e.stopPropagation();
                    if (rowLoading) return;
                    const next = !open;
                    row.toggleExpanded(next);
                    if (next) void events.onExpandRow?.(row.original);
                }}
                disabled={rowLoading}
                aria-label={rowLoading ? 'Загрузка дочерних строк' : `${open ? 'Свернуть' : 'Развернуть'} ${subject}`}
            >
                {rowLoading ? <span className="dtb__expand-spinner" aria-hidden /> : <HiChevronRight aria-hidden />}
            </button>
        );
    };

    const renderRowExpander = (row: DataTableRow<T>, canExpand: boolean, open: boolean, loading: boolean) => (
        <div className={clsx('dtb__tree-toggle', stickyClass('__expander'))} style={stickyStyle('__expander')}>
            {canExpand && renderExpandButton(row, open, loading)}
        </div>
    );

    const renderTreeLoading = (row: DataTableRow<T>) => {
        const childDepth = row.depth + 1;
        return (
            <div className="dtb__tree-loading" role="status" aria-label="Загрузка дочерних строк">
                {Array.from({ length: TREE_SKELETON_ROWS }, (_, i) => (
                    <div key={i} className="dtb__tree-loading-row">
                        {allowDrag && <div />}
                        {selectable && (
                            <div className="dtb__tree-loading-check">
                                <Skeleton height={18} width={18} radius={5} />
                            </div>
                        )}
                        {hasActionsLeft && <div />}
                        {showExpanderTrack && <div />}
                        {visibleCols.map((c, j) => (
                            <Fragment key={c.id}>
                                {c.id === spacerBeforeColId && <div className="dtb__elastic-spacer" />}
                                <div className="dtb__cell dtb__tree-loading-cell">
                                    <div style={{ marginLeft: j === 0 ? childDepth * TREE_INDENT : 0 }}>
                                        <Skeleton height={12} radius={6} width={`${[68, 52, 62, 44][(i + j) % 4]}%`} />
                                    </div>
                                </div>
                            </Fragment>
                        ))}
                        {spacerBeforeActions && <div className="dtb__elastic-spacer" />}
                        {hasActionsCol && <div />}
                        {needSpacer && rightStickyCount === 0 && <div className="dtb__elastic-spacer" />}
                    </div>
                ))}
            </div>
        );
    };

    /** Содержимое строки группы: кастомное (groupRender колонки) или «Заголовок: значение». */
    const renderGroupContent = (row: DataTableRow<T>, sticky: boolean) => {
        const groupColId = row.groupingColumnId ?? grouping[row.depth] ?? '';
        const gcol = columns.find((c) => c.field === groupColId);
        if (gcol?.groupRender) {
            return gcol.groupRender({
                value: row.getValue(groupColId),
                rows: row
                    .getLeafRows()
                    .filter((r) => !r.getIsGrouped())
                    .map((r) => r.original),
                isExpanded: row.getIsExpanded(),
                hasActiveFilters,
                sticky,
            });
        }
        return (
            <span className="dtb__group-title">
                {gcol?.header ?? groupColId}:{' '}
                <b>{formatCellText(row.getValue(groupColId), gcol?.dataType) || '(Пусто)'}</b>
            </span>
        );
    };

    // Копия верхней группы: в туллбаре (внешний скролл) или под шапкой (внутренний)
    const renderBarTopGroup = (rowId: string) => {
        const topGroupRow = table.getRowModel().rowsById[rowId];
        if (!topGroupRow) return null;
        return (
            <div className="dtb__bar-group" role="status">
                {renderGroupContent(topGroupRow, true)}
            </div>
        );
    };
    const renderStickyTopGroup = (rowId: string) => {
        const topGroupRow = table.getRowModel().rowsById[rowId];
        if (!topGroupRow) return null;
        return (
            <div className="dtb__stickygroup" style={{ top: theadH }} role="status">
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                <div
                    className="dtb__grouprow-tile"
                    onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button, input, a')) return;
                        topGroupRow.toggleExpanded(false);
                    }}
                >
                    <div className="dtb__group-label">
                        <button
                            type="button"
                            className="dtb__expand is-open"
                            onClick={() => topGroupRow.toggleExpanded(false)}
                            aria-label="Свернуть группу"
                        >
                            <HiChevronRight aria-hidden />
                        </button>
                        {renderGroupContent(topGroupRow, true)}
                        <span className="dtb__group-count">{topGroupRow.subRows.length}</span>
                    </div>
                </div>
            </div>
        );
    };

    /** Строка-группа: шеврон + значение + счётчик + агрегаты колонок с aggregate. */
    const renderGroupRow = (
        row: DataTableRow<T>,
        rowIndex: number,
        vi?: VirtualItem,
        measure?: (el: HTMLElement | null) => void,
    ) => {
        const open = row.getIsExpanded();
        const aggCols = visibleCols.filter((c) => c.columnDef.meta?.dataColumn?.aggregate);
        return (
            // li — слот строки (в нём живёт зазор между группами), плитка — вложенный
            // div: margin слота виртуализатор не измерил бы, а padding красился бы фоном
            <li
                key={row.id}
                data-index={vi?.index}
                role="row"
                data-row-key={row.id}
                ref={vi ? measure : undefined}
                aria-expanded={open}
                className={clsx('dtb__grouprow', rowIndex > 0 && row.depth === 0 && 'dtb__grouprow--gap')}
                style={virtualStyle(vi)}
            >
                {/* вся плитка — большая мышиная цель разворота; доступный контрол — шеврон,
                    клики по интерактиву внутри groupRender (кнопки/ссылки) не перехватываем */}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                <div
                    className={clsx(
                        'dtb__grouprow-tile',
                        row.depth === 0 ? 'dtb__grouprow-tile--first' : 'dtb__grouprow-tile--nested',
                        isLastRow(rowIndex) && 'dtb__grouprow-tile--last',
                    )}
                    style={{ '--dtb-group-inset': `${14 + row.depth * 22}px` } as CSSProperties}
                    onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button, input, a')) return;
                        row.toggleExpanded();
                    }}
                >
                    <div className={clsx('dtb__group-label', aggCols.length > 0 && 'dtb__group-label--aggs')}>
                        <button
                            type="button"
                            className={clsx('dtb__expand', open && 'is-open')}
                            onClick={row.getToggleExpandedHandler()}
                            aria-label={open ? 'Свернуть группу' : 'Развернуть группу'}
                        >
                            <HiChevronRight aria-hidden />
                        </button>
                        {renderGroupContent(row, false)}
                        <span className="dtb__group-count">{row.subRows.length}</span>
                        {aggCols.length > 0 && (
                            <span className="dtb__group-aggs">
                                {aggCols.map((c) => {
                                    const dcol = c.columnDef.meta?.dataColumn;
                                    if (!dcol?.aggregate) return null;
                                    return (
                                        <span key={c.id} className="dtb__group-agg">
                                            {dcol.header}:{' '}
                                            <b>{formatAggregate(row.getValue(c.id), dcol.aggregate, dcol.dataType)}</b>
                                        </span>
                                    );
                                })}
                            </span>
                        )}
                    </div>
                </div>
            </li>
        );
    };

    /** Запись серверной группировки: группа (плитка как у клиентских групп,
     *  но счётчик и раскрытие — от сервера) или кнопка «Показать ещё». */
    const renderServerGroupEntry = (
        entry: ServerGroupEntry<T>,
        rowIndex: number,
        vi?: VirtualItem,
        measure?: (el: HTMLElement | null) => void,
    ) => {
        if (entry.kind === 'row') return null; // строки рендерит общий путь
        if (entry.kind === 'more') {
            return (
                <li
                    key={entry.id}
                    data-index={vi?.index}
                    role="row"
                    data-row-key={entry.id}
                    ref={vi ? measure : undefined}
                    className="dtb__grouprow"
                    style={virtualStyle(vi)}
                >
                    <div
                        className={clsx('dtb__groupmore', isLastRow(rowIndex) && 'dtb__groupmore--last')}
                        style={{ '--dtb-group-inset': `${14 + entry.depth * 22}px` } as CSSProperties}
                    >
                        <div className="dtb__group-label">
                            {entry.error ? (
                                <span className="dtb__vfoot-fail">
                                    Продолжение не загрузилось
                                    <button
                                        type="button"
                                        className="dtb__fail-retry dtb__fail-retry--inline"
                                        onClick={() => groupsApi.loadMoreAt(entry.path)}
                                    >
                                        Повторить
                                    </button>
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    className="dtb__groupmore-btn"
                                    disabled={entry.loading}
                                    onClick={() => groupsApi.loadMoreAt(entry.path)}
                                >
                                    {entry.loading
                                        ? 'Загрузка…'
                                        : `Показать ещё (${entry.remaining.toLocaleString('ru-RU')})`}
                                </button>
                            )}
                        </div>
                    </div>
                </li>
            );
        }
        const gcol = columns.find((c) => c.field === entry.columnId);
        const open = entry.expanded;
        return (
            // структура и классы — как у клиентской группы (см. renderGroupRow)
            <li
                key={entry.id}
                data-index={vi?.index}
                role="row"
                data-row-key={entry.id}
                ref={vi ? measure : undefined}
                aria-expanded={open}
                className={clsx('dtb__grouprow', rowIndex > 0 && entry.depth === 0 && 'dtb__grouprow--gap')}
                style={virtualStyle(vi)}
            >
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                <div
                    className={clsx(
                        'dtb__grouprow-tile',
                        entry.depth === 0 ? 'dtb__grouprow-tile--first' : 'dtb__grouprow-tile--nested',
                        isLastRow(rowIndex) && 'dtb__grouprow-tile--last',
                    )}
                    style={{ '--dtb-group-inset': `${14 + entry.depth * 22}px` } as CSSProperties}
                    onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button, input, a')) return;
                        groupsApi.toggleGroup(entry.path);
                    }}
                >
                    <div className="dtb__group-label">
                        <button
                            type="button"
                            className={clsx('dtb__expand', open && 'is-open')}
                            onClick={() => groupsApi.toggleGroup(entry.path)}
                            aria-label={open ? 'Свернуть группу' : 'Развернуть группу'}
                        >
                            {entry.loading ? (
                                <span className="dtb__check-spin" aria-hidden />
                            ) : (
                                <HiChevronRight aria-hidden />
                            )}
                        </button>
                        <span className="dtb__group-title">
                            {gcol?.header ?? entry.columnId}: <b>{entry.caption || '(Пусто)'}</b>
                        </span>
                        <span className="dtb__group-count">{entry.count.toLocaleString('ru-RU')}</span>
                    </div>
                </div>
            </li>
        );
    };

    const isLastRow = (rowIndex: number) => {
        if (hasGrouping) {
            // Виртуальный список плоский: остров заканчивается только перед следующей
            // корневой группой, а его низом может быть подгруппа или «Показать ещё».
            const next = rows[rowIndex + 1];
            if (!next) return true;
            const entry = serverGroupEntryOf(next.original);
            return entry ? entry.kind === 'group' && entry.depth === 0 : next.getIsGrouped() && next.depth === 0;
        }
        // При догрузке низ обычного списка закрывает последний skeleton.
        return rowIndex === rows.length - 1 && !server.hasMore && !loadingMore;
    };

    const renderRow = (
        row: DataTableRow<T>,
        rowIndex: number,
        vi?: VirtualItem,
        sortable?: ISortableRowState,
        measure?: (el: HTMLElement | null) => void,
    ) => {
        const serverEntry = serverGroupEntryOf(row.original);
        if (serverEntry) return renderServerGroupEntry(serverEntry, rowIndex, vi, measure);
        if (row.getIsGrouped()) return renderGroupRow(row, rowIndex, vi, measure);
        const sel = row.getIsSelected();
        const expanded = row.getIsExpanded();
        const loading = loadingRowIdSet.has(row.id);
        const detailOpen = hasDetail && expanded;
        const treeLoading = hasTree && expanded && loading;
        const isDragFollower = !!dragGroup && dragGroup.ids.has(row.id) && dragGroup.activeId !== row.id;
        // один вызов на строку: у форм rowStatus — полный скан валидации строки
        const status = rowStatus?.(row.original);
        const clickable =
            (!!onRowClick && (!isRowClickable || isRowClickable(row.original))) || canToggleDetailOnClick(row);
        const editingColId = editingColOf(row);
        return (
            <DataTableBodyRow<T>
                key={row.id}
                row={row}
                rowIndex={rowIndex}
                virtualIndex={vi?.index}
                // строка видит только своё место в списке (top = start − scrollMargin): перемер отступа
                // списка от контейнера прокрутки (первый layout-эффект грида) строки не перерисовывает
                top={vi ? vi.start - scrollMargin : undefined}
                measure={vi ? measure : undefined}
                sortable={sortable?.handle}
                dndY={sortable?.dndY}
                dndTransition={sortable?.dndTransition}
                className={clsx(
                    'dtb__row',
                    // края зоны данных: скругление несут первая и последняя строки
                    // (в виртуальном списке :first-child/:last-child врут — в DOM
                    // живут только видимые строки)
                    rowIndex === 0 && 'dtb__row--first',
                    // низ зоны — у настоящей последней строки (при подгрузке
                    // прокруткой скруглять край очередной порции нельзя) и у
                    // последней строки группы: каждая группа — свой остров
                    isLastRow(rowIndex) && 'dtb__row--last',
                    row.id === currentRowId && 'dtb__row--current',
                    row.id === revealedRow?.rowId && 'dtb__row--revealed',
                    status && `dtb__row--${status}`,
                    rowClassName?.(row.original),
                    sel && 'is-selected',
                    detailOpen && 'is-detail-open',
                    treeLoading && 'is-tree-loading',
                    clickable && 'is-clickable',
                    sortable?.dragging && 'is-dragging',
                    isDragFollower && 'is-drag-follower',
                )}
                selected={sel}
                // без выбора чекбокса нет: рекурсию по детям и правило выбора не считаем
                someSelected={selectable && row.getIsSomeSelected()}
                selectionDisabled={selectable && !row.getCanSelect()}
                pinned={row.getIsPinned() === 'top'}
                expanded={expanded}
                canExpand={row.getCanExpand()}
                loading={loading}
                ariaSelected={selectable ? sel : undefined}
                ariaLevel={hasTree ? row.depth + 1 : undefined}
                editingColId={editingColId}
                // правило editable(row) считается на каждый рендер: строка перерисуется, когда его ответ сменится
                editableKey={
                    canEdit
                        ? visibleCols.map((c) => (isColEditable(c.columnDef.meta?.dataColumn, row) ? 1 : 0)).join('')
                        : undefined
                }
                view={editingColId ? {} : rowsView}
                version={rowVersion?.(row.original)}
                renderer={bodyRowRenderer}
            />
        );
    };

    const renderPinButton = (row: DataTableRow<T>, pinned: boolean) => (
        <button
            type="button"
            className={clsx('dtb__pin', pinned && 'is-on')}
            title={pinned ? 'Открепить строку' : 'Закрепить строку'}
            aria-label={pinned ? 'Открепить строку' : 'Закрепить строку'}
            onClick={() => row.pin(pinned ? false : 'top')}
        >
            <HiOutlineMapPin aria-hidden />
        </button>
    );

    /** Служебные треки, ячейки и действия строки данных (содержимое `.dtb__rowline`). */
    const renderBodyRowLine = ({
        row,
        sortable,
        selected,
        someSelected,
        selectionDisabled,
        pinned,
        canExpand,
        expanded,
        loading,
        editingColId,
        version,
    }: IDataTableBodyRowContentProps<T>) => (
        <>
            {allowDrag && (sortable ? renderDragHandle(sortable) : <div />)}
            {selectable && (
                <div
                    className={clsx('dtb__cell dtb__cell--check', stickyClass('__check'))}
                    style={stickyStyle('__check')}
                >
                    <input
                        type="checkbox"
                        className="dtb-check"
                        checked={selected}
                        disabled={selectionDisabled}
                        ref={(el) => {
                            if (el) el.indeterminate = someSelected && !selected;
                        }}
                        onChange={(e) => toggleRowSelected(row, e)}
                        aria-label="Выбрать строку"
                    />
                </div>
            )}
            {hasActionsLeft && (
                <div
                    className={clsx(
                        'dtb__row-actions dtb__row-actions--left',
                        rowActionsAlwaysVisible && 'dtb__row-actions--visible',
                        stickyClass('__actionsLeft'),
                    )}
                    style={stickyStyle('__actionsLeft')}
                >
                    {allowRowPinning && renderPinButton(row, pinned)}
                    {rowActions?.(row.original)}
                </div>
            )}
            {showExpanderTrack && (hasExpansionColumn ? renderRowExpander(row, canExpand, expanded, loading) : <div />)}
            {renderCells(row, editingColId, version)}
            {spacerBeforeActions && <div className="dtb__elastic-spacer" />}
            {hasActionsCol && (
                <div
                    className={clsx(
                        'dtb__row-actions',
                        rowActionsAlwaysVisible && 'dtb__row-actions--visible',
                        stickyClass('__actions'),
                    )}
                    style={stickyStyle('__actions')}
                >
                    {allowRowPinning && renderPinButton(row, pinned)}
                    {rowActions?.(row.original)}
                </div>
            )}
        </>
    );

    /** Под строкой: панель master-detail или скелет загружаемых дочерних строк. */
    const renderBodyRowBelow = ({ row, expanded, loading }: IDataTableBodyRowContentProps<T>) => (
        <>
            {hasDetail && expanded && <div className="dtb__detail">{renderDetail?.(row.original)}</div>}
            {hasTree && expanded && loading && renderTreeLoading(row)}
        </>
    );

    const bodyRowRenderer = useLatest<IDataTableBodyRowRenderer<T>>({
        renderLine: renderBodyRowLine,
        renderBelow: renderBodyRowBelow,
        onRowElement: (row, element) => onRowRendered?.({ element, row: row.original, rowIndex: row.index }),
        onLineClick: onRowLineClick,
        onLineMouseDown: onRowLineMouseDown,
        onLineDoubleClick: onRowLineDoubleClick,
        onLineContextMenu: onRowContext,
    });
    const bodyCellRenderer = useLatest<IDataTableBodyCellRenderer<T>>({
        renderContent: ({ row, col, value, editing }) =>
            editing
                ? renderCellEditor(row, col, value)
                : col.render
                  ? col.render(row.original)
                  : formatCell(value, col.dataType),
        renderExpandButton,
        stickyClass,
        stickyStyle,
        onCellElement: onCellRendered
            ? ({ row, col, colId, value }, element) =>
                  onCellRendered({
                      element,
                      row: row.original,
                      field: colId,
                      value,
                      text: cellTextOf(col, row, colId),
                  })
            : undefined,
        startEdit,
    });

    /** Призрак перетаскивания (DragOverlay): копия активной строки + бейдж количества. */
    const dragOverlayRow = dragGroup ? table.getRowModel().rowsById[dragGroup.activeId] : undefined;
    const dragOverlayContent = dragOverlayRow ? (
        <div className="dtb__row dtb__row--overlay" style={tableVars}>
            <div className="dtb__rowline">
                {allowDrag && (
                    <div className="dtb__cell dtb__cell--draggrip">
                        <span className="dtb__draghandle">
                            <MdDragIndicator aria-hidden />
                        </span>
                    </div>
                )}
                {selectable && <div />}
                {hasActionsLeft && <div />}
                {showExpanderTrack && <div />}
                {renderCells(dragOverlayRow, editingColOf(dragOverlayRow), rowVersion?.(dragOverlayRow.original))}
                {spacerBeforeActions && <div className="dtb__elastic-spacer" />}
                {hasActionsCol && <div />}
            </div>
            {dragGroup && dragGroup.ids.size > 1 && (
                <span className="dtb__dragcount" aria-hidden>
                    {dragGroup.ids.size}
                </span>
            )}
        </div>
    ) : null;

    /** Строка в sortable-обёртке (dnd); группы и закреплённые строки не сортируются. */
    const renderRowSortable = (row: DataTableRow<T>, i: number, vi?: VirtualItem, measure?: (el: HTMLElement | null) => void) =>
        allowDrag && !row.getIsGrouped() ? (
            <SortableRow key={row.id} id={row.id}>
                {(s) => renderRow(row, i, vi, s, measure)}
            </SortableRow>
        ) : (
            renderRow(row, i, vi, undefined, measure)
        );

    const isFiltered = hasActiveFilters || externalFiltersActive;
    // Полосу об устаревших данных можно закрыть; следующий отказ показывает её снова.
    const [dismissedError, setDismissedError] = useState<unknown>(undefined);
    const failureDismissed = dismissedError !== undefined && dismissedError === (error ?? server.error);
    // Колонок ещё нет — рисовать нечего даже под прелоадером: показываем скелет
    // таблицы, иначе спиннер висит в схлопнутом контенте (левый верхний угол).
    const schemaPending = effectiveLoading && columns.length === 0;
    /** Первая загрузка: колонки известны, строк ещё нет — показываем скелет строк,
     *  иначе на месте списка мелькает пустая таблица со словами «Нет данных». */
    const emptyRef = useRef<HTMLDivElement>(null);
    const [emptyHeight, setEmptyHeight] = useState<number>();
    // Пустой ответ уже был показан: до следующего ответа держим его высоту,
    // скрывая устаревшее сообщение. Это не первая загрузка со скелетом строк.
    const refreshingEmpty =
        showEmptyState &&
        emptyHeight != null &&
        effectiveData.length === 0 &&
        !allColumnsHidden &&
        (effectiveLoading || (isServer && server.stale));
    const firstLoadPending = effectiveLoading && columns.length > 0 && effectiveData.length === 0 && !refreshingEmpty;
    // Отказ загрузки: своё сообщение потребителя или отказ серверного источника.
    const failure = error ?? server.error;
    const hasRows = effectiveData.length > 0;
    // Показывать нечего — сообщение занимает место списка; строки на экране есть —
    // сообщение полосой над ними, данные остаются от прошлой удачной загрузки.
    const failureBlocks = !!failure && !hasRows && !failureDismissed;
    const failureBar = !!failure && hasRows && !server.errorOnLoadMore && !failureDismissed;
    const retry = onRetry ?? (isServer ? server.reload : undefined);
    const showEmpty =
        showEmptyState &&
        !effectiveLoading &&
        !(isServer && server.stale) &&
        !allColumnsHidden &&
        !failureBlocks &&
        (isServer ? server.totalCount === 0 : filteredCount === 0);
    // Пусто по фильтру: строки есть, но фильтр или поиск скрыл все. Опустевший клиентский источник — пустой и с
    // фильтром; серверную выборку по фильтру от пустого источника не отличить. Фильтры страницы сужают строки ещё до
    // таблицы — с ними пусто по фильтру при любом источнике.
    const emptyByFilter = externalFiltersActive || (hasActiveFilters && (isServer || hasRows));
    const emptyActions: readonly IEmptyStateAction[] =
        !emptyByFilter || filteredEmptyActions === false
            ? []
            : (filteredEmptyActions ?? [
                  {
                      label: 'Сбросить фильтры',
                      icon: <HiOutlineArrowPath aria-hidden />,
                      variant: 'outline',
                      onClick: resetFilters,
                  },
              ]);
    useLayoutEffect(() => {
        const element = emptyRef.current;
        if (showEmpty && element) {
            setEmptyHeight(element.getBoundingClientRect().height + (parseFloat(getComputedStyle(element).marginTop) || 0));
        }
    }, [showEmpty, emptyText, emptyHint, emptyByFilter, filteredEmptyText, filteredEmptyHint, emptyActions.length]);
    // Пустота для владельца — по данным, а не по показу: без оглядки на showEmptyState и скрытые столбцы.
    const emptyState: DataTableEmptyState | undefined =
        effectiveLoading || (isServer && server.stale)
            ? undefined
            : !failure && (isServer ? server.totalCount === 0 : filteredCount === 0)
              ? emptyByFilter
                  ? 'filtered'
                  : 'empty'
              : 'none';
    const onEmptyStateChangeRef = useRef(onEmptyStateChange);
    onEmptyStateChangeRef.current = onEmptyStateChange;
    const reportedEmptyStateRef = useRef<DataTableEmptyState>('none');
    useEffect(() => {
        if (!emptyState || reportedEmptyStateRef.current === emptyState) return;
        reportedEmptyStateRef.current = emptyState;
        onEmptyStateChangeRef.current?.(emptyState);
    }, [emptyState]);
    const serverStartDistance = Math.max(serverTopRowIndex, server.windowStartIndex ?? 0);
    const replaceEmptyGrid = !!emptyPresentation && showEmpty && !hasRows && !isFiltered && !filtersDirty && !failure;
    const serverStartFeedbackVisible = serverStartFeedback !== 'idle';
    const showServerStartAction =
        showServerScrollToStart &&
        serverInfinite &&
        !serverGroupActive &&
        (serverStartFeedbackVisible || (serverStartActionAvailable && serverStartDistance > 0 && !!server.jumpToStart));
    const showBar =
        allowFullscreen ||
        !!fullscreenControl ||
        showGlobalSearch ||
        showColumnChooser ||
        showTableSettings ||
        !!exportFileName ||
        !!serverExport ||
        !!onRefresh ||
        // набранный, но не применённый фильтр — тулбар нужен ради кнопки «Применить»
        filtersDirty ||
        filterRowToggle ||
        !!title ||
        !!toolbarLead ||
        !!toolbarStart ||
        !!toolbarEnd ||
        showServerStartAction ||
        !!filteredRevealRequest ||
        showGroupPanel ||
        trackTopGroup;

    // Панель группировки живёт в тулбаре: отдельной полосой она занимала всю
    // ширину ради подсказки, которая нужна только в момент перетаскивания.
    const groupPanel = showGroupPanel ? (
        <div
            className={clsx('dtb__grouppanel', panelOver && 'is-dropready')}
            onDragOver={(e) => {
                if (dragColRef.current && !grouping.includes(dragColRef.current) && canGroupBy(dragColRef.current)) {
                    e.preventDefault();
                    setPanelOver(true);
                }
            }}
            onDragLeave={() => setPanelOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setPanelOver(false);
                const id = dragColRef.current;
                dragColRef.current = null;
                if (id && !grouping.includes(id) && canGroupBy(id)) setGrouping((prev) => [...prev, id]);
            }}
        >
            <HiOutlineRectangleGroup className="dtb__grouppanel-icon" aria-hidden />
            {grouping.length === 0 && (
                <span className="dtb__grouppanel-hint">Перетащите сюда заголовок колонки, чтобы сгруппировать</span>
            )}
            {grouping.map((id) => {
                const header = columns.find((col) => col.field === id)?.header ?? id;
                return (
                    <span
                        key={id}
                        className="dtb__groupchip"
                        draggable
                        onDragStart={() => {
                            chipDragRef.current = id;
                        }}
                        onDragEnd={() => {
                            chipDragRef.current = null;
                        }}
                        onDragOver={(e) => {
                            if (chipDragRef.current && chipDragRef.current !== id) e.preventDefault();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            const from = chipDragRef.current;
                            chipDragRef.current = null;
                            if (!from || from === id) return;
                            setGrouping((prev) => {
                                const arr = [...prev];
                                const fromIdx = arr.indexOf(from);
                                const toIdx = arr.indexOf(id);
                                if (fromIdx < 0 || toIdx < 0) return prev;
                                arr.splice(fromIdx, 1);
                                arr.splice(toIdx, 0, from);
                                return arr;
                            });
                        }}
                    >
                        {header}
                        <button
                            type="button"
                            className="dtb__groupchip-x"
                            aria-label={`Убрать группировку: ${header}`}
                            onClick={() => removeGrouping(id)}
                        >
                            <HiXMark aria-hidden />
                        </button>
                    </span>
                );
            })}
        </div>
    ) : null;

    // Выбор всех страниц не должен добавлять новую строку под таблицей: в режиме
    // fillHeight она забирала высоту у списка и сдвигала строки. Прогресс временно
    // занимает уже существующий футер (счётчик бесконечного списка или пейджер).
    const showVirtualFooter = !paging && totalCount != null && !allColumnsHidden && !failureBlocks;
    const showPager = paging && !unpagedFullscreen && !allColumnsHidden;
    const selectAllStatusContent = (
        <>
            <span className="dtb__selbar-spin" aria-hidden />
            <span>
                Выбираем все строки
                {server.totalCount ? ` — ${server.totalCount.toLocaleString('ru-RU')}` : ''}…
            </span>
            <button type="button" className="dtb__selbar-cancel" onClick={cancelSelectAll}>
                Отменить
            </button>
        </>
    );

    // Клавиши режима слушает документ, а не корень: корень не фокусируем, и клик по пустому
    // месту слоя отдаёт фокус ближайшему фокусируемому предку — форме документа
    // (<form tabindex="-1">) или body.
    const onFullscreenKeyDown = (e: globalThis.KeyboardEvent) => {
        // события из порталов (меню, пикеры, drawer строки) режим не закрывают; портал — это
        // узел вне слоя, а не его предок
        const root = rootRef.current;
        const target = e.target as Node;
        if (!root || (!root.contains(target) && !target.contains(root))) return;
        fullscreenApi.trapTab(e);
        // code, а не key: в русской раскладке клавиша та же, символ другой
        if ((e.ctrlKey || e.metaKey) && e.code === 'KeyF') {
            e.preventDefault();
            findInputRef.current?.focus();
            findInputRef.current?.select();
        }
    };
    const onFullscreenKeyDownRef = useLatest(onFullscreenKeyDown);
    useEffect(() => {
        if (!fullscreen) return;
        const onKeyDown = (e: globalThis.KeyboardEvent) => onFullscreenKeyDownRef.current(e);
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [fullscreen, onFullscreenKeyDownRef]);

    // ----- stickyHeader: прилипшие туллбар, шапка и полоса прокрутки -----
    // Шапка уходит вместе с последней строкой, а не перекрывает её: иначе короткая
    // таблица на время прокрутки выглядела пустой. Нижний margin шапки (sticky
    // держит в пределах блока margin-box) уводит её на высоту последней строки
    // раньше; строкам он возвращается отрицательным.
    // Туллбар стоит над шапкой и уходит вместе с ней. Его sticky-предел — конец
    // всей таблицы с пейджером, без поправки шапка наезжала на ещё прилипший
    // туллбар; поправка — тот же приём с margin.
    useLayoutEffect(() => {
        const root = rootRef.current;
        const wrap = tablewrapRef.current;
        const scroll = scrollRef.current;
        const headSticky = headStickyRef.current;
        if (!stickyHead || !root || !wrap || !scroll || !headSticky) return;
        const bar = barRef.current;
        const lead = stickyLeadRef?.current ?? null;
        const leadHost = lead?.parentElement ?? null;
        const update = () => {
            updateHBarOverflow();
            // заново смонтированной шапке (выход из полноэкранного) — тени закреплённых колонок
            updateScrollEdges();
            wrap.style.setProperty('--dtb-head-sticky-h', `${headSticky.offsetHeight}px`);
            // полоса прокрутки встаёт над прилипшим к низу счётчиком ленты, а не поверх него
            const footer = root.querySelector<HTMLElement>(':scope > .dtb__vfoot');
            wrap.style.setProperty('--dtb-hbar-bottom', `${footer?.offsetHeight ?? 0}px`);
            // расстояния — в layout-px: модалка могла мериться посреди scale-анимации
            const wrapRect = wrap.getBoundingClientRect();
            const scale = wrap.offsetWidth > 0 && wrapRect.width > 0 ? wrapRect.width / wrap.offsetWidth : 1;
            // предел шапки — конец плитки; под строками в ней ещё полоса прокрутки
            const tileEnd = wrapRect.bottom - parseFloat(getComputedStyle(wrap).paddingBottom) * scale;
            const list = rowsRef.current;
            // у виртуального списка в DOM только окно строк, а высота списка — полная
            const lastSize = virtual ? virtualizerRef.current?.measurementsCache.at(-1)?.size : undefined;
            const lastRowTop =
                (lastSize != null && list
                    ? list.getBoundingClientRect().bottom - lastSize * scale
                    : list?.lastElementChild?.getBoundingClientRect().top) ?? tileEnd;
            wrap.style.setProperty('--dtb-head-release', `${Math.max(0, tileEnd - lastRowTop) / scale}px`);
            // сверху вниз прилипают: элемент потребителя (stickyLeadRef), туллбар, шапка
            const leadH = lead?.offsetHeight ?? 0;
            const barH = bar?.offsetHeight ?? 0;
            root.style.setProperty('--dtb-bar-sticky-top', `${leadH}px`);
            wrap.style.setProperty('--dtb-head-sticky-top', `${leadH + barH}px`);
            // уходят, когда шапка начинает уходить вслед за последней строкой: margin —
            // расстояние от последней строки до конца их sticky-предела плюс то, что
            // под ними прилипло
            const releaseOf = (host: HTMLElement, below: number) => {
                const end = host.getBoundingClientRect().bottom - parseFloat(getComputedStyle(host).paddingBottom) * scale;
                return Math.max(0, (end - lastRowTop) / scale + below);
            };
            if (bar) root.style.setProperty('--dtb-bar-release', `${releaseOf(root, headSticky.offsetHeight)}px`);
            if (leadHost) {
                leadHost.style.setProperty('--dtb-lead-release', `${releaseOf(leadHost, barH + headSticky.offsetHeight)}px`);
            }
        };
        update();
        const observer = new ResizeObserver(update);
        // ширина строк (.dtb__rowsclip) меняется без смены размера scrollport — ресайз колонок
        [root, wrap, scroll, headSticky, bar, lead, leadHost, rowsRef.current?.parentElement].forEach(
            (element) => element && observer.observe(element),
        );
        return () => {
            observer.disconnect();
            for (const name of [
                '--dtb-head-sticky-h',
                '--dtb-head-release',
                '--dtb-head-sticky-top',
                '--dtb-hbar-bottom',
                '--dtb-x-max',
            ]) {
                wrap.style.removeProperty(name);
            }
            root.style.removeProperty('--dtb-bar-release');
            root.style.removeProperty('--dtb-bar-sticky-top');
            leadHost?.style.removeProperty('--dtb-lead-release');
        };
        // allowDrag пересоздаёт обёртку строк (DndWrap), а с ней .dtb__rowsclip. Строки
        // (сортировка, перенос) меняют последнюю строку без смены размеров; ширина
        // колонок — ход scroll-timeline шапки, который до следующего кадра читает
        // прокрутка к колонке (TableData.scrollToColumn)
    }, [
        stickyHead,
        showBar,
        schemaPending,
        allowDrag,
        rows,
        virtual,
        rowMinWidth,
        colsTemplate,
        updateHBarOverflow,
        updateScrollEdges,
        stickyLeadRef,
    ]);
    // Шапка со scroll-timeline не скроллер: горизонтальное колесо над ней
    // (свайп трекпада, Shift+колесо) отдаём строкам
    useEffect(() => {
        const head = headScrollRef.current;
        if (!headTimeline || !stickyHead || !head) return;
        const onWheel = (event: WheelEvent) => {
            const dx = event.shiftKey && !event.deltaX ? event.deltaY : event.deltaX;
            const body = scrollRef.current;
            if (!body || Math.abs(dx) <= Math.abs(event.shiftKey ? 0 : event.deltaY)) return;
            event.preventDefault();
            body.scrollLeft += dx;
        };
        // Автопрокрутку у края при переносе колонки браузер делает только у
        // скроллеров (auto/scroll) — у шапки со scroll-timeline её ведём сами
        const DRAG_EDGE = 48;
        const onDragOver = (event: globalThis.DragEvent) => {
            const body = scrollRef.current;
            if (!body) return;
            const rect = head.getBoundingClientRect();
            const toLeft = rect.left + DRAG_EDGE - event.clientX;
            const toRight = event.clientX - (rect.right - DRAG_EDGE);
            if (toLeft > 0) body.scrollLeft -= toLeft / 2;
            else if (toRight > 0) body.scrollLeft += toRight / 2;
        };
        head.addEventListener('wheel', onWheel, { passive: false });
        head.addEventListener('dragover', onDragOver);
        return () => {
            head.removeEventListener('wheel', onWheel);
            head.removeEventListener('dragover', onDragOver);
        };
    }, [headTimeline, stickyHead]);

    const headerCellRenderer = useLatest<IDataTableHeaderCellRenderer<T>>({
        toggleSort: (c, e) => {
            // ресайз, отпущенный над заголовком, сортировку не переключает
            if (resizeClickGuardRef.current) return;
            c.getToggleSortingHandler()?.(e);
        },
        openMenu: (c, e) => onHeaderContext(c)(e),
        reorderProps: (c) => ({ ...headerDropProps(c), ...headerDragProps(c) }),
        startResize: (c, e) => {
            if (canFastResize) startFastResize(c)(e);
            else {
                const header = headerByCol.get(c.id);
                if (header) withResizeGuard(c.id, header.getResizeHandler())(e);
            }
        },
        // быстрый ресайз ведут pointer-события, touch-обработчик TanStack — только обычному
        touchResize: (c, e) => {
            if (!canFastResize) headerByCol.get(c.id)?.getResizeHandler()(e);
        },
    });

    const theadNode = (
        <div className="dtb__thead" ref={theadRef} role="rowgroup" data-testid="dtb-head">
            {showColumnGroups && (
                <DataTableColumnGroupsRow
                    bands={columnGroupBands}
                    leadTracks={groupLeadTracks}
                    actionsTrack={hasActionsCol ? '__actions' : undefined}
                    spacerBeforeColId={spacerBeforeColId}
                    spacerBeforeActions={spacerBeforeActions}
                    trailingSpacer={needSpacer && rightStickyCount === 0}
                    colIndexOf={(id) => colIndexById.get(id)}
                    stickyOf={stickyBand}
                />
            )}
            <div className="dtb__head" role="row">
                {allowDrag && (
                    <div
                        className={clsx('dtb__hcell', stickyClass('__drag', 'head'))}
                        style={stickyStyle('__drag')}
                    />
                )}
                {selectable && (
                    <div
                        className={clsx(
                            'dtb__hcell dtb__hcell--check',
                            stickyClass('__check', 'head'),
                        )}
                        style={stickyStyle('__check')}
                    >
                        {selectingAll && (
                            <span
                                className="dtb__check-spin"
                                role="status"
                                aria-label="Выбираем все строки"
                            />
                        )}
                        <input
                            type="checkbox"
                            className="dtb-check"
                            hidden={selectingAll || singleSelection || !showSelectAll}
                            checked={table.getIsAllRowsSelected()}
                            ref={(el) => {
                                if (el) {
                                    el.indeterminate =
                                        table.getIsSomeRowsSelected() &&
                                        !table.getIsAllRowsSelected();
                                }
                            }}
                            onChange={toggleAllRowsSelected}
                            aria-label="Выбрать все"
                            disabled={selectAllDisabled}
                        />
                    </div>
                )}
                {hasActionsLeft && (
                    <div
                        className={clsx(
                            'dtb__hcell dtb__hcell--actions',
                            stickyClass('__actionsLeft', 'head'),
                        )}
                        style={stickyStyle('__actionsLeft')}
                    >
                        {rowActionsHeader}
                    </div>
                )}
                {showExpanderTrack && (
                    <div
                        className={clsx('dtb__hcell', stickyClass('__expander', 'head'))}
                        style={stickyStyle('__expander')}
                    />
                )}
                {visibleCols.map((c, colIdx) => {
                    const col = c.columnDef.meta?.dataColumn;
                    if (!col) return <div key={c.id} />;
                    const sorted = c.getIsSorted();
                    const filterShown = !!showHeaderFilter && col.allowHeaderFiltering !== false;
                    return (
                        <Fragment key={`h-${c.id}`}>
                            {c.id === spacerBeforeColId && (
                                <div key="__hspacer" className="dtb__hcell dtb__elastic-spacer" />
                            )}
                            <DataTableHeaderCell<T>
                                column={c}
                                col={col}
                                colIndex={colIdx + 1}
                                align={alignOf(col)}
                                sorted={sorted}
                                canSort={c.getCanSort()}
                                // не getSortIndex(): у таблицы первым уровнем
                                // может стоять служебная колонка «поколения»
                                sortIndex={sorted ? sorting.findIndex((s) => s.id === c.id) + 1 : 0}
                                multiSort={sorting.length > 1}
                                pinned={c.getIsPinned()}
                                tight={isFixedTrack(c) && c.getSize() < 110}
                                stickyClassName={stickyClass(c.id)}
                                style={stickyStyle(c.id)}
                                highlighted={!!highlightSet?.has(c.id)}
                                reorderable={canReorder(c)}
                                filterShown={filterShown}
                                filterSelected={filterShown ? columnFilterOf(c.id)?.header : undefined}
                                filterScope={filterShown ? facetSelectionOf?.(c.id).scope : undefined}
                                getFacet={facetLabels.getFacet}
                                filterLoadPage={sourceFacetPage ? loadFacetPage : undefined}
                                filterServerSearch={serverFacetSearch(col.dataType)}
                                onFilterChange={sourceFacetPage ? setHeaderFilterFromPages : setHeaderFilter}
                                resizable={!!headerByCol.get(c.id) && c.getCanResize()}
                                resizing={c.getIsResizing()}
                                renderer={headerCellRenderer}
                            />
                        </Fragment>
                    );
                })}
                {spacerBeforeActions && <div className="dtb__hcell dtb__elastic-spacer" />}
                {hasActionsCol && (
                    <div
                        className={clsx(
                            'dtb__hcell dtb__hcell--actions',
                            stickyClass('__actions', 'head'),
                        )}
                        style={stickyStyle('__actions')}
                    >
                        {rowActionsHeader}
                    </div>
                )}
                {needSpacer && rightStickyCount === 0 && <div className="dtb__hcell dtb__elastic-spacer" />}
            </div>

            {filterRowShown && (
                <div className="dtb__filter" role="row" data-testid="dtb-filter-row">
                    {allowDrag && (
                        <div
                            className={clsx('dtb__fspacer', stickyClass('__drag', 'filter'))}
                            style={stickyStyle('__drag')}
                        />
                    )}
                    {selectable && (
                        <div
                            style={stickyStyle('__check')}
                            className={clsx('dtb__fspacer', stickyClass('__check', 'filter'))}
                        />
                    )}
                    {hasActionsLeft && (
                        <div
                            className={clsx(
                                'dtb__fspacer',
                                stickyClass('__actionsLeft', 'filter'),
                            )}
                            style={stickyStyle('__actionsLeft')}
                        />
                    )}
                    {showExpanderTrack && (
                        <div
                            className={clsx(
                                'dtb__fspacer',
                                stickyClass('__expander', 'filter'),
                            )}
                            style={stickyStyle('__expander')}
                        />
                    )}
                    {visibleCols.map((c, colIdx) => {
                        const col = c.columnDef.meta?.dataColumn;
                        const filterSpacer =
                            c.id === spacerBeforeColId ? (
                                <div className="dtb__fspacer dtb__elastic-spacer" />
                            ) : null;
                        if (!col || !c.getCanFilter() || col.allowFiltering === false) {
                            return (
                                <Fragment key={`f-${c.id}`}>
                                    {filterSpacer}
                                    <div
                                        className={clsx('dtb__fspacer', stickyClass(c.id))}
                                        style={stickyStyle(c.id)}
                                    />
                                </Fragment>
                            );
                        }
                        return (
                            <Fragment key={`f-${c.id}`}>
                                {filterSpacer}
                                <div
                                    className={stickyClass(c.id, 'filter')}
                                    style={stickyStyle(c.id)}
                                    data-col={c.id}
                                    aria-colindex={colIdx + 1}
                                >
                                    <DataTableFilterCell
                                        kind={filterKindOf(col.dataType)}
                                        dataType={col.dataType}
                                        header={col.header}
                                        columnId={c.id}
                                        value={columnFilterOf(c.id)?.row}
                                        operations={col.filterOperations}
                                        defaultOperation={col.defaultFilterOperation}
                                        onChange={setRowFilter}
                                        onSubmit={applyFiltersOnClick ? submitFilters : undefined}
                                    />
                                </div>
                            </Fragment>
                        );
                    })}
                    {spacerBeforeActions && <div className="dtb__fspacer dtb__elastic-spacer" />}
                    {hasActionsCol && (
                        <div
                            className={clsx('dtb__fspacer', stickyClass('__actions', 'filter'))}
                            style={stickyStyle('__actions')}
                        />
                    )}
                    {needSpacer && rightStickyCount === 0 && <div className="dtb__fspacer dtb__elastic-spacer" />}
                </div>
            )}
        </div>
    );

    const tableNode = (
        <div
            ref={rootRef}
            className={clsx(
                'dtb',
                (fillHeight || fullscreen) && 'dtb--fill',
                fullscreen && 'dtb--fullscreen',
                replaceEmptyGrid && 'dtb--empty-presentation',
            )}
            data-testid={testId}
            {...(fullscreen
                ? {
                      role: 'dialog',
                      'aria-labelledby': fullscreenHeadTitle ? fullscreenTitleId : undefined,
                      'aria-label': fullscreenHeadTitle ? undefined : 'Таблица на весь экран',
                  }
                : {})}
        >
            {fullscreen && (
                <DataTableFullscreenHeader
                    title={fullscreenHeadTitle}
                    titleId={fullscreenTitleId}
                    context={fullscreenContext}
                    exitLabel={fullscreenExitLabel}
                    onExit={fullscreenApi.exit}
                />
            )}
            {replaceEmptyGrid && <div className="dtb__empty-presentation">{emptyPresentation}</div>}
            {/* пока схемы нет, полосу туллбара рисует скелетон: живые кнопки
                над пустой таблицей нечем обслуживать (колонок ещё нет) */}
            {showBar && !schemaPending && (
                <div
                    ref={barRef}
                    className={clsx('dtb__bar', stickyBar && 'is-sticky', stickyHead && 'dtb__bar--release')}
                    data-testid="dtb-toolbar"
                >
                    {title && (
                        <h3 id={titleId} className="dtb__title">
                            {title}
                        </h3>
                    )}
                    {toolbarLead && <div className="dtb__bar-lead">{toolbarLead}</div>}
                    {showGlobalSearch && (
                        <DataTableGlobalSearch
                            ref={globalSearchRef}
                            value={globalFilter}
                            onApply={applyGlobalSearch}
                        />
                    )}
                    {showGlobalSearch && globalSearchMode === 'similar' && filteredCount > 0 && (
                        <span className="dtb__search-note" role="status">
                            Точных совпадений нет — показаны похожие
                        </span>
                    )}
                    {!internalScroll && trackTopGroup && (
                        <DataTableTopGroup atom={topGroupAtom}>{renderBarTopGroup}</DataTableTopGroup>
                    )}
                    {filteredRevealRequest && (
                        <span className="dtb__reveal-filtered" role="status">
                            <span className="dtb__reveal-filtered-dot" aria-hidden />
                            Новая строка скрыта фильтрами
                            <button
                                type="button"
                                onClick={() => {
                                    resetFilters();
                                    setFilteredRevealRequest(null);
                                }}
                            >
                                Сбросить фильтры
                            </button>
                        </span>
                    )}
                    {toolbarStart}
                    {groupPanel}
                    <div className="dtb__bar-end">
                        {fullscreen && (
                            <DataTableFind
                                inputRef={findInputRef}
                                rows={findRows}
                                colIds={dataColIds}
                                rowsRef={rowsRef}
                                reveal={revealFindMatch}
                                queryRef={findQueryRef}
                            />
                        )}
                        {showServerStartAction && (
                            <span
                                className={clsx(
                                    'dtb__to-start-slot',
                                    serverStartFeedbackVisible && 'is-confirmed',
                                    serverStartFeedback === 'exiting' && 'is-exiting',
                                )}
                            >
                                <button
                                    type="button"
                                    className={clsx('dtb__to-start', serverStartFeedbackVisible && 'is-confirmed')}
                                    aria-label={
                                        serverStartFeedbackVisible
                                            ? 'Вы в начале списка'
                                            : `В начало списка, ${serverStartDistance.toLocaleString('ru-RU')} строк выше`
                                    }
                                    disabled={jumpingToServerStart || serverStartFeedbackVisible}
                                    onClick={requestServerJumpToStart}
                                >
                                    <span className="dtb__to-start-state dtb__to-start-state--action">
                                        {jumpingToServerStart ? (
                                            <span className="dtb__btn-spin" aria-hidden />
                                        ) : (
                                            <span className="dtb__to-start-icon" aria-hidden />
                                        )}
                                        <span>В начало</span>
                                        <span className="dtb__to-start-count">
                                            {serverStartDistance.toLocaleString('ru-RU')}
                                        </span>
                                    </span>
                                    <span className="dtb__to-start-state dtb__to-start-state--success" aria-hidden>
                                        <span className="dtb__to-start-check">
                                            <HiOutlineCheck aria-hidden />
                                        </span>
                                        <span>Вы в начале списка</span>
                                    </span>
                                </button>
                                {serverStartFeedbackVisible && (
                                    <span className="dtb__to-start-announcement" role="status" aria-live="polite">
                                        Вы в начале списка
                                    </span>
                                )}
                            </span>
                        )}
                        {toolbarEnd}
                        {showGroupsToggle && groupingActive && (
                            <button
                                type="button"
                                className="dtb__icon-btn"
                                data-testid="dtb-groups-toggle"
                                title={
                                    groupsExpanded
                                        ? 'Свернуть все группы'
                                        : canExpandAllGroups
                                          ? 'Развернуть все группы'
                                          : 'Групп слишком много, чтобы раскрыть все разом — раскрывайте по одной'
                                }
                                aria-label={groupsExpanded ? 'Свернуть все группы' : 'Развернуть все группы'}
                                disabled={!groupsExpanded && !canExpandAllGroups}
                                onClick={() => setAllGroupsExpanded(!groupsExpanded)}
                            >
                                {groupsExpanded ? (
                                    <HiOutlineChevronDoubleUp aria-hidden />
                                ) : (
                                    <HiOutlineChevronDoubleDown aria-hidden />
                                )}
                            </button>
                        )}
                        {filterRowToggle && (
                            <button
                                type="button"
                                className={clsx('dtb__icon-btn', filterRowOn && 'is-active')}
                                title={filterRowOn ? 'Скрыть строку фильтров' : 'Показать строку фильтров'}
                                aria-label={filterRowOn ? 'Скрыть строку фильтров' : 'Показать строку фильтров'}
                                aria-pressed={filterRowOn}
                                onClick={() => setFilterRowOn((v) => !v)}
                            >
                                <HiOutlineFunnel aria-hidden />
                            </button>
                        )}
                        {filtersDirty && (
                            <button
                                type="button"
                                className="dtb__icon-btn is-accent"
                                title="Применить фильтр"
                                aria-label="Применить фильтр"
                                onClick={applyFilters}
                            >
                                <HiOutlineCheck aria-hidden />
                            </button>
                        )}
                        {onRefresh && (
                            <button
                                type="button"
                                className="dtb__icon-btn"
                                title={refreshLabel}
                                aria-label={refreshLabel}
                                onClick={onRefresh}
                            >
                                <HiOutlineArrowPath aria-hidden />
                            </button>
                        )}
                        {isFiltered && (
                            <button
                                type="button"
                                className="dtb__icon-btn"
                                title="Сбросить фильтры"
                                onClick={resetFilters}
                            >
                                <span className="dtb__filterx">
                                    <HiOutlineFunnel aria-hidden />
                                    <HiXMark className="dtb__filterx-x" aria-hidden />
                                </span>
                            </button>
                        )}
                        {serverExport && (
                            <button
                                ref={serverExportButtonRef}
                                {...exportButtonProps}
                                onClick={handleServerExportClick}
                            >
                                {exportButtonIcon}
                            </button>
                        )}
                        {!serverExport &&
                            exportFileName &&
                            (exportTexts?.allowSelected ? (
                                <DataTableExportMenu
                                    buttonProps={exportButtonProps}
                                    icon={exportButtonIcon}
                                    exportAllLabel={exportTexts.exportAll ?? 'Экспортировать всё'}
                                    exportSelectedLabel={
                                        exportTexts.exportSelectedRows ?? `Экспортировать выбранные (${selectedCount})`
                                    }
                                    selectedCount={selectedCount}
                                    onExport={handleExport}
                                />
                            ) : (
                                <button {...exportButtonProps} onClick={() => handleExport(false)}>
                                    {exportButtonIcon}
                                </button>
                            ))}
                        {/* меню — только когда в нём есть что-то кроме высоты строк:
                            ради одного контрола шестерёнка стоила двух кликов */}
                        {showTableSettings &&
                            (tableSettingsExtra ? (
                                <DataTableSettingsControl
                                    wrapMode={wrapMode}
                                    onWrapModeChange={changeWrapMode}
                                    extra={tableSettingsExtra}
                                    rowsRef={rowsRef}
                                    scrollRef={scrollRef}
                                    internalScroll={internalScroll}
                                    rowCount={rows.length}
                                />
                            ) : (
                                <DataTableRowHeightButton wrapMode={wrapMode} onWrapModeChange={changeWrapMode} />
                            ))}
                        {showColumnChooser && (
                            <DataTableChooserControl
                                ref={chooserRef}
                                options={columnChooserOptions}
                                columns={chooserColumns}
                                onApply={applyChooserVisibility}
                                label={columnChooserLabel}
                                alert={columnChooserAlert}
                            />
                        )}
                        {fullscreenControl ?? (allowFullscreen && !fullscreen && (
                            <DataTableFullscreenButton ref={fullscreenTriggerRef} onClick={fullscreenApi.enter} />
                        ))}
                    </div>
                </div>
            )}
            {toolbarBelow && !schemaPending && <div className="dtb__bar-below">{toolbarBelow}</div>}
            {showFilterSummary && filterSummaryPosition === 'top' && (
                <DataTableFilterSummary
                    extra={filterSummaryExtra}
                    end={filterSummaryEnd}
                    columns={filterSummaryColumns}
                    filters={columnFilters}
                    facetLabels={facetLabels.labels}
                    globalFilter={globalFilter}
                    onRemove={removeColumnFilter}
                    onClearSearch={clearSearch}
                    onClearAll={resetFilters}
                />
            )}

            {failureBar && (
                <DataTableFailure
                    variant="bar"
                    title="Список не обновился"
                    hint="Показаны прежние данные."
                    error={failure}
                    onRetry={retry}
                    onDismiss={() => setDismissedError(failure)}
                />
            )}

            <div className="dtb__content">
                {schemaPending && <DataTableSkeleton fillHeight={fillHeight} showBar={showBar} paging={paging} />}
                <div
                    ref={tablewrapRef}
                    className={clsx(
                        'dtb__tablewrap',
                        wrapMode !== 'nowrap' && `dtb__tablewrap--${wrapMode}`,
                        stickyHead && headTimeline && 'dtb__tablewrap--x-timeline',
                    )}
                    style={tableVars}
                    role="table"
                    aria-labelledby={title ? titleId : undefined}
                >
                    {overflowTooltip && <DataTableOverflowTip containerRef={tablewrapRef} />}
                    {!allColumnsHidden && !stickyHead && (
                        <span
                            className={clsx('dtb__head-rule', internalScroll && 'dtb__head-rule--scroll-y')}
                            aria-hidden
                        />
                    )}
                    {stickyHead && (
                        <div className="dtb__headsticky" ref={headStickyRef}>
                            <div className="dtb__headplate" ref={headPlateRef}>
                                <span className="dtb__head-rule" aria-hidden />
                                <div className="dtb__headscroll" ref={headScrollRef} onScroll={handleHeadScroll}>
                                    {theadNode}
                                </div>
                            </div>
                        </div>
                    )}
                    <div
                        className={clsx('dtb__scroll', internalScroll && 'dtb__scroll--y', stickyHead && 'dtb__scroll--hbar')}
                        ref={scrollRef}
                        onScroll={handleScroll}
                        // предел высоты — для таблицы в потоке страницы; в полноэкранном режиме лента заполняет слой
                        style={maxHeight != null && !fullscreen ? { maxHeight } : undefined}
                    >
                        {!allColumnsHidden && (
                            <>
                                {!stickyHead && theadNode}

                                {internalScroll && trackTopGroup && (
                                    <DataTableTopGroup atom={topGroupAtom}>{renderStickyTopGroup}</DataTableTopGroup>
                                )}

                                {allowRowPinning && topRows.length > 0 && (
                                    <ul className="dtb__rows dtb__rows--pinnedrows" role="rowgroup">
                                        {topRows.map((row) => renderRow(row, -1))}
                                    </ul>
                                )}

                                {serverInfinite && server.hasPrevious && (
                                    <div ref={previousSentinelRef} className="dtb__sentinel" aria-hidden />
                                )}

                                <DndWrap
                                    enabled={allowDrag}
                                    sensors={dndSensors}
                                    items={sortableIds}
                                    onDragStart={onDndStart}
                                    onDragEnd={onDndEnd}
                                    onDragCancel={() => setDragGroup(null)}
                                    overlay={dragOverlayContent}
                                >
                                    <DataTableRows
                                        rows={rows}
                                        virtual={virtual}
                                        scrollParent={scrollParent}
                                        scrollMargin={scrollMargin}
                                        estimateSize={EST_ROW}
                                        // Перенос отдельной колонки и раскрытые подробности тоже меняют высоту строки.
                                        uniformRows={!hasWrappingCells && !hasExpandableRows}
                                        // Без переносимых колонок высота строк не зависит от их ширин —
                                        // шаблон в ключ не входит, ресайз ничего не перемеривает
                                        // на весь экран ширина контейнера меняется скачком, а шаблон колонок — нет:
                                        // высоты строк с переносом перемеряем и по флагу режима
                                        measureKey={
                                            !hasWrappingCells
                                                ? wrapMode
                                                : `${wrapMode}|${colsTemplate}|${fullscreen}`
                                        }
                                        listRef={rowsRef}
                                        renderRow={renderRowSortable}
                                        onVirtualizer={onVirtualizer}
                                        onRenderedRows={onRenderedRowsChange}
                                    />
                                </DndWrap>

                                {(loadingMore || firstLoadPending) && (
                                    <ul className="dtb__rows dtb__rows--skeleton" aria-hidden>
                                        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                                            <li
                                                key={i}
                                                // края зоны данных: верх — только когда строк ещё нет
                                                // (заглушки и есть весь список), низ — всегда
                                                className={clsx(
                                                    'dtb__row',
                                                    firstLoadPending && i === 0 && 'dtb__row--first',
                                                    i === SKELETON_ROWS - 1 && 'dtb__row--last',
                                                )}
                                            >
                                                <div className="dtb__rowline">
                                                    {allowDrag && <div />}
                                                    {selectable && <div />}
                                                    {hasActionsLeft && <div />}
                                                    {showExpanderTrack && <div />}
                                                    {visibleCols.map((c, j) => (
                                                        <div key={c.id} className="dtb__cell">
                                                            <Skeleton
                                                                height={12}
                                                                radius={6}
                                                                width={`${[82, 58, 70, 46, 64][(i + j) % 5]}%`}
                                                            />
                                                        </div>
                                                    ))}
                                                    {hasActionsCol && <div />}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                        {failureBlocks && (
                            <DataTableFailure
                                title={isServer ? 'Список не загрузился' : 'Данные не загрузились'}
                                error={failure}
                                onRetry={retry}
                            />
                        )}
                        {!schemaPending && allColumnsHidden && (
                            <div className="dtb__empty">
                                <HiOutlineViewColumns aria-hidden />
                                <h3>Все столбцы скрыты</h3>
                                <p>Включите нужные столбцы в «Выборе столбцов».</p>
                                {showColumnChooser && (
                                    <button
                                        type="button"
                                        className="dtb__empty-btn"
                                        onClick={() => chooserRef.current?.open()}
                                    >
                                        <HiOutlineViewColumns aria-hidden />
                                        Выбор столбцов
                                    </button>
                                )}
                            </div>
                        )}
                        {refreshingEmpty && !failureBlocks && (
                            <div data-testid="dtb-empty-pending" aria-hidden style={{ height: emptyHeight }} />
                        )}
                        {showEmpty && (
                            <div className="dtb__empty" ref={emptyRef}>
                                {emptyByFilter ? (
                                    <HiOutlineMagnifyingGlass aria-hidden />
                                ) : (
                                    (emptyIcon ?? <HiOutlineInbox aria-hidden />)
                                )}
                                <h3>
                                    {emptyByFilter
                                        ? (filteredEmptyText ?? 'Ничего не найдено')
                                        : (emptyText ?? 'Нет данных')}
                                </h3>
                                <p>
                                    {emptyByFilter
                                        ? (filteredEmptyHint ?? 'Попробуйте изменить фильтры или поиск.')
                                        : (emptyHint ?? '')}
                                </p>
                                {emptyActions.length > 0 && (
                                    <DataTableEmptyActions
                                        actions={emptyActions}
                                        actionTestId={filteredEmptyActions ? 'dtb-empty-action' : 'dtb-empty-reset'}
                                    />
                                )}
                            </div>
                        )}
                        {!allColumnsHidden && hasReachEnd && (
                            <div ref={sentinelRef} className="dtb__sentinel" aria-hidden />
                        )}
                    </div>
                    {stickyHead && (
                        <div className="dtb__hbar" ref={hbarRef} onScroll={handleHBarScroll} aria-hidden>
                            <span />
                        </div>
                    )}
                </div>

                {effectiveLoading && loadingOverlayReady && !schemaPending && !firstLoadPending && !allColumnsHidden && (
                    <div className="dtb__loading">
                        <Preloader size="lg" />
                    </div>
                )}
            </div>

            {(filterSummaryPosition === 'bottom' || filterSummaryBelow) && (
                <div className="dtb__filter-bottom">
                    {showFilterSummary && filterSummaryPosition === 'bottom' && (
                        <DataTableFilterSummary
                            extra={filterSummaryExtra}
                            end={filterSummaryEnd}
                            columns={filterSummaryColumns}
                            filters={columnFilters}
                            facetLabels={facetLabels.labels}
                            globalFilter={globalFilter}
                            onRemove={removeColumnFilter}
                            onClearSearch={clearSearch}
                            onClearAll={resetFilters}
                        />
                    )}
                    {filterSummaryBelow}
                </div>
            )}

            {selectingAll && !showVirtualFooter && !showPager && (
                <div className="dtb__selbar" role="status">
                    {selectAllStatusContent}
                </div>
            )}

            {/* Sticky-счётчик при бесконечной прокрутке — аналог всегда видимого
                пейджера DX: липнет к низу вьюпорта, пока таблица в кадре */}
            {showVirtualFooter && (
                <div className="dtb__vfoot" data-testid="dtb-counter" role={selectingAll ? 'status' : undefined}>
                    {selectingAll ? (
                        selectAllStatusContent
                    ) : (
                        <>
                            <span>
                                {serverGroupActive
                                    ? `Групп ${groupsApi.loadedGroupCount.toLocaleString('ru-RU')} из ${groupsApi.groupCount.toLocaleString('ru-RU')} · строк ${totalCount.toLocaleString('ru-RU')}`
                                    : (server.windowStartIndex ?? 0) > 0
                                      ? `Показаны ${(server.windowStartIndex! + 1).toLocaleString('ru-RU')}–${(
                                            server.windowStartIndex! + effectiveData.length
                                        ).toLocaleString('ru-RU')} из ${totalCount.toLocaleString('ru-RU')}`
                                      : `Загружено ${effectiveData.length.toLocaleString('ru-RU')} из ${totalCount.toLocaleString('ru-RU')}`}
                            </span>
                            {(loadingMore || loadingPrevious) && <span className="dtb__vfoot-loading">загрузка…</span>}
                            {server.errorOnLoadMore && !loadingMore && (
                                <span className="dtb__vfoot-fail">
                                    Продолжение не загрузилось
                                    {retry && (
                                        <button
                                            type="button"
                                            className="dtb__fail-retry dtb__fail-retry--inline"
                                            onClick={retry}
                                        >
                                            Повторить
                                        </button>
                                    )}
                                </span>
                            )}
                            {footerEnd && <span className="dtb__vfoot-end">{footerEnd}</span>}
                        </>
                    )}
                </div>
            )}

            <DataTableContextMenuHost ref={ctxMenuRef} />

            {fullscreenTotals && <DataTableTotals {...fullscreenTotals} />}

            {showPager &&
                (selectingAll ? (
                    <div className="dtb__foot dtb__foot--selecting" role="status">
                        {selectAllStatusContent}
                    </div>
                ) : (
                    <DataTablePager
                        page={pagination.pageIndex}
                        pageSize={pagination.pageSize}
                        totalCount={pagedRowCount}
                        totalText={
                            serverGroupActive
                                ? `групп ${groupsApi.groupCount.toLocaleString('ru-RU')} · строк ${server.totalCount.toLocaleString('ru-RU')}`
                                : undefined
                        }
                        pagerSizes={pageSizes}
                        data-testid="dtb-pager"
                        end={footerEnd}
                        onPageChange={(p) => setPagination((prev) => ({ ...prev, pageIndex: p }))}
                        onPageSizeChange={(size) => setPagination({ pageIndex: 0, pageSize: size })}
                    />
                ))}
        </div>
    );

    // Таблица в поле группы (FormGroup): у полей её ячеек, фильтров и окон подпись и сообщение группы не берутся
    return (
        <FieldA11yContext value={null}>
            {fullscreen && (
                // держит место таблицы в потоке: форма под слоем не схлопывается
                <div
                    ref={fullscreenPlaceholderRef}
                    className="dtb-fullscreen-placeholder"
                    style={{ height: fullscreenApi.placeholderHeight }}
                    aria-hidden
                />
            )}
            {tableNode}
            {showExportLimitModal && (
                <Suspense fallback={null}>
                    <ExportLimitModal
                        totalCount={server.totalCount}
                        background={
                            backgroundExport ? { suggestAboveRows: backgroundExport.suggestAboveRows } : undefined
                        }
                        onClose={() => setShowExportLimitModal(false)}
                        onSubmit={(limit, inBackground) => {
                            setShowExportLimitModal(false);
                            runServerExport(limit, inBackground ? { suggested: false } : undefined);
                        }}
                    />
                </Suspense>
            )}
        </FieldA11yContext>
    );
}

export default DataTable;
